// getresumatch.com build 2026-03-12 — mobile banner + job matching improvements
import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { supabase } from "./supabase";
import { C, compatLabel, ALL_JOBS, SYSTEM_PROMPT, PRIVACY_SECTIONS, TERMS_SECTIONS } from "./constants";
import { useToast, useCopyToast } from "./hooks";
import { downloadResumeAsPDF } from "./utils";
import MobileBanner from "./components/MobileBanner";
import LegalPage from "./components/LegalPage";
import { UpgradeModal, HowItWorksModal } from "./components/UpgradeModal";
import { CompatBadge, Tag, Pill, Logo, ResumeToast, LeadCaptureBar, FaqItem } from "./components/UI";
const SupportChat = lazy(() => import("./components/SupportChat"));
const CancelModal = lazy(() => import("./components/CancelModal"));
const TrackerPage = lazy(() => import("./components/TrackerPage"));
const AuthModal = lazy(() => import("./components/AuthModal"));
const ResultsScreen = lazy(() => import("./components/ResultsScreen"));

export default function App() {
  const [prevScreen, setPrevScreen] = useState("results");
  const [screen, setScreen] = useState("upload");
  const [fileName, setFileName] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMsg, setProgressMsg] = useState("");
  const [savedIds, setSavedIds] = useState([]);
  const [savedJobObjects, setSavedJobObjects] = useState([]);
  const [activeTab, setActiveTab] = useState("results");
  const [selectedJob, setSelectedJob] = useState(null);
  const [rewriting, setRewriting] = useState(false);
  const [rewriteDone, setRewriteDone] = useState(false);
  const [tailoredResume, setTailoredResume] = useState("");
  const [originalTailoredResume, setOriginalTailoredResume] = useState("");
  const [beforeFixResume, setBeforeFixResume] = useState("");
  const [lastFixTool, setLastFixTool] = useState("");
  const [tailorError, setTailorError] = useState("");
  const [upgrade, setUpgrade] = useState(null);
  const [showLeadBar, setShowLeadBar] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [leadEmail, setLeadEmail] = useState("");
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [usedRewrite, setUsedRewrite] = useState(false);
  const [searchesLeft, setSearchesLeft] = useState(5);
  const [location, setLocation] = useState("");
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [fairChance, setFairChance] = useState(false);
  const [excludeRemote, setExcludeRemote] = useState(false);
  const [pivotTarget, setPivotTarget] = useState("");
  const [savedVersions, setSavedVersions] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveVersionLabel, setSaveVersionLabel] = useState("");
  const [saveVersionLoading, setSaveVersionLoading] = useState(false);
  const [saveVersionSuccess, setSaveVersionSuccess] = useState(false);
  const [showVersionsPanel, setShowVersionsPanel] = useState(false);
  const [searchRadius, setSearchRadius] = useState(50);
  const [showRefine, setShowRefine] = useState(false);
  const [refineInput, setRefineInput] = useState("");
  const [refineHistory, setRefineHistory] = useState([]);
  const [refining, setRefining] = useState(false);
  const [refinedNote, setRefinedNote] = useState("");
  const fileRef = useRef();
  const refineRef = useRef();
  const [resumeData, setResumeData] = useState(null);
  const [resumeText, setResumeText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [atsReadable, setAtsReadable] = useState(null);
  const [parsedOnUpload, setParsedOnUpload] = useState(null);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [jobDetails, setJobDetails] = useState({});

  // ── Auth state ──
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const isSuperUser = user?.email === "levy1620@gmail.com" || user?.email === "tchesnee@yahoo.com";
  const [showAuth, setShowAuth] = useState(false);
  const [anonLimitHit, setAnonLimitHit] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [authMode, setAuthMode] = useState("login");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authName, setAuthName] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [changePwCurrent, setChangePwCurrent] = useState("");
  const [changePwNew, setChangePwNew] = useState("");
  const [changePwConfirm, setChangePwConfirm] = useState("");
  const [changePwError, setChangePwError] = useState("");
  const [changePwSuccess, setChangePwSuccess] = useState("");
  const [changePwLoading, setChangePwLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelDone, setCancelDone] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [referralCopied, setReferralCopied] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactSubject, setContactSubject] = useState("general");
  const [contactMessage, setContactMessage] = useState("");
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState("");
  const [showUpgradeChoice, setShowUpgradeChoice] = useState(false);
  const [showPromoPopup, setShowPromoPopup] = useState(false);
  useEffect(() => {
    if (user) return;
    const timer = setTimeout(() => setShowPromoPopup(true), 25000);
    return () => clearTimeout(timer);
  }, [user]);

  const [inlinePromoCode, setInlinePromoCode] = useState("");
  const [inlinePromoState, setInlinePromoState] = useState(null);
  const [inlinePromoLoading, setInlinePromoLoading] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [, setSavedResumeId] = useState(null);
  const [resumeLoadedFromCloud, setResumeLoadedFromCloud] = useState(false);

  const { copied: resumeCopied, copy: copyResume } = useCopyToast();
  const { toast: resumeToast, showToast: showResumeToast, dismissToast: dismissResumeToast } = useToast();
  const { toast: trackerToast, showToast: showTrackerToast, dismissToast: dismissTrackerToast } = useToast();
  const [nudgeJob, setNudgeJob] = useState(null);
  const [showNudgeToast, setShowNudgeToast] = useState(false);
  const [showBannerToast, setShowBannerToast] = useState(false);
  const [bannerJob, setBannerJob] = useState(null);
  const handleTailorNudge = useCallback((job, isBanner = false) => {
    if (isBanner) {
      setBannerJob(job);
      setShowBannerToast(true);
    } else {
      setNudgeJob(job);
      setShowNudgeToast(true);
    }
  }, []);

  const addToTracker = async (job, suppressToast = false) => {
    if (!user) return;
    try {
      const today = new Date().toISOString().split("T")[0];
      const res = await fetch("https://resumatch-backend-production.up.railway.app/add-job", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          job_title: job.title,
          company: job.company,
          url: job.url,
          status: "Applied",
          notes: "",
          applied_date: today,
        }),
      });
      const data = await res.json();
      if (!suppressToast) {
        if (data.duplicate) {
          showTrackerToast("Already in your Job Tracker", "info", "✓", true);
        } else {
          showTrackerToast("Added to your Job Tracker ✓", "success", "✓", true);
        }
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    const path = window.location.pathname;
    if (path === "/privacy") setScreen("privacy");
    else if (path === "/terms") setScreen("terms");
    else if (path !== "/" && path !== "") setScreen("404");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) return;
    try {
      const stored = JSON.parse(localStorage.getItem("rm_usage") || "{}");
      const thisMonth = new Date().toISOString().slice(0, 7);
      if (stored.month !== thisMonth) {
        localStorage.setItem("rm_usage", JSON.stringify({ count: 0, month: thisMonth }));
        setSearchesLeft(5);
      } else {
        setSearchesLeft(Math.max(0, 5 - (stored.count || 0)));
      }
    } catch {}
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
        loadSavedResume(session.user.id);
        loadSavedJobs(session.user.id);
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        setUser(session.user);
        loadProfile(session.user.id);
        loadSavedResume(session.user.id);
        loadSavedJobs(session.user.id);
        // Show onboarding for first-time users
        const key = `rm_onboarded_${session.user.id}`;
        if (!localStorage.getItem(key)) {
          setTimeout(() => setShowOnboarding(true), 800);
          fetch('https://resumatch-backend-production.up.railway.app/send-welcome-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: session.user.email })
          }).catch(err => console.error('Welcome email failed:', err));
        }
        setAuthMode(current => {
          if (current !== "confirm") {
            setShowAuth(false);
            setAuthEmail(""); setAuthPassword(""); setAuthName("");
            return "login";
          }
          return current;
        });
      } else if (event === "SIGNED_OUT") {
        setUser(null);
        setProfile(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const plan = params.get("plan");
    const ref = params.get("ref");
    if (ref) {
      sessionStorage.setItem("referral_code", ref);
      window.history.replaceState({}, "", "/");
    }
    if (payment === "cancelled") {
      window.history.replaceState({}, "", "/");
    }
    if (payment === "success") {
      window.history.replaceState({}, "", "/");
      setTimeout(() => {
        alert(`🎉 Payment successful! Your ${plan === "pro" ? "Pro Matcher" : plan === "pro_annual" ? "Pro Annual" : "Match Pack"} is now active.`);
        if (user) loadProfile(user.id);
      }, 1500);
      setTimeout(() => {
        if (user) loadProfile(user.id);
      }, 4000);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show lead capture bar after 30s for logged-out visitors
  useEffect(() => {
    if (user || leadSubmitted) return;
    const timer = setTimeout(() => setShowLeadBar(true), 30000);
    return () => clearTimeout(timer);
  }, [user, leadSubmitted]);

  useEffect(() => {
    const accepted = localStorage.getItem("rm_cookie_consent");
    if (!accepted) setTimeout(() => setShowCookieBanner(true), 1500);
  }, []);

  const handleCookieAccept = () => {
    localStorage.setItem("rm_cookie_consent", "accepted");
    setShowCookieBanner(false);
  };

  const handleOnboardingDone = () => {
    if (user) localStorage.setItem(`rm_onboarded_${user.id}`, "true");
    setShowOnboarding(false);
    setOnboardingStep(0);
  };

  const handleLeadCapture = async () => {
    if (!leadEmail || !leadEmail.includes("@")) return;
    setLeadLoading(true);
    try {
      await fetch("https://resumatch-backend-production.up.railway.app/capture-lead", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: leadEmail }),
      });
      setLeadSubmitted(true);
      setTimeout(() => setShowLeadBar(false), 3000);
    } catch (e) { console.error("Lead capture error", e); }
    setLeadLoading(false);
  };

  const loadSavedJobs = async (userId) => {
    try {
      const { data } = await supabase.from("saved_jobs").select("*").eq("user_id", userId).order("created_at", { ascending: false });
      if (data && data.length > 0) {
        setSavedJobObjects(data);
        setSavedIds(data.map(j => j.job_id));
      }
    } catch (err) { console.error("Load saved jobs error:", err); }
  };

  const loadSavedResume = async (userId) => {
    try {
      const { data } = await supabase.from("resumes").select("*").eq("user_id", userId).order("created_at", { ascending: false }).limit(1).single();
      if (data) {
        setResumeData(data.parsed_data);
        setResumeText(data.raw_text || "");
        setSavedResumeId(data.id);
        setResumeLoadedFromCloud(true);
        setFileName(data.file_name);
        sessionStorage.removeItem("presignup_file_name");
      } else {
        // No cloud resume — restore pre-signup upload if it exists
        const savedText = sessionStorage.getItem("presignup_resume_text");
        const savedData = sessionStorage.getItem("presignup_resume_data");
        const savedName = sessionStorage.getItem("presignup_file_name");
        if (savedText) {
          setResumeText(savedText);
          const parsedData = savedData ? JSON.parse(savedData) : null;
          if (parsedData) setResumeData(parsedData);
          if (savedName) setFileName(savedName);
          sessionStorage.removeItem("presignup_resume_text");
          sessionStorage.removeItem("presignup_resume_data");
          sessionStorage.removeItem("presignup_file_name");
          // Persist pre-signup resume to Supabase now that user is logged in
          try {
            await supabase.from("resumes").delete().eq("user_id", userId);
            const { data: savedResume } = await supabase.from("resumes").insert({ user_id: userId, file_name: savedName || "resume", parsed_data: parsedData, raw_text: savedText }).select().single();
            if (savedResume) { setSavedResumeId(savedResume.id); setResumeLoadedFromCloud(true); }
          } catch (saveErr) { console.warn("Pre-signup resume cloud save failed (non-fatal):", saveErr); }
        }
      }
    } catch {
      // No cloud resume — restore pre-signup upload if it exists
      const savedText = sessionStorage.getItem("presignup_resume_text");
      const savedData = sessionStorage.getItem("presignup_resume_data");
      const savedName = sessionStorage.getItem("presignup_file_name");
      if (savedText) {
        setResumeText(savedText);
        const parsedData = savedData ? JSON.parse(savedData) : null;
        if (parsedData) setResumeData(parsedData);
        if (savedName) setFileName(savedName);
        sessionStorage.removeItem("presignup_resume_text");
        sessionStorage.removeItem("presignup_resume_data");
        sessionStorage.removeItem("presignup_file_name");
        // Persist pre-signup resume to Supabase now that user is logged in
        try {
          await supabase.from("resumes").delete().eq("user_id", userId);
          const { data: savedResume } = await supabase.from("resumes").insert({ user_id: userId, file_name: savedName || "resume", parsed_data: parsedData, raw_text: savedText }).select().single();
          if (savedResume) { setSavedResumeId(savedResume.id); setResumeLoadedFromCloud(true); }
        } catch (saveErr) { console.warn("Pre-signup resume cloud save failed (non-fatal):", saveErr); }
      }
    }
  };

  const loadProfile = async (userId) => {
    const { data } = await supabase.from("profiles").select("*").eq("id", userId).single();
    if (data) {
      setProfile(data);
      setSearchesLeft(data.plan === "pro" || data.plan === "pro_annual" ? 999 : data.plan === "pack" ? Math.max(0, 5 - (data.searches_used || 0)) : Math.max(0, 5 - (data.searches_used || 0)));
    }
  };

  const handleSignUp = async () => {
    setAuthLoading(true); setAuthError("");
    if (!authEmail || !authPassword) { setAuthError("Please enter your email and password."); setAuthLoading(false); return; }
    if (authPassword.length < 6) { setAuthError("Password must be at least 6 characters."); setAuthLoading(false); return; }
    const { data, error } = await supabase.auth.signUp({ email: authEmail, password: authPassword, options: { data: { full_name: authName } } });
    if (error) { setAuthError(error.message); setAuthLoading(false); return; }
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setAuthError("An account with this email already exists. Please sign in instead.");
      setAuthLoading(false); return;
    }
    setAuthMode("confirm"); setAuthLoading(false);
    // Apply referral if present
    const refCode = sessionStorage.getItem("referral_code");
    if (refCode && data?.user?.email) {
      fetch("https://resumatch-backend-production.up.railway.app/apply-referral", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ referralCode: refCode, newUserEmail: data.user.email }),
      }).then(() => sessionStorage.removeItem("referral_code")).catch(() => {});
    }
  };

  const handleLogin = async () => {
    setAuthLoading(true); setAuthError("");
    const { error } = await supabase.auth.signInWithPassword({ email: authEmail, password: authPassword });
    if (error) setAuthError(error.message);
    else { setShowAuth(false); setAuthEmail(""); setAuthPassword(""); }
    setAuthLoading(false);
  };

  const handleGoogleLogin = async () => {
    setAuthLoading(true); setAuthError("");
    const { error } = await supabase.auth.signInWithOAuth({ provider: "google", options: { redirectTo: window.location.origin, queryParams: { access_type: "offline", prompt: "consent" } } });
    if (error) { setAuthError(error.message); setAuthLoading(false); }
  };

  // Google One Tap disabled — using signInWithOAuth in AuthModal instead

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null); setProfile(null); setSearchesLeft(5);
    setScreen("upload");
    setResumeData(null); setResumeText(""); setFileName(null); setSelectedFile(null);
    setActiveJobs([]); setSavedIds([]); setSavedJobObjects([]);
    setResumeLoadedFromCloud(false); setSavedResumeId(null);
  };

  const handleForgotPassword = async () => {
    if (!authEmail) { setAuthError("Enter your email address first."); return; }
    setAuthLoading(true); setAuthError("");
    const { error } = await supabase.auth.resetPasswordForEmail(authEmail, {
      redirectTo: `${window.location.origin}?reset=true`,
    });
    if (error) setAuthError(error.message);
    else setAuthMode("forgot-sent");
    setAuthLoading(false);
  };

  const handleResendConfirmation = async () => {
    setAuthLoading(true); setAuthError("");
    const { error } = await supabase.auth.resend({ type: "signup", email: authEmail });
    if (error) setAuthError(error.message);
    else setAuthMode("resent");
    setAuthLoading(false);
  };

  const handleChangePassword = async () => {
    setChangePwError(""); setChangePwSuccess("");
    if (!changePwNew || !changePwConfirm) { setChangePwError("Please fill in all fields."); return; }
    if (changePwNew.length < 6) { setChangePwError("Password must be at least 6 characters."); return; }
    if (changePwNew !== changePwConfirm) { setChangePwError("Passwords do not match."); return; }
    setChangePwLoading(true);
    const { error } = await supabase.auth.updateUser({ password: changePwNew });
    if (error) setChangePwError(error.message);
    else { setChangePwSuccess("Password updated successfully."); setChangePwCurrent(""); setChangePwNew(""); setChangePwConfirm(""); }
    setChangePwLoading(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") { setDeleteError("Type DELETE to confirm."); return; }
    setDeleteLoading(true); setDeleteError("");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch("https://resumatch-backend-production.up.railway.app/delete-account", {
        method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session?.access_token}` },
      });
      await supabase.auth.signOut();
      setUser(null); setProfile(null); setScreen("upload");
      setResumeData(null); setResumeText(""); setFileName(null); setSelectedFile(null);
      setActiveJobs([]); setSavedIds([]); setSavedJobObjects([]);
    } catch (e) { setDeleteError("Something went wrong. Please try again."); }
    setDeleteLoading(false);
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true); setCancelError("");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch("https://resumatch-backend-production.up.railway.app/cancel-subscription", {
        method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to cancel");
      setCancelDone(true);
    } catch (e) { setCancelError("Something went wrong. Please try again or email support@getresumatch.com."); }
    setCancelLoading(false);
  };

  const handleBillingPortal = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch("https://resumatch-backend-production.up.railway.app/billing-portal", {
        method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (data.url) window.open(data.url, "_blank");
      else alert("No billing account found. If you believe this is an error, please contact support@getresumatch.com.");
    } catch (e) { console.error("Billing portal error", e); alert("Unable to open billing portal. Please contact support@getresumatch.com."); }
  };

  const handleContactSubmit = async () => {
    if (!contactName || !contactEmail || !contactMessage) { setContactError("Please fill in all fields."); return; }
    if (!contactEmail.includes("@")) { setContactError("Please enter a valid email address."); return; }
    setContactLoading(true); setContactError("");
    try {
      const res = await fetch("https://resumatch-backend-production.up.railway.app/contact", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: contactName, email: contactEmail, subject: contactSubject, message: contactMessage }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setContactSent(true);
      setContactName(""); setContactEmail(""); setContactMessage(""); setContactSubject("general");
    } catch (e) { setContactError("Failed to send message. Please email us directly at support@getresumatch.com."); }
    setContactLoading(false);
  };

  const handleUpgrade = async (plan, promoId = null) => {
    if (!user) { setAuthMode("signup"); setShowAuth(true); return; }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/create-checkout`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, userId: user.id, userEmail: user.email, ...(promoId && { promoId }) }),
      });
      const data = await res.json();
      if (data.url) { if (window.clarity) window.clarity("event", "upgrade_clicked"); if (window.gtag) window.gtag("event", "upgrade_clicked"); window.location.href = data.url; }
    } catch (err) { console.error("Upgrade error:", err); alert("Could not connect to payment server."); }
  };

  const saveSearchUsage = async () => {
    if (!user) return;
    await supabase.from("profiles").update({ searches_used: (profile?.searches_used || 0) + 1 }).eq("id", user.id);
  };

  const resetSearchCount = async () => {
    if (!user) return;
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/reset-searches`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: user.id }) });
      await loadProfile(user.id);
    } catch (err) { console.error("Reset failed:", err); }
  };

  const msgs = [
    "Reading your career story…",
    "Identifying your strongest skills…",
    "Scanning jobs within 50 miles of your location…",
    "Including remote opportunities…",
    "Calculating compatibility scores…",
    "Curating your perfect matches…",
  ];

  const loadingQuotes = [
    "The right job is out there. We'll find it.",
    "Scanning thousands of listings so you don't have to…",
    "Matching your skills to what employers actually want…",
    "Your next opportunity is closer than you think…",
    "Filtering out the noise. Only the good stuff makes it through…",
    "Great matches take a moment. Worth the wait.",
    "Every application deserves a resume that speaks the right language…",
    "Your experience is valuable. Let's prove it to the right employer.",
  ];
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [quoteFade, setQuoteFade] = useState(true);

  useEffect(() => {
    if (screen !== "searching") return;
    const qiv = setInterval(() => {
      setQuoteFade(false);
      setTimeout(() => {
        setQuoteIndex(i => (i + 1) % loadingQuotes.length);
        setQuoteFade(true);
      }, 400);
    }, 2800);
    return () => clearInterval(qiv);
  }, [screen]);

  useEffect(() => {
    if (screen !== "searching") return;
    let mi = 0;
    const miv = setInterval(() => { if (++mi < msgs.length) setProgressMsg(msgs[mi]); }, 900);
    const piv = setInterval(() => {
      setProgress(p => { if (p >= 90) { clearInterval(piv); return 90; } return p + 2; });
    }, 100);
    return () => { clearInterval(piv); clearInterval(miv); };
  }, [screen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (screen !== "results") return;
    const t = setTimeout(() => setShowRefine(true), 1500);
    return () => clearTimeout(t);
  }, [screen]);

  const handleFile = async f => {
    if (!f) return;
    setSelectedFile(f);
    setFileName(null);
    setAtsReadable(null);
    setParsedOnUpload(null);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append("resume", f);
      const parseRes = await fetch(`${process.env.REACT_APP_API_URL}/parse-resume`, { method: "POST", body: formData });
      const parseData = await parseRes.json();
      if (!parseData.success) {
        setSelectedFile(null);
        setUploadError(parseData.error || "Resume parsing failed");
        return;
      }
      setFileName(f.name);
      setAtsReadable(true);
      setParsedOnUpload(parseData);
    } catch (err) {
      setSelectedFile(null);
      setUploadError("Something went wrong reading your resume. Please try again.");
    }
  };

  const handleBuildResume = async () => {
    const { name, targetRole, recentTitle, skills } = buildForm;
    if (!name.trim() || !targetRole.trim() || !recentTitle.trim() || !skills.trim()) {
      setBuildError("Please fill in Full Name, Target Role, Most Recent Job Title, and Top Skills."); return;
    }
    setBuildLoading(true); setBuildError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/build-resume`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildForm),
      });
      const data = await res.json();
      if (!res.ok || !data.builtResume) throw new Error(data.error || "Build failed");
      setBuiltResume(data.builtResume);
      setBuildDraft(true);
    } catch (err) {
      setBuildError(err.message || "Something went wrong. Please try again.");
    } finally {
      setBuildLoading(false);
    }
  };

  const handleUseBuiltResume = async () => {
    const skillList = buildForm.skills.split(",").map(s => s.trim()).filter(Boolean);
    const builtResumeData = {
      rawText: builtResume,
      name: "AI-Built Resume",
      title: buildForm.recentTitle || buildForm.targetRole,
      current_title: buildForm.recentTitle || buildForm.targetRole,
      skills: skillList,
      experience: [],
      education: [],
    };
    setResumeText(builtResume);
    setResumeData(builtResumeData);
    setFileName("AI-built-resume.txt");
    setSelectedFile(null);
    setResumeLoadedFromCloud(false);
    setSavedResumeId(null);
    if (buildForm.targetRole) setPivotTarget(buildForm.targetRole);
    const buildsUsed = profile?.builds_used ?? 0;
    const isFreeUser = !user || !profile || profile.plan === "free" || !profile.plan;
    const isPackUser = profile?.plan === "pack";
    if (isFreeUser && buildsUsed >= 1) { setUpgrade("You've used your free resume build. Upgrade to keep building — more applications means more interviews."); return; }
    if (isPackUser && buildsUsed >= 5) { setUpgrade("You've used your 5 resume builds. Upgrade to Pro for unlimited builds — don't stop when you're on a roll."); return; }
    if (user) {
      await supabase.from("profiles").update({ builds_used: (profile?.builds_used || 0) + 1 }).eq("id", user.id);
      setProfile(prev => ({ ...prev, builds_used: (prev?.builds_used || 0) + 1 }));
    }
    setUploadTab("upload");
    setBuildDraft(false);
    setBuiltResume("");
    setBuildForm({ name:"", targetRole:"", recentTitle:"", recentCompany:"", yearsExp:"", skills:"", education:"" });
  };
  const handleDrop = e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); };

  const startSearch = async () => {
    const file = selectedFile;
    // No resume required — location/remote searches allowed without one
    if (!user) {
      const anonCount = parseInt(localStorage.getItem("anon_search_count") || "0");
      if (anonCount >= 2) { setAuthMode("signup"); setShowAuth(true); setAnonLimitHit(true); return; }
      localStorage.setItem("anon_search_count", String(anonCount + 1));
    }
    if (searchesLeft <= 0 && user) { setUpgrade("You've used your 5 free matches. Upgrade to keep going — the right job is still out there."); return; }
    setProgress(0); setProgressMsg(msgs[0]);
    setScreen("searching");
    try {
      let currentResumeData = resumeData;
      if (!currentResumeData && !file && resumeText) {
        currentResumeData = { rawText: resumeText, name: "AI-Built Resume", skills: [], experience: [], education: [] };
        setResumeData(currentResumeData);
      }
      if (file) {
        const parseData = parsedOnUpload || await (async () => {
          const formData = new FormData();
          formData.append("resume", file);
          const parseRes = await fetch(`${process.env.REACT_APP_API_URL}/parse-resume`, { method: "POST", body: formData });
          return await parseRes.json();
        })();
        if (!parseData.success) throw new Error(parseData.error || "Resume parsing failed");
        currentResumeData = parseData.data;
        setResumeData(parseData.data);
        setResumeText(parseData.rawText || "");
        if (!user) {
          sessionStorage.setItem("presignup_resume_text", parseData.rawText || "");
          sessionStorage.setItem("presignup_resume_data", JSON.stringify(parseData.data));
          sessionStorage.setItem("presignup_file_name", file.name);
        }
        if (user) {
          try {
            await supabase.from("resumes").delete().eq("user_id", user.id);
            const { data: savedResume } = await supabase.from("resumes").insert({ user_id: user.id, file_name: file.name, parsed_data: parseData.data, raw_text: parseData.rawText || "" }).select().single();
            if (savedResume) { setSavedResumeId(savedResume.id); setResumeLoadedFromCloud(true); }
          } catch (saveErr) { console.warn("Resume save to cloud failed (non-fatal):", saveErr); }
        }
      } else {
        setProgressMsg("Using your saved resume…");
        await new Promise(r => setTimeout(r, 600));
      }
      setProgressMsg("Scanning live job listings for your profile…");
      const searchPayloadResume = currentResumeData || { title: pivotTarget || "professional", skills: [], experience_years: null, industries: [] };
      await streamJobSearch(
        { resumeData: searchPayloadResume, location, remoteOnly, excludeRemote, radius: searchRadius, pivotTarget, fairChance },
        { onFirstBatch: () => {
            setSearchesLeft(n => n === 999 ? 999 : n - 1);
            if (user) saveSearchUsage();
            setProgress(100);
            setTimeout(() => { setScreen("results"); setActiveTab("results"); if (window.clarity) window.clarity("event", "search_completed"); if (window.gtag) window.gtag("event", "search_completed"); }, 400);
          } }
      );
    } catch (err) {
      console.error("Search error:", err);
      setScreen("upload");
      setSearchError(err.message || "Job search failed. Please try again.");
    }
  };

  const streamJobSearch = async (body, { onFirstBatch } = {}) => {
    const searchRes = await fetch(`${process.env.REACT_APP_API_URL}/search-jobs`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...body, stream: true }),
    });
    if (!searchRes.ok || !searchRes.body) throw new Error("Job search failed");
    const reader = searchRes.body.getReader();
    const decoder = new TextDecoder();
    let buf = "", total = 0, first = true, streamError = null;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop();
      for (const line of lines) {
        if (!line.trim()) continue;
        let evt; try { evt = JSON.parse(line); } catch (e) { continue; }
        if (evt.type === "jobs" && Array.isArray(evt.jobs) && evt.jobs.length) {
          total += evt.jobs.length;
          if (first) {
            first = false;
            setActiveJobs([...evt.jobs].sort((a, b) => (b.match || 0) - (a.match || 0)));
            if (onFirstBatch) onFirstBatch();
          } else {
            setActiveJobs(prev => [...prev, ...evt.jobs].sort((a, b) => (b.match || 0) - (a.match || 0)));
          }
        } else if (evt.type === "error") {
          streamError = evt.error || "Job search failed";
        }
      }
    }
    if (total === 0) throw new Error(streamError || "Job search failed");
    return total;
  };

  const startSearchWithRadius = async (newRadius) => {
    if (!resumeData) return;
    setProgress(0); setProgressMsg("Expanding your search radius…");
    setScreen("searching");
    try {
      await streamJobSearch(
        { resumeData, location, remoteOnly, excludeRemote, radius: newRadius, fairChance },
        { onFirstBatch: () => {
            setProgress(100);
            setTimeout(() => { setScreen("results"); setActiveTab("results"); if (window.clarity) window.clarity("event", "search_completed"); if (window.gtag) window.gtag("event", "search_completed"); }, 400);
          } }
      );
    } catch (err) {
      console.error("Radius search error:", err);
      setScreen("results");
    }
  };

  const toggleSave = async (jobOrId, e) => {
    e?.stopPropagation();
    const job = typeof jobOrId === "object" ? jobOrId : activeJobs.find(j => j.id === jobOrId);
    const id = job?.id ?? jobOrId;
    const isSaved = savedIds.includes(id);
    if (isSaved) {
      setSavedIds(prev => prev.filter(x => x !== id));
      setSavedJobObjects(prev => prev.filter(j => j.job_id !== id));
      if (user) await supabase.from("saved_jobs").delete().eq("user_id", user.id).eq("job_id", id);
    } else {
      const isPro = profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack";
      if (!isPro && savedIds.length >= 5) { setUpgrade("You've saved 5 matches. Upgrade to save unlimited roles — don't lose track of your best opportunities."); return; }
      setSavedIds(prev => [...prev, id]);
      if (job) setSavedJobObjects(prev => [...prev, { ...job, job_id: id }]);
      if (user && job) {
        await supabase.from("saved_jobs").upsert({ user_id: user.id, job_id: id, job_title: job.title, company: job.company, location: job.location, salary: job.salary, match_score: job.match, tags: job.tags, description: job.desc, url: job.url, source: job.source }, { onConflict: "user_id,job_id" });
      }
    }
  };

  const openRewrite = (job, e) => {
    e?.stopPropagation();
    setSelectedJob(job); setRewriteDone(false);
    setTailoredResume(""); setTailorError(""); setOriginalTailoredResume(""); setBeforeFixResume(""); setLastFixTool("");
    setAtsScore(null); setAtsError(""); setFixingGaps(false);
    setCoverLetter(""); setCoverLetterError("");
    setInterviewQuestions(null); setInterviewError("");
    setLinkedInResult(null); setLinkedInError("");
    setStrengthScore(null); setStrengthError(""); setStrengtheningResume(false); setStrengthAttempts(0); setStrengthPeaked(false); setBestStrengthScore(null); bestStrengthScoreRef.current = null; setBestStrengthResume(null); setResumeFlash(false);
    dismissResumeToast();
    setScreen("rewrite");
  };

  const doRewrite = async () => {
    const isFreeUser = !isSuperUser && (!profile || (profile?.plan !== "pro" && profile?.plan !== "pro_annual" && profile?.plan !== "pack"));
    const isPackUser = !isSuperUser && profile?.plan === "pack";
    const thisMonth = new Date().toISOString().slice(0, 7);
    const needsReset = profile?.tailors_reset_month !== thisMonth;
    if (needsReset && user) {
      await supabase.from("profiles").update({ tailors_used: 0, tailors_reset_month: thisMonth }).eq("id", user.id);
      setProfile(prev => ({ ...prev, tailors_used: 0, tailors_reset_month: thisMonth }));
    }
    const tailorsUsed = needsReset ? 0 : (profile?.tailors_used ?? 0);
    if ((isFreeUser && (tailorsUsed >= 1 || usedRewrite)) || (isPackUser && tailorsUsed >= 5)) { setUpgrade("You've used your free tailor. Tailoring your resume is the #1 way to get past ATS — upgrade to do it for every job."); if (window.clarity) window.clarity("event", "upgrade_prompt_shown"); if (window.gtag) window.gtag("event", "upgrade_prompt_shown"); return; }
    if (!resumeText || resumeText.length < 200) { setTailorError("We couldn't read enough text from your resume. Please re-upload as a PDF for best results."); return; }
    setRewriting(true); setTailorError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/tailor-resume`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resumeText, job: selectedJob, pivotTarget }),
      });
      const data = await res.json();
      if (!res.ok || !data.tailoredResume) throw new Error(data.error || "Tailoring failed");
      setTailoredResume(data.tailoredResume);
      setOriginalTailoredResume(data.tailoredResume);
      setRewriteDone(true); setUsedRewrite(true);
      showResumeToast(`Resume tailored for ${selectedJob?.company}`, "teal", "tailored");
      if (user) {
        const currentTailorsUsed = profile?.tailors_used || 0;
        await supabase.from("profiles").update({ tailors_used: currentTailorsUsed + 1 }).eq("id", user.id);
        setProfile(prev => ({ ...prev, tailors_used: (prev?.tailors_used || 0) + 1 }));
        if (currentTailorsUsed === 0) {
          fetch(`${process.env.REACT_APP_API_URL}/send-review-request`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: user.email })
          }).catch(() => {});
        }
      }
    } catch (err) {
      setTailorError(err.message || "Something went wrong. Please try again.");
    } finally {
      setRewriting(false);
    }
  };

  const [activeJobs, setActiveJobs] = useState(ALL_JOBS);
  const [perPage, setPerPage] = useState(20);
  const [searchError, setSearchError] = useState("");

  // ── ATS Score state ──
  const [atsScore, setAtsScore] = useState(null);
  const [achievementFeedback, setAchievementFeedback] = useState(null);
  const [freeAtsUsed, setFreeAtsUsed] = useState(false);
  const [jobAtsResults, setJobAtsResults] = useState({});
  const [jobAtsLoading, setJobAtsLoading] = useState({});

  useEffect(() => {
    if (profile && (profile.ats_used ?? 0) >= 1) {
      const isPaid = isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack";
      if (!isPaid) setFreeAtsUsed(true);
    }
  }, [profile]);
  const [uploadTab, setUploadTab] = useState("upload"); // "upload" | "build"
  const [buildForm, setBuildForm] = useState({ name:"", targetRole:"", recentTitle:"", recentCompany:"", yearsExp:"", skills:"", education:"" });
  const [buildLoading, setBuildLoading] = useState(false);
  const [buildError, setBuildError] = useState("");
  const [builtResume, setBuiltResume] = useState("");
  const [buildDraft, setBuildDraft] = useState(false);
  const [atsLoading, setAtsLoading] = useState(false);
  const [atsError, setAtsError] = useState("");
  const [fixingGaps, setFixingGaps] = useState(false);
  const [atsAttempts, setAtsAttempts] = useState(0);
  const [keywordPrompt, setKeywordPrompt] = useState(null); // keyword string being prompted
  const [addedKeywords, setAddedKeywords] = useState(new Set()); // keywords already added
  const [keywordAdding, setKeywordAdding] = useState(false); // loading state
  const [keywordAddedToast, setKeywordAddedToast] = useState(null); // success toast message
  const [atsPeaked, setAtsPeaked] = useState(false);
  const bestAtsScoreRef = useRef(null);
  const [bestAtsScore, setBestAtsScore] = useState(null);
  const [bestAtsResume, setBestAtsResume] = useState(null);

  // ── Cover Letter state ──
  const [coverLetter, setCoverLetter] = useState("");
  const [coverLetterLoading, setCoverLetterLoading] = useState(false);
  const [coverLetterError, setCoverLetterError] = useState("");
  const [coverLetterTone, setCoverLetterTone] = useState("professional");
  const [coverLetterCopied, setCoverLetterCopied] = useState(false);
  const [interviewQuestions, setInterviewQuestions] = useState(null);
  const [interviewLoading, setInterviewLoading] = useState(false);
  const [interviewError, setInterviewError] = useState("");
  const [interviewCopied, setInterviewCopied] = useState(false);
  const [interviewMode, setInterviewMode] = useState("study");
  const [mockCurrentIdx, setMockCurrentIdx] = useState(0);
  const [mockAnswer, setMockAnswer] = useState("");
  const [mockFeedback, setMockFeedback] = useState(null);
  const [mockFeedbackLoading, setMockFeedbackLoading] = useState(false);
  const [mockFeedbackError, setMockFeedbackError] = useState("");
  const [mockComplete, setMockComplete] = useState(false);
  const [linkedInResult, setLinkedInResult] = useState(null);
  const [linkedInLoading, setLinkedInLoading] = useState(false);
  const [linkedInError, setLinkedInError] = useState("");
  const [linkedInCopied, setLinkedInCopied] = useState({ headline: false, summary: false });

  // ── Resume Strength Score state ──
  const [strengthScore, setStrengthScore] = useState(null);
  const [strengthLoading, setStrengthLoading] = useState(false);
  const [strengthError, setStrengthError] = useState("");
  const [strengtheningResume, setStrengtheningResume] = useState(false);
  const [strengthAttempts, setStrengthAttempts] = useState(0);
  const [strengthPeaked, setStrengthPeaked] = useState(false);
  const [bestStrengthScore, setBestStrengthScore] = useState(null);
  const bestStrengthScoreRef = useRef(null);
  const [bestStrengthResume, setBestStrengthResume] = useState(null);
  const [resumeFlash, setResumeFlash] = useState(false);

  const runAtsScore = async () => {
    const isPaid = isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack";
    if (!isPaid) { setFreeAtsUsed(true); }
    if (!tailoredResume || !selectedJob) return;
    setAtsLoading(true); setAtsError(""); setAtsScore(null);
    try {
      const res = await fetch("https://resumatch-backend-production.up.railway.app/ats-score", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tailoredResume, job: selectedJob, pivotTarget }) });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "ATS scoring failed");
      setAtsScore(data);
      setBestAtsScore(data.score); bestAtsScoreRef.current = data.score;
      setBestAtsResume(tailoredResume);
      setAtsAttempts(1); setAtsPeaked(false);
      // Fire achievement framing check in background
      setAchievementFeedback(null);
      fetch("https://resumatch-backend-production.up.railway.app/achievement-feedback", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ resumeText: tailoredResume }) })
        .then(r => r.json()).then(d => { if (d.success && d.flags?.length > 0) setAchievementFeedback(d.flags); })
        .catch(() => {});
    } catch (e) { setAtsError("Could not generate ATS score. Please try again."); }
    finally { setAtsLoading(false); }
  };

  const runAtsScoreQuiet = async (resumeTextOverride) => {
    try {
      const res = await fetch("https://resumatch-backend-production.up.railway.app/ats-score", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tailoredResume: resumeTextOverride, job: selectedJob }) });
      let data;
      try { data = await res.json(); } catch { return null; }
      if (!data.success) throw new Error(data.error || "Unknown error from server");
      return data;
    } catch (e) { console.error("Silent ATS score error:", e); return null; }
  };

  const handleJobAtsCheck = async (job) => {
    if (!resumeText || resumeText.length < 100) return;
    const isPaid = isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack";
    if (!isPaid && freeAtsUsed) { if (window.clarity) window.clarity("event", "upgrade_prompt_shown"); if (window.gtag) window.gtag("event", "upgrade_prompt_shown"); return; }
    setJobAtsLoading(prev => ({ ...prev, [job.id]: true }));
    try {
      const res = await fetch("https://resumatch-backend-production.up.railway.app/ats-score", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tailoredResume: resumeText, job, pivotTarget })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "ATS check failed");
      setJobAtsResults(prev => ({ ...prev, [job.id]: data }));
      if (!isPaid) {
        setFreeAtsUsed(true);
        const currentAtsUsed = profile?.ats_used || 0;
        await supabase.from("profiles").update({ ats_used: currentAtsUsed + 1 }).eq("id", user.id);
        setProfile(prev => ({ ...prev, ats_used: (prev?.ats_used || 0) + 1 }));
      }
    } catch (e) {
      setJobAtsResults(prev => ({ ...prev, [job.id]: { error: true } }));
    } finally {
      setJobAtsLoading(prev => ({ ...prev, [job.id]: false }));
    }
  };

  const handleAddKeyword = async (keyword) => {
    setKeywordAdding(true);
    setKeywordPrompt(null);
    try {
      const res = await fetch("https://resumatch-backend-production.up.railway.app/add-keyword", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resumeText: tailoredResume, keyword, job: selectedJob })
      });
      const data = await res.json();
      if (data.updatedResume) {
        setTailoredResume(data.updatedResume);
        setAddedKeywords(prev => new Set([...prev, keyword]));
        setKeywordAddedToast(keyword);
        setTimeout(() => setKeywordAddedToast(null), 4000);
      }
    } catch (err) {
      console.error("Add keyword error:", err.message);
    } finally {
      setKeywordAdding(false);
    }
  };

  const fixMyGaps = async () => {
    if (!tailoredResume || !selectedJob) return;
    if (strengtheningResume) { setAtsError("Please wait — resume strengthening is in progress."); return; }
    if (atsAttempts === 0) return;
    setBeforeFixResume(tailoredResume); setLastFixTool("ATS Fix My Gaps"); setFixingGaps(true); setAtsError("");
    try {
      const fixAttempt = Math.max(1, atsAttempts - 1);
      const missingKeywords = atsScore?.keywords_missing || [];
      console.log("[Fix My Gaps] sending attempt:", fixAttempt, "resumeLen:", tailoredResume?.length, "keywords:", missingKeywords);
      const res = await fetch("https://resumatch-backend-production.up.railway.app/fix-ats-gaps", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ resumeText: tailoredResume, job: selectedJob, missingKeywords, attempt: fixAttempt, pivotTarget }) });
      console.log("[Fix My Gaps] HTTP status:", res.status);
      let data;
      try { data = await res.json(); } catch(jsonErr) { throw new Error(`Bad response (${res.status}): not JSON`); }
      if (!data.success) throw new Error(data.error || data.details || `Server error ${res.status}`);
      const fixedResume = data.fixedResume;
      const scoreData = await runAtsScoreQuiet(fixedResume);
      const newAttempts = atsAttempts + 1;
      setAtsAttempts(newAttempts);
      const isFinalAttempt = newAttempts >= 4;
      if (scoreData) {
        const improved = scoreData.score > (bestAtsScoreRef.current || 0);
        if (scoreData.grade === "A" || improved) {
          setTailoredResume(fixedResume);
          setAtsScore(scoreData);
          setBestAtsScore(scoreData.score); bestAtsScoreRef.current = scoreData.score;
          setBestAtsResume(fixedResume);
          const improvedMsg = scoreData.grade === "A" ? "ATS optimized — resume is ready to pass filters" : `ATS score improved to ${scoreData.grade} (${scoreData.score}/100) — updated`;
          showResumeToast(improvedMsg, "teal", "ats");
          setAtsPeaked(isFinalAttempt);
        } else {
          if (bestAtsResume) { setTailoredResume(bestAtsResume); setAtsScore(prev => bestAtsScore ? { ...prev, score: bestAtsScore } : prev); }
          const noImprovMsg = isFinalAttempt ? "All attempts complete — showing your best version" : "Score unchanged — keeping your best version";
          showResumeToast(noImprovMsg, "teal", "ats");
          if (isFinalAttempt) setAtsPeaked(true);
          else setAtsError("__no_improvement__");
        }
      } else {
        setTailoredResume(fixedResume);
        showResumeToast("Resume updated — re-run ATS check to see new score", "teal", "ats");
      }
      setResumeFlash(true); setTimeout(() => setResumeFlash(false), 2000);
    } catch (e) {
      console.error("Fix gaps error:", e.message);
      if (e.message?.includes("504") || e.message?.includes("timeout") || e.message?.includes("network")) setAtsError("Request timed out — please try again.");
      else setAtsError("Fix failed — please try again.");
    } finally { setFixingGaps(false); }
  };

  const runCoverLetter = async () => {
    const isPaid = isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack";
    if (!isPaid) { setUpgrade("A strong cover letter can be the difference between a callback and silence. Unlock Cover Letter Generator on Match Pack ($9.99) or Pro."); return; }
    if (!tailoredResume || !selectedJob) return;
    setCoverLetterLoading(true); setCoverLetterError(""); setCoverLetter("");
    try {
      const res = await fetch("https://resumatch-backend-production.up.railway.app/cover-letter", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tailoredResume, job: selectedJob, tone: coverLetterTone }) });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Cover letter generation failed");
      setCoverLetter(data.coverLetter);
    } catch (e) { setCoverLetterError("Could not generate cover letter. Please try again."); }
    finally { setCoverLetterLoading(false); }
  };

  const copyCoverLetter = () => {
    navigator.clipboard.writeText(coverLetter);
    setCoverLetterCopied(true);
    setTimeout(() => setCoverLetterCopied(false), 2000);
  };

  const runInterviewPrep = async () => {
    const isPaid = isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack";
    if (!isPaid) { setUpgrade("Walking in prepared wins interviews. Unlock Interview Prep on Match Pack ($9.99) or Pro — get targeted questions for every role you apply to."); return; }
    setInterviewLoading(true); setInterviewError(""); setInterviewQuestions(null);
    try {
      const res = await fetch("https://resumatch-backend-production.up.railway.app/interview-prep", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tailoredResume, job: selectedJob, pivotTarget }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to generate questions");
      setInterviewQuestions(data.questions);
    } catch (e) { setInterviewError("Could not generate interview questions. Please try again."); }
    finally { setInterviewLoading(false); }
  };

  const copyInterviewQuestions = () => {
    if (!interviewQuestions) return;
    const text = interviewQuestions.map(cat =>
      `${cat.category}\n${cat.questions.map((q, i) => `${i + 1}. ${q.q}\n   Tip: ${q.tip}`).join("\n")}`
    ).join("\n\n");
    navigator.clipboard.writeText(text);
    setInterviewCopied(true);
    setTimeout(() => setInterviewCopied(false), 2000);
  };

  const submitMockAnswer = async () => {
    if (!mockAnswer.trim()) return;
    const allQuestions = interviewQuestions.flatMap(cat => cat.questions.map(q => ({ ...q, category: cat.category })));
    const current = allQuestions[mockCurrentIdx];
    setMockFeedbackLoading(true); setMockFeedbackError(""); setMockFeedback(null);
    try {
      const res = await fetch("https://resumatch-backend-production.up.railway.app/interview-feedback", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: current.q, answer: mockAnswer, tip: current.tip, job: selectedJob }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to get feedback");
      setMockFeedback(data.feedback);
    } catch (e) { setMockFeedbackError("Could not get feedback. Please try again."); }
    finally { setMockFeedbackLoading(false); }
  };

  const nextMockQuestion = () => {
    const allQuestions = interviewQuestions.flatMap(cat => cat.questions);
    if (mockCurrentIdx + 1 >= allQuestions.length) {
      setMockComplete(true);
    } else {
      setMockCurrentIdx(prev => prev + 1);
      setMockAnswer("");
      setMockFeedback(null);
      setMockFeedbackError("");
    }
  };

  const resetMockInterview = () => {
    setMockCurrentIdx(0);
    setMockAnswer("");
    setMockFeedback(null);
    setMockFeedbackError("");
    setMockComplete(false);
  };

  const runLinkedIn = async () => {
    const isPaid = isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack";
    if (!isPaid) { setUpgrade("Recruiters check LinkedIn before they call you. Unlock the LinkedIn Rewriter on Match Pack ($9.99) or Pro — make your profile work as hard as your resume."); return; }
    setLinkedInLoading(true); setLinkedInError(""); setLinkedInResult(null);
    try {
      const res = await fetch("https://resumatch-backend-production.up.railway.app/linkedin-rewrite", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tailoredResume, job: selectedJob, pivotTarget }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to generate LinkedIn content");
      setLinkedInResult(data);
    } catch (e) { setLinkedInError("Could not generate LinkedIn content. Please try again."); }
    finally { setLinkedInLoading(false); }
  };

  const copyLinkedIn = (field, text) => {
    navigator.clipboard.writeText(text);
    setLinkedInCopied(prev => ({ ...prev, [field]: true }));
    setTimeout(() => setLinkedInCopied(prev => ({ ...prev, [field]: false })), 2000);
  };

  const runStrengthScoreQuiet = async (resumeTextOverride) => {
    try {
      const res = await fetch("https://resumatch-backend-production.up.railway.app/strength-score", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tailoredResume: resumeTextOverride, job: selectedJob }) });
      let data;
      try { data = await res.json(); } catch { return null; }
      if (!data || !data.success) return null;
      return data;
    } catch { return null; }
  };

  const strengthenResume = async () => {
    if (!tailoredResume || !selectedJob) return;
    if (fixingGaps) { setStrengthError("Please wait — ATS gap fix is in progress."); return; }
    setBeforeFixResume(tailoredResume); setLastFixTool("Strengthen Resume"); setStrengtheningResume(true); setStrengthError("");
    try {
      if (strengthAttempts === 0) {
        const scoreData = await runStrengthScoreQuiet(tailoredResume);
        if (scoreData) {
          setStrengthScore(scoreData);
          setBestStrengthScore(scoreData.score); bestStrengthScoreRef.current = scoreData.score;
          setBestStrengthResume(tailoredResume); setStrengthAttempts(1);
        }
        setStrengtheningResume(false);
        return;
      }
      const rewriteAttempt = strengthAttempts - 1;
      const res = await fetch("https://resumatch-backend-production.up.railway.app/strengthen-resume", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ resumeText: tailoredResume, job: selectedJob, attempt: rewriteAttempt, pivotTarget }) });
      console.log("[Strengthen] HTTP status:", res.status);
      let data;
      try { data = await res.json(); } catch(jsonErr) { throw new Error(`Bad response (${res.status}): not JSON`); }
      if (!data.success) throw new Error(data.error || data.details || `Server error ${res.status}`);
      const newResume = data.strengthenedResume;
      const scoreData = await runStrengthScoreQuiet(newResume);
      const newAttempts = strengthAttempts + 1;
      setStrengthAttempts(newAttempts);
      if (scoreData) {
        const improved = scoreData.score > (bestStrengthScoreRef.current || 0);
        const isFinalAttempt = newAttempts >= 4;
        if (scoreData.grade === "A" || improved) {
          setTailoredResume(newResume);
          setStrengthScore(scoreData);
          setBestStrengthScore(scoreData.score); bestStrengthScoreRef.current = scoreData.score;
          setBestStrengthResume(newResume);
          const improvedMsg = scoreData.grade === "A" ? "Resume maximized — ready to impress hiring managers" : `Resume improved to ${scoreData.grade} (${scoreData.score}/100) — updated`;
          showResumeToast(improvedMsg, "gold", "strength");
          setStrengthPeaked(isFinalAttempt);
          setResumeFlash(true); setTimeout(() => setResumeFlash(false), 2000);
        } else {
          if (bestStrengthResume) { setTailoredResume(bestStrengthResume); setStrengthScore(prev => bestStrengthScore ? { ...prev, score: bestStrengthScore } : prev); }
          const noImprovMsg = isFinalAttempt ? "All attempts complete — showing your best version" : "Score unchanged — keeping your best version";
          showResumeToast(noImprovMsg, "gold", "strength");
          setResumeFlash(true); setTimeout(() => setResumeFlash(false), 2000);
          if (isFinalAttempt) setStrengthPeaked(true);
          else setStrengthError("__no_improvement__");
        }
      } else {
        setTailoredResume(newResume);
        showResumeToast("Resume rewritten — run Re-run to see your new score", "gold", "strength");
      }
    } catch (e) {
      console.error("Strengthen error:", e.message);
      if (e.message?.includes("504") || e.message?.includes("timeout") || e.message?.includes("network")) setStrengthError("Request timed out — please try again.");
      else setStrengthError("Strengthening failed — please try again.");
    }
    finally { setStrengtheningResume(false); }
  };

  const runStrengthScore = async () => {
    const isPaid = isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack";
    if (!isPaid) { setUpgrade("Don't send a weak resume and wonder why you're not hearing back. Unlock Resume Strength Score on Match Pack ($9.99) or Pro."); return; }
    if (!tailoredResume || !selectedJob) return;
    setStrengthLoading(true); setStrengthError(""); setStrengthScore(null);
    try {
      const res = await fetch("https://resumatch-backend-production.up.railway.app/strength-score", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tailoredResume, job: selectedJob, pivotTarget }) });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Strength scoring failed");
      setStrengthScore(data);
      setBestStrengthScore(data.score); bestStrengthScoreRef.current = data.score;
      setBestStrengthResume(tailoredResume); setStrengthAttempts(1);
    } catch (e) { setStrengthError("Could not generate strength score. Please try again."); }
    finally { setStrengthLoading(false); }
  };

  const handleSaveVersion = async () => {
    if (!profile?.id || !tailoredResume || !saveVersionLabel.trim()) return;
    setSaveVersionLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/save-version`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: profile.id, label: saveVersionLabel.trim(), resume_text: tailoredResume, job_title: selectedJob?.title || "", company: selectedJob?.company || "" }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setSaveVersionSuccess(true);
      setSavedVersions(prev => [data.version, ...prev]);
      setTimeout(() => { setShowSaveModal(false); setSaveVersionSuccess(false); setSaveVersionLabel(""); }, 1500);
    } catch (e) { alert("Failed to save version. Please try again."); }
    finally { setSaveVersionLoading(false); }
  };

  const handleLoadVersions = async () => {
    if (!profile?.id) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/versions/${profile.id}`);
      const data = await res.json();
      if (data.success) setSavedVersions(data.versions);
    } catch (e) { console.error("Failed to load versions", e); }
  };

  const handleDeleteVersion = async (id) => {
    if (!profile?.id) return;
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/version/${id}`, {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: profile.id }),
      });
      setSavedVersions(prev => prev.filter(v => v.id !== id));
    } catch (e) { console.error("Failed to delete version", e); }
  };

  const handlePDFDownload = async () => {
    const isPaid = isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack";
    if (!isPaid) { setUpgrade("Download your tailored resume as a PDF and send it today. Available on Match Pack ($9.99) or Pro."); return; }
    await downloadResumeAsPDF(tailoredResume, selectedJob?.title || "Role", selectedJob?.company || "Company");
  };

  const handleDocxDownload = async () => {
    const isPaid = isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack";
    if (!isPaid) { setUpgrade("Download your tailored resume as a Word doc and make it yours. Available on Match Pack ($9.99) or Pro."); return; }
    const { downloadResumeAsDocx: dlDocx1 } = await import("./utils"); dlDocx1(tailoredResume, selectedJob?.title || "Role", selectedJob?.company || "Company");
  };

  const handleCoverLetterPDFDownload = async () => {
    const isPaid = isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack";
    if (!isPaid) { setUpgrade("Download your tailored resume as a PDF and send it today. Available on Match Pack ($9.99) or Pro."); return; }
    await downloadResumeAsPDF(coverLetter, `Cover Letter — ${selectedJob?.title || "Role"}`, selectedJob?.company || "Company");
  };

  const handleCoverLetterDocxDownload = async () => {
    const isPaid = isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack";
    if (!isPaid) { setUpgrade("Download your tailored resume as a Word doc and make it yours. Available on Match Pack ($9.99) or Pro."); return; }
    const { downloadResumeAsDocx: dlDocx2 } = await import("./utils"); dlDocx2(coverLetter, `Cover Letter — ${selectedJob?.title || "Role"}`, selectedJob?.company || "Company");
  };

  const loadJobDetails = async (job) => {
    const id = job.id;
    if (job.requirements && job.requirements.length > 0) {
      setJobDetails(prev => ({ ...prev, [id]: { requirements: job.requirements, responsibilities: job.responsibilities, loading: false } }));
      return;
    }
    if (jobDetails[id] && !jobDetails[id].loading) return;
    setJobDetails(prev => ({ ...prev, [id]: { loading: true } }));
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 500, messages: [{ role: "user", content: `Based on this job posting, generate realistic requirements and responsibilities.\n\nJob Title: ${job.title}\nCompany: ${job.company}\nDescription: ${job.desc}\nTags: ${(job.tags || []).join(", ")}\n\nRespond ONLY with valid JSON in this exact format, no other text:\n{"requirements":["req1","req2","req3","req4"],"responsibilities":["resp1","resp2","resp3","resp4"]}` }] })
      });
      const data = await res.json();
      const text = data.content?.map(b => b.text || "").join("") || "{}";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setJobDetails(prev => ({ ...prev, [id]: { requirements: parsed.requirements || [], responsibilities: parsed.responsibilities || [], loading: false } }));
    } catch (e) {
      setJobDetails(prev => ({ ...prev, [id]: { requirements: [`Experience in ${job.tags?.[0] || "relevant field"}`, "Strong communication skills", "Ability to work in a fast-paced environment", "Bachelor's degree or equivalent experience"], responsibilities: [`Work on ${job.title} responsibilities at ${job.company}`, "Collaborate with cross-functional teams", "Drive projects from conception to completion", "Report progress to stakeholders"], loading: false }}));
    }
  };

  const visibleJobs = perPage === 0 ? activeJobs : activeJobs.slice(0, perPage);
  const displayedJobs = activeTab === "saved"
    ? (user ? savedJobObjects.map(j => ({ ...j, id: j.job_id, logo: j.logo || "💼", posted: j.posted || "", tags: j.tags || [], desc: j.description })) : activeJobs.filter(j => savedIds.includes(j.id)))
    : visibleJobs;

  const handleRefine = async () => {
    const text = refineInput.trim();
    if (!text || refining) return;
    setRefining(true); setRefineInput("");
    const allRefinements = [...refineHistory, text].join(" ");
    try {
      const searchRes = await fetch(`${process.env.REACT_APP_API_URL}/search-jobs`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resumeData, location, remoteOnly, excludeRemote, refinement: allRefinements, radius: searchRadius, fairChance }),
      });
      const searchData = await searchRes.json();
      if (searchData.success && searchData.jobs?.length > 0) {
        setActiveJobs([...searchData.jobs].sort((a, b) => (b.match || 0) - (a.match || 0)));
        setRefineHistory(prev => [...prev, text]);
        setRefinedNote(text);
      } else {
        showResumeToast("No results found for that refinement — try different wording.", "accent", "!");
      }
    } catch (err) {
      showResumeToast("Refinement search failed. Please try again.", "accent", "!");
    }
    setRefining(false);
  };

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:C.text, fontFamily:"'DM Sans',sans-serif" }}>
      {/* ── Mobile Banner — must be first child ── */}
      <MobileBanner />


      <style>{`

      `}</style>

      {upgrade && <UpgradeModal reason={upgrade} onClose={() => setUpgrade(null)} onUpgrade={handleUpgrade}/>}

      {/* Save Version Modal */}
      {showSaveModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => setShowSaveModal(false)}>
          <div style={{ background:"#fff", borderRadius:18, padding:"28px 32px", width:420, maxWidth:"90vw", boxShadow:"0 20px 60px rgba(0,0,0,0.18)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:800, marginBottom:6 }}>Save This Version</div>
            <div style={{ fontSize:13, color:C.muted, marginBottom:20, fontFamily:"'Space Mono',monospace" }}>Give this tailored resume a name so you can find it later.</div>
            <input
              type="text"
              value={saveVersionLabel}
              onChange={e => setSaveVersionLabel(e.target.value)}
              placeholder="e.g. Google PM — March 2026"
              style={{ width:"100%", border:`1.5px solid ${C.border}`, borderRadius:9, padding:"10px 14px", fontSize:14, fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box", outline:"none", marginBottom:16 }}
            />
            {saveVersionSuccess
              ? <div style={{ textAlign:"center", color:C.teal, fontWeight:700, fontSize:15 }}>✓ Saved!</div>
              : <button onClick={handleSaveVersion} disabled={!saveVersionLabel.trim() || saveVersionLoading}
                  style={{ width:"100%", background:C.teal, color:"#fff", border:"none", borderRadius:9, padding:"11px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity: !saveVersionLabel.trim() ? 0.5 : 1 }}>
                  {saveVersionLoading ? "Saving..." : "Save Version"}
                </button>
            }
          </div>
        </div>
      )}

      {/* Versions Panel */}
      {showVersionsPanel && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => setShowVersionsPanel(false)}>
          <div style={{ background:"#fff", borderRadius:18, padding:"28px 32px", width:520, maxWidth:"92vw", maxHeight:"80vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.18)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:800, marginBottom:6 }}>My Saved Versions</div>
            <div style={{ fontSize:13, color:C.muted, marginBottom:20, fontFamily:"'Space Mono',monospace" }}>Load a saved version to continue working on it.</div>
            {savedVersions.length === 0
              ? <div style={{ color:C.muted, fontSize:13, fontFamily:"'Space Mono',monospace" }}>No saved versions yet.</div>
              : savedVersions.map(v => (
                <div key={v.id} style={{ border:`1px solid ${C.border}`, borderRadius:12, padding:"14px 16px", marginBottom:10, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, fontFamily:"'DM Sans',sans-serif" }}>{v.label}</div>
                    {v.job_title && <div style={{ fontSize:12, color:C.muted, fontFamily:"'Space Mono',monospace", marginTop:2 }}>{v.job_title}{v.company ? ` — ${v.company}` : ""}</div>}
                    <div style={{ fontSize:11, color:C.muted, fontFamily:"'Space Mono',monospace", marginTop:2 }}>{new Date(v.created_at).toLocaleDateString()}</div>
                  </div>
                  <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                    <button onClick={() => { setTailoredResume(v.resume_text); setShowVersionsPanel(false); showResumeToast(`"${v.label}" loaded successfully`, "teal", "ats"); }}
                      style={{ background:C.teal, color:"#fff", border:"none", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                      Load
                    </button>
                    <button onClick={() => handleDeleteVersion(v.id)}
                      style={{ background:"transparent", border:`1px solid ${C.accent}`, color:C.accent, borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                      Delete
                    </button>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      )}
      {showHowItWorks && <HowItWorksModal onClose={() => setShowHowItWorks(false)}/>}

      {/* ── Keyword Prompt Modal ── */}
      {keywordPrompt && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 16px" }}>
          <div style={{ background:"#fff", borderRadius:20, padding:"32px 28px", maxWidth:420, width:"100%", boxShadow:"0 8px 40px rgba(0,0,0,0.18)" }}>
            <div style={{ fontSize:18, fontWeight:700, color:"#1a1a2e", marginBottom:10 }}>
              Add "{keywordPrompt}" to your resume?
            </div>
            <div style={{ fontSize:14, color:"#64748b", lineHeight:1.6, marginBottom:24 }}>
              We'll find the best place in your resume to naturally work in this keyword based on your experience — no fabrication, just a targeted tweak to close the gap.
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button
                onClick={() => handleAddKeyword(keywordPrompt)}
                disabled={keywordAdding}
                style={{ flex:1, background:"#1a1a2e", color:"#fff", border:"none", borderRadius:10, padding:"12px", fontSize:14, fontWeight:700, cursor:"pointer" }}>
                {keywordAdding ? "Adding…" : "Add to Resume"}
              </button>
              <button
                onClick={() => setKeywordPrompt(null)}
                disabled={keywordAdding}
                style={{ flex:1, background:"#f8fafc", color:"#64748b", border:"1.5px solid #e2e8f0", borderRadius:10, padding:"12px", fontSize:14, fontWeight:600, cursor:"pointer" }}>
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Keyword Added Toast ── */}
      {keywordAddedToast && (
        <div style={{ position:"fixed", bottom:32, left:"50%", transform:"translateX(-50%)", zIndex:500, background:"#1a1a2e", border:"1.5px solid #f59e0b", borderRadius:16, padding:"14px 24px", boxShadow:"0 8px 40px rgba(0,0,0,0.15)", display:"flex", alignItems:"center", gap:10, whiteSpace:"nowrap" }}>
          <span style={{ fontSize:16 }}>✨</span>
          <span style={{ fontSize:14, fontWeight:600, color:"#fff" }}>"{keywordAddedToast}" has been added to your resume</span>
          <span style={{ fontSize:13, color:"#f59e0b", fontWeight:600, marginLeft:4 }}>↑ See highlight above</span>
        </div>
      )}

      {/* ── Auth Modal ── */}
      <AuthModal
        show={showAuth}
        authMode={authMode} setAuthMode={setAuthMode}
        authEmail={authEmail} setAuthEmail={setAuthEmail}
        authPassword={authPassword} setAuthPassword={setAuthPassword}
        authName={authName} setAuthName={setAuthName}
        authError={authError} authLoading={authLoading}
        onClose={() => { setShowAuth(false); setAuthMode("login"); setAuthError(""); }}
        onLogin={handleLogin}
        onSignUp={handleSignUp}
        onGoogleLogin={handleGoogleLogin}
        onForgotPassword={handleForgotPassword}
        onResendConfirmation={handleResendConfirmation}
        anonLimitHit={anonLimitHit}
        onNavigate={(screen) => { setShowAuth(false); setScreen(screen); }}
      />

            {/* ── Upgrade Choice Modal ── */}
      {showPromoPopup && !user && screen !== "results" && (
        <div style={{ position:"fixed", ...(isMobile ? { top:70, bottom:"auto" } : { bottom:32 }), left:"50%", transform:"translateX(-50%)", zIndex:300, background:"#fff", border:"1.5px solid #86efac", borderRadius:16, padding:"20px 28px", boxShadow:"0 8px 40px rgba(0,0,0,0.15)", maxWidth:420, width:"calc(100% - 48px)", display:"flex", alignItems:"flex-start", gap:16, animation:"fadeUp .3s ease forwards" }}>
          <div style={{ fontSize:28, flexShrink:0 }}>🎉</div>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:800, fontSize:15, marginBottom:4, letterSpacing:"-0.01em" }}>Launch offer — 25% off Pro</div>
            <div style={{ fontSize:13, color:C.sub, marginBottom:12, lineHeight:1.6 }}>Use code <span style={{ fontFamily:"'Space Mono',monospace", fontWeight:700, color:"#16a34a", background:"#f0fdf4", padding:"1px 7px", borderRadius:5 }}>LAUNCH25</span> at checkout for 25% off your first month of Pro Matcher.</div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={() => { setShowPromoPopup(false); setShowUpgradeChoice(true); }} style={{ background:C.accent, color:"#fff", border:"none", borderRadius:8, padding:"8px 16px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Claim offer →</button>
              <button onClick={() => setShowPromoPopup(false)} style={{ background:"transparent", color:C.muted, border:`1px solid ${C.border}`, borderRadius:8, padding:"8px 12px", fontSize:13, cursor:"pointer" }}>Dismiss</button>
            </div>
          </div>
        </div>
      )}

      {showUpgradeChoice && (
        <div onClick={() => setShowUpgradeChoice(false)} style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.35)", backdropFilter:"blur(6px)", zIndex:200, display:"flex", alignItems:"flex-start", justifyContent:"center", padding: isMobile ? "16px 12px" : "24px", overflowY:"auto" }}>
          <div onClick={e => e.stopPropagation()} style={{ background:"#fff", borderRadius:20, padding: isMobile ? "28px 20px" : "36px 32px", width:"100%", maxWidth:700, boxShadow:"0 20px 60px rgba(0,0,0,0.15)", position:"relative", margin:"auto" }}>
            <button onClick={() => setShowUpgradeChoice(false)} style={{ position:"absolute", top:16, right:18, background:"transparent", border:"none", color:C.muted, fontSize:22, cursor:"pointer" }}>×</button>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontWeight:800, fontSize:22, textAlign:"center", margin:"0 0 8px" }}>Choose your plan</h2>
            <p style={{ color:C.sub, fontSize:13, textAlign:"center", margin:"0 0 8px" }}>Stop applying blind. Upgrade to tailor, match, and actually land interviews.</p>
            <p style={{ textAlign:"center", fontSize:12, margin:"0 0 24px", fontFamily:"'Space Mono',monospace" }}>
              <span style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:6, padding:"3px 10px", color:"#15803d", fontWeight:700 }}>LAUNCH25</span>
              <span style={{ color:C.muted, marginLeft:8 }}>— 25% off Pro your first month</span>
            </p>
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:14, maxWidth:540, margin:"0 auto" }}>

              {/* Match Pack — dark navy */}
              <div style={{ background:"#1a1a2e", border:"2px solid #0d9488", borderRadius:14, padding:"22px 18px", cursor:"pointer", position:"relative" }}
                onClick={() => { setShowUpgradeChoice(false); const pid = inlinePromoState?.valid && inlinePromoState.plan === "pack" ? inlinePromoState.promoId : null; handleUpgrade("pack", pid); }}>
                <div style={{ display:"inline-block", fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:20, marginBottom:10, background:"#0d9488", color:"#fff" }}>Pay once, try everything</div>
                <div style={{ fontWeight:700, fontSize:15, color:"#e0dcea", marginBottom:3 }}>Match Pack</div>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, color:"#fff", lineHeight:1, marginBottom:2 }}>$9.99</div>
                <div style={{ fontSize:11, color:"#5a5a80", marginBottom:14 }}>one-time purchase</div>
                <hr style={{ border:"none", borderTop:"0.5px solid #2a2a4a", margin:"0 0 14px" }}/>
                {["5 job matches","5 resume tailors","5 AI resume builds","ATS score + Fix My Gaps","Resume Strength Score","Cover letter generator","Interview prep questions","AI Mock Interview","LinkedIn rewriter","PDF & Word downloads","Job tracker"].map(f => (
                  <div key={f} style={{ fontSize:13, color:"#8a86aa", marginBottom:6, display:"flex", gap:8 }}>
                    <span style={{ color:"#0d9488", flexShrink:0 }}>✓</span>{f}
                  </div>
                ))}
                <button style={{ width:"100%", marginTop:14, background:"#0d9488", color:"#fff", border:"none", borderRadius:9, padding:"11px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                  Get Match Pack →
                </button>
                <p style={{ fontSize:11, color:"#4a4a6a", textAlign:"center", marginTop:7, marginBottom:0 }}>Pay once, use anytime. No subscription.</p>
              </div>

              {/* Pro Matcher */}
              <div style={{ background:"#fff", border:`2px solid ${C.accent}`, borderRadius:14, padding:"22px 18px", position:"relative", cursor:"pointer" }}
                onClick={() => { setShowUpgradeChoice(false); const pid = inlinePromoState?.valid && inlinePromoState.plan === "pro" ? inlinePromoState.promoId : null; handleUpgrade("pro", pid); }}>
                <div style={{ position:"absolute", top:-1, right:14, background:C.accent, color:"#fff", fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:"0 0 8px 8px", letterSpacing:"0.04em", textTransform:"uppercase" }}>MOST POPULAR</div>
                <div style={{ display:"inline-block", fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:20, marginBottom:10, background:"#fde8ec", color:C.accent }}>Best value</div>
                <div style={{ fontWeight:700, fontSize:15, color:"#1a1a2e", marginBottom:3 }}>Pro Matcher</div>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, color:C.accent, lineHeight:1, marginBottom:2 }}>$24.99<span style={{ fontSize:13, fontFamily:"'DM Sans',sans-serif", fontWeight:400, color:"#888" }}>/mo</span></div>
                <div style={{ fontSize:11, color:"#999", marginBottom:14 }}>cancel anytime</div>
                <hr style={{ border:"none", borderTop:"0.5px solid #e8e6e2", margin:"0 0 14px" }}/>
                {[
                  { label:"Unlimited job matches", highlight:true },
                  { label:"Unlimited resume tailors", highlight:true },
                  { label:"Unlimited AI resume builds", highlight:true },
                  { label:"ATS score + Fix My Gaps", highlight:false },
                  { label:"Resume Strength Score", highlight:false },
                  { label:"Cover letter generator", highlight:false },
                  { label:"Interview prep questions", highlight:false },
                  { label:"AI Mock Interview", highlight:false },
                  { label:"LinkedIn rewriter", highlight:false },
                  { label:"PDF & Word downloads", highlight:false },
                  { label:"Job tracker", highlight:false },
                  { label:"Priority support", highlight:false },
                ].map(f => (
                  <div key={f.label} style={{ fontSize:13, color:"#444", marginBottom:6, display:"flex", gap:8 }}>
                    <span style={{ color:C.accent, flexShrink:0 }}>✓</span>
                    {f.highlight ? <span><strong style={{ color:C.accent }}>Unlimited</strong> {f.label.replace("Unlimited ","")}</span> : f.label}
                  </div>
                ))}
                <button style={{ width:"100%", marginTop:14, background:C.accent, color:"#fff", border:"none", borderRadius:9, padding:"11px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                  Start Pro Matcher →
                </button>
                <p style={{ fontSize:11, color:"#aaa", textAlign:"center", marginTop:7, marginBottom:0 }}>Weekly job match emails included</p>
              </div>

            </div>
            <div style={{ marginTop:20, maxWidth:540, margin:"20px auto 0" }}>
              <div style={{ display:"flex", gap:8 }}>
                <input
                  value={inlinePromoCode}
                  onChange={e => { setInlinePromoCode(e.target.value); setInlinePromoState(null); }}
                  placeholder="Promo code (optional)"
                  style={{ flex:1, border:`1.5px solid ${inlinePromoState?.valid ? C.teal : inlinePromoState?.valid === false ? "#e74c3c" : C.border}`, borderRadius:8, padding:"8px 12px", fontSize:13, outline:"none" }}
                />
                <button
                  onClick={async () => {
                    if (!inlinePromoCode.trim()) return;
                    setInlinePromoLoading(true); setInlinePromoState(null);
                    try {
                      const r = await fetch(`${process.env.REACT_APP_API_URL}/validate-promo`, { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ code: inlinePromoCode.trim().toUpperCase(), plan:"pro" }) });
                      const d = await r.json();
                      setInlinePromoState(d.valid ? { valid:true, promoId:d.promoId, discount:d.discount, description:d.description, plan:"pro" } : { valid:false, error:d.error || "Invalid code" });
                    } catch { setInlinePromoState({ valid:false, error:"Could not validate" }); }
                    setInlinePromoLoading(false);
                  }}
                  disabled={inlinePromoLoading || !inlinePromoCode.trim()}
                  style={{ background:C.teal, color:"#fff", border:"none", borderRadius:8, padding:"8px 14px", fontSize:13, fontWeight:700, cursor:"pointer", opacity: inlinePromoLoading || !inlinePromoCode.trim() ? 0.5 : 1 }}>
                  {inlinePromoLoading ? "..." : "Apply"}
                </button>
              </div>
              {inlinePromoState?.valid && <div style={{ marginTop:6, fontSize:12, color:C.teal, fontWeight:600 }}>✓ {inlinePromoState.description} — click your plan to apply</div>}
              {inlinePromoState?.valid === false && <div style={{ marginTop:6, fontSize:12, color:"#e74c3c" }}>✗ {inlinePromoState.error}</div>}
            </div>
            <p style={{ textAlign:"center", fontSize:12, color:C.muted, fontFamily:"'Space Mono',monospace", marginTop:16, marginBottom:0 }}>
              Free tier: 5 searches/mo · 1 tailor/mo · 1 AI Resume Build · Save Job Matches
            </p>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <header style={{ borderBottom:`1px solid ${C.border}`, padding:"0 16px", height:60, display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, background:"rgba(247,245,242,0.95)", backdropFilter:"blur(18px)", zIndex:99 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }} onClick={() => setScreen("upload")}>
          <Logo size={30}/>
          <span style={{ fontFamily:"'Playfair Display',serif", fontWeight:800, fontSize:17, letterSpacing:"-0.01em" }}>
            get<span style={{ color:"#b02d45" }}>resu</span>match.com
          </span>
        </div>
        {/* Hamburger button - mobile only */}
        {isMobile && <button className="mobile-menu-btn" onClick={() => setShowMobileMenu(prev => !prev)}>
          <div style={{ width:22, height:2, background:C.text, marginBottom:5, borderRadius:2 }}/>
          <div style={{ width:22, height:2, background:C.text, marginBottom:5, borderRadius:2 }}/>
          <div style={{ width:22, height:2, background:C.text, borderRadius:2 }}/>
        </button>}
        <div className="nav-items" style={{ display: isMobile ? "none" : "flex", alignItems:"center", gap:10 }}>
          {(screen === "results" || screen === "rewrite") && (
            <>
              <Pill active={activeTab==="results" && screen==="results"} color={C.accent} onClick={() => { setActiveTab("results"); setScreen("results"); }}>
                {`💘 Matches (${activeJobs.length})`}
              </Pill>
              <Pill active={activeTab==="saved" && screen==="results"} color={C.teal} onClick={() => { setActiveTab("saved"); setScreen("results"); }}>
                🔖 Saved {savedIds.length > 0 ? `(${savedIds.length}/5)` : ""}
              </Pill>
            </>
          )}
          <button onClick={() => setShowHowItWorks(true)} style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.text, borderRadius:8, padding:"6px 14px", fontSize:12, cursor:"pointer", fontFamily:"'Space Mono',monospace", whiteSpace:"nowrap" }}>
            How It Works
          </button>
          <button onClick={() => setShowUpgradeChoice(true)}
            style={{ background:`linear-gradient(135deg,${C.accent},#c0392b)`, color:"#fff", border:"none", borderRadius:9, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Space Mono',monospace", whiteSpace:"nowrap", boxShadow:`0 0 18px ${C.accentGlow}` }}>
            Upgrade 💘
          </button>
          {user ? (
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <button onClick={() => showTrackerToast(searchesLeft === 999 ? "You have unlimited job matches — you're on Pro! 💘" : `You have ${searchesLeft} job match${searchesLeft === 1 ? "" : "es"} left this month. Upgrade for unlimited.`, "info", "ℹ️")} style={{ background:C.accentSoft, border:`1px solid ${C.accent}40`, borderRadius:8, padding:"5px 12px", fontSize:11, fontFamily:"'Space Mono',monospace", color:"#a01f38", whiteSpace:"nowrap", cursor:"pointer" }}>
                  {searchesLeft === 999 ? "∞ Pro" : `${searchesLeft} left`}
                </button>
                {searchesLeft < 5 && profile?.plan !== "pro" && profile?.plan !== "pro_annual" && (
                  <button onClick={resetSearchCount} title="Reset search count (testing)"
                    style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.muted, borderRadius:6, padding:"4px 8px", fontSize:10, cursor:"pointer", fontFamily:"'Space Mono',monospace" }}>
                    ↺
                  </button>
                )}
              </div>
              <button onClick={() => { setPrevScreen(screen); setScreen("tracker"); }} style={{ background:"none", border:`1px solid ${C.border}`, color:C.text, borderRadius:8, padding:"7px 14px", fontWeight:600, fontSize:13, cursor:"pointer", whiteSpace:"nowrap" }}>Job Tracker</button>
              <button onClick={() => setScreen("account")}
                title="Account Settings"
                style={{ background:C.surface, border:`1px solid ${C.border}`, color:C.sub, borderRadius:8, padding:"6px 12px", fontSize:11, cursor:"pointer", fontFamily:"'Space Mono',monospace", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:6 }}>
                {user.email?.split("@")[0]}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity:0.5 }}><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
              </button>
              <button onClick={() => handleLogout()}
                style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.muted, borderRadius:8, padding:"6px 12px", fontSize:11, cursor:"pointer", fontFamily:"'Space Mono',monospace", whiteSpace:"nowrap" }}>
                Sign out
              </button>
            </div>
          ) : (
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <button onClick={() => showTrackerToast(searchesLeft === 999 ? "You have unlimited job matches — you're on Pro! 💘" : `You have ${searchesLeft} job match${searchesLeft === 1 ? "" : "es"} left this month. Sign up free to get started.`, "info", "ℹ️")} style={{ background:C.accentSoft, border:`1px solid ${C.accent}40`, borderRadius:8, padding:"5px 12px", fontSize:11, fontFamily:"'Space Mono',monospace", color:"#a01f38", whiteSpace:"nowrap", cursor:"pointer" }}>
                {searchesLeft === 999 ? "∞ Pro" : `${searchesLeft} left`}
              </button>
              <button onClick={() => { setAuthMode("login"); setShowAuth(true); }}
                style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.text, borderRadius:8, padding:"6px 14px", fontSize:12, cursor:"pointer", fontFamily:"'Space Mono',monospace" }}>
                Log in
              </button>
              <button onClick={() => { setAuthMode("signup"); setShowAuth(true); }}
                style={{ background:C.text, color:"#fff", border:"none", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Space Mono',monospace" }}>
                Sign up
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Mobile nav drawer */}
      {isMobile && showMobileMenu && <div onClick={() => setShowMobileMenu(false)} style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:97, background:"transparent" }}/>}
      {isMobile && showMobileMenu && <div className="mobile-nav-drawer open" style={{ display:"flex", flexDirection:"column", gap:10, position:"fixed", top:60, left:0, right:0, background:"rgba(247,245,242,0.98)", backdropFilter:"blur(18px)", borderBottom:"1px solid #e5e2dd", padding:16, zIndex:99 }}>
        <button onClick={() => { setShowHowItWorks(true); setShowMobileMenu(false); }}
          style={{ background:"transparent", border:`1px solid #e5e2dd`, color:"#1a1a2e", borderRadius:8, padding:"10px 14px", fontSize:13, cursor:"pointer", fontFamily:"'Space Mono',monospace", textAlign:"left" }}>
          How It Works
        </button>
        <button onClick={() => { setShowUpgradeChoice(true); setShowMobileMenu(false); }}
          style={{ background:"linear-gradient(135deg,#e74c3c,#c0392b)", color:"#fff", border:"none", borderRadius:9, padding:"11px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Space Mono',monospace" }}>
          Upgrade 💘
        </button>
        {user ? (
          <>
            <button onClick={() => { setScreen("tracker"); setShowMobileMenu(false); }} style={{ background:"none", border:"none", color:C.text, fontWeight:600, fontSize:15, cursor:"pointer", padding:"8px 0", textAlign:"left" }}>Job Tracker</button>
              <button onClick={() => { setScreen("account"); setShowMobileMenu(false); }}
              style={{ background:"transparent", border:`1px solid #e5e2dd`, color:"#1a1a2e", borderRadius:8, padding:"10px 14px", fontSize:13, cursor:"pointer", fontFamily:"'Space Mono',monospace", textAlign:"left" }}>
              {user.email?.split("@")[0]} — Account
            </button>
            <button onClick={() => { handleLogout(); setShowMobileMenu(false); }}
              style={{ background:"transparent", border:`1px solid #e5e2dd`, color:"#888", borderRadius:8, padding:"10px 14px", fontSize:13, cursor:"pointer", fontFamily:"'Space Mono',monospace", textAlign:"left" }}>
              Sign out
            </button>
          </>
        ) : (
          <>
            <button onClick={() => { setAuthMode("login"); setShowAuth(true); setShowMobileMenu(false); }}
              style={{ background:"transparent", border:`1px solid #e5e2dd`, color:"#1a1a2e", borderRadius:8, padding:"10px 14px", fontSize:13, cursor:"pointer", fontFamily:"'Space Mono',monospace" }}>
              Log in
            </button>
            <button onClick={() => { setAuthMode("signup"); setShowAuth(true); setShowMobileMenu(false); }}
              style={{ background:"#1a1a2e", color:"#fff", border:"none", borderRadius:8, padding:"11px 14px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Space Mono',monospace" }}>
              Sign up
            </button>
          </>
        )}
      </div>}

      <main className="main-content" style={{ maxWidth:1280, margin:"0 auto" }}>

        {/* ── UPLOAD ── */}
        {screen === "upload" && (
          <div style={{ animation:"fadeUp .4s ease forwards", willChange:"transform, opacity" }}>

            {/* ── HERO ── */}
            <div style={{ textAlign:"center", paddingBottom:48 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:24, flexWrap:"wrap" }}>
                <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#1a1a2e", borderRadius:20, padding:"5px 14px", fontSize:11, fontFamily:"'Space Mono',monospace", color:"#d4d0e8", letterSpacing:"0.04em" }}>
                  <span style={{ animation:"heartbeat 1.8s ease-in-out infinite", display:"inline-block" }}>✦</span>
                  THE DATING APP FOR YOUR CAREER
                </div>
                <a href="https://www.anthropic.com" target="_blank" rel="noopener noreferrer"
                  style={{ display:"inline-flex", alignItems:"center", gap:5, background:"#fff", border:"0.5px solid #e0deda", borderRadius:20, padding:"5px 13px", textDecoration:"none" }}>
                  <span style={{ fontSize:11, fontFamily:"'Space Mono',monospace", color:"#555", fontWeight:500 }}>+ Powered by Claude AI</span>
                </a>
              </div>

              <h1 style={{ fontSize:"clamp(40px,6vw,72px)", fontWeight:800, lineHeight:1.07, letterSpacing:"-0.04em", margin:"0 0 20px", fontFamily:"'Playfair Display',serif", color:"#1a1a2e" }}>
                Get more interviews.<br/>
                <span style={{ color:C.accent }}>Stop getting ignored.</span>
              </h1>

              <p style={{ color:C.sub, fontSize:17, maxWidth:560, margin:"0 auto", lineHeight:1.7, fontWeight:400 }}>
                Upload your resume and we'll match you to real jobs, tailor it for each one using semantic matching — not just keywords — and show you exactly why you're getting filtered out.
              </p>
              <p style={{ color:"#999", fontSize:12, maxWidth:480, margin:"10px auto 0", lineHeight:1.6 }}>
                🔒 We never sell or train on your data. Your resume stays yours.
              </p>

              {/* Trust row — compact pills */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:12, margin:"26px 0 28px", flexWrap:"wrap" }}>
                <a href="https://www.trustpilot.com/review/getresumatch.com" target="_blank" rel="noopener noreferrer"
                  style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#fff", border:"0.5px solid #e0deda", borderRadius:9, padding:"8px 14px", textDecoration:"none" }}>
                  <span style={{ color:"#00b67a", fontSize:13, letterSpacing:1 }}>★★★★★</span>
                  <span style={{ fontSize:12, color:"#333", fontFamily:"'DM Sans',sans-serif" }}>
                    All <strong style={{ color:"#00b67a" }}>5-star</strong> reviews on Trustpilot
                  </span>
                </a>
                <div style={{ fontSize:12, color:"#555", background:"#fff", border:"0.5px solid #e0deda", borderRadius:9, padding:"8px 14px", fontFamily:"'DM Sans',sans-serif" }}>
                  <strong style={{ color:"#1a1a2e" }}>500+</strong> job seekers already using Get Resumatch
                </div>
              </div>
            </div>



            {/* Tab toggle */}
            <div style={{ display:"flex", gap:0, borderRadius:14, overflow:"hidden", border:`1.5px solid ${C.border}`, marginBottom:4, alignSelf:"center", width:"fit-content" }}>
              {[["upload","📄 Upload Resume"],["build","✨ Build My Resume"]].map(([tab, label]) => (
                <button key={tab} onClick={() => {
                  if (tab === "upload" && uploadTab === "upload") {
                    fileRef.current.click();
                  } else {
                    setUploadTab(tab);
                  }
                }} style={{ padding:"11px 28px", fontSize:15, fontWeight:600, border:"none", cursor:"pointer", fontFamily:"'DM Sans',sans-serif", transition:"all .2s", background: uploadTab === tab ? C.accent : "transparent", color: uploadTab === tab ? "#fff" : C.sub, borderRight: tab === "upload" ? `1.5px solid ${C.border}` : "none" }}>{label}</button>
              ))}
            </div>

            {/* Drop zone */}
            {uploadTab === "upload" && (
            <>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current.click()}
              style={{
                border:`2px dashed ${dragOver ? C.accent : fileName ? C.teal : C.border}`,
                borderRadius:24, padding:"72px 40px", textAlign:"center", cursor:"pointer",
                transition:"all .2s",
                background: dragOver ? C.accentSoft : fileName ? C.tealSoft : "#fafafa",
                boxShadow: dragOver ? `0 0 40px ${C.accentGlow}` : "none",
              }}>
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" style={{ display:"none" }} onChange={e => handleFile(e.target.files[0])}/>
              <div style={{ fontSize:52, marginBottom:18, display:"inline-block", animation: dragOver ? "heartbeat .6s ease infinite" : "none", pointerEvents:"none" }}>
                {fileName ? "✅" : "📄"}
              </div>
              {fileName ? (
                <><p style={{ fontSize:20, fontWeight:700, color:C.teal, margin:"0 0 6px", pointerEvents:"none" }}>{fileName}</p><p style={{ color:C.muted, fontSize:15, margin:"0 0 8px", pointerEvents:"none" }}>Click to change · Ready to find your match</p>{atsReadable && <span style={{ display:"inline-block", fontSize:15, fontWeight:600, color:C.teal, background:"#e6f7f5", borderRadius:8, padding:"4px 14px", pointerEvents:"none" }}>✓ ATS-readable format detected</span>}</>
              ) : (
                <><p style={{ fontSize:20, fontWeight:600, margin:"0 0 10px", pointerEvents:"none" }}>Drop your resume here</p><p style={{ color:C.muted, fontSize:15, margin:0, pointerEvents:"none" }}>PDF, DOC, DOCX · Max 5MB · Your data stays private</p></>
              )}
              {resumeLoadedFromCloud && fileName && fileName !== "AI-built-resume.txt" && (
                <div style={{ marginTop:10, display:"flex", alignItems:"center", gap:6, justifyContent:"center" }}>
                  <span style={{ fontSize:12, color:C.teal, fontFamily:"'Space Mono',monospace", background:"#e6f7f5", borderRadius:8, padding:"3px 10px" }}>☁️ Loaded from your last session</span>
                  <button onClick={(e) => { e.stopPropagation(); setResumeData(null); setResumeText(""); setFileName(null); setSelectedFile(null); setParsedOnUpload(null); setResumeLoadedFromCloud(false); setSavedResumeId(null); }} style={{ background:"transparent", border:"none", color:C.muted, fontSize:12, cursor:"pointer", textDecoration:"underline" }}>Upload new</button>
                </div>
              )}
            </div>
            {uploadError && (
              <div style={{ marginTop:12, padding:"12px 16px", background:"#fff5f5", border:"1.5px solid #fca5a5", borderRadius:12, color:"#b91c1c", fontSize:14, lineHeight:1.5 }}>
                ⚠️ {uploadError}
              </div>
            )}
            </>
            )}

            {/* Build My Resume tab */}
            {uploadTab === "build" && (
            <div style={{ background:"#fafafa", border:`1.5px solid ${C.border}`, borderRadius:24, padding:"36px 40px", display:"flex", flexDirection:"column", gap:18 }}>
              {!buildDraft ? (
                <>
                  <p style={{ margin:"0 0 4px", color:C.sub, fontSize:15, lineHeight:1.6 }}>Answer a few quick questions and we'll generate a clean, ATS-ready resume for you in seconds.</p>
                  {[
                    { key:"name", label:"Full Name *", placeholder:"e.g. Jane Smith" },
                    { key:"targetRole", label:"Target Role *", placeholder:"e.g. Software Engineer, Marketing Manager" },
                    { key:"recentTitle", label:"Most Recent Job Title *", placeholder:"e.g. Senior Associate" },
                    { key:"recentCompany", label:"Most Recent Company", placeholder:"e.g. Acme Corp" },
                    { key:"yearsExp", label:"Years of Experience", placeholder:"e.g. 5" },
                    { key:"skills", label:"Top Skills * (comma-separated)", placeholder:"e.g. Python, project management, data analysis" },
                    { key:"education", label:"Highest Education", placeholder:"e.g. BS Computer Science, State University" },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <label style={{ fontSize:13, fontWeight:600, color:C.sub, display:"block", marginBottom:6 }}>{label}</label>
                      <input
                        value={buildForm[key]}
                        onChange={e => setBuildForm(prev => ({ ...prev, [key]: e.target.value }))}
                        placeholder={placeholder}
                        style={{ width:"100%", boxSizing:"border-box", padding:"11px 14px", borderRadius:10, border:`1.5px solid ${C.border}`, fontSize:15, fontFamily:"'DM Sans',sans-serif", background:"#fff", color:C.text, outline:"none" }}
                      />
                    </div>
                  ))}
                  {buildError && <p style={{ color:C.accent, fontSize:14, margin:0 }}>{buildError}</p>}
                  <button onClick={handleBuildResume} disabled={buildLoading} style={{ marginTop:4, padding:"14px 32px", borderRadius:12, border:"none", background: buildLoading ? C.muted : C.accent, color:"#fff", fontSize:16, fontWeight:700, cursor: buildLoading ? "not-allowed" : "pointer", fontFamily:"'DM Sans',sans-serif", transition:"all .2s" }}>
                    {buildLoading ? "Building your resume…" : "✨ Build My Resume"}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
                    <span style={{ fontSize:22 }}>✅</span>
                    <p style={{ margin:0, fontSize:17, fontWeight:700, color:C.teal }}>Your resume is ready — review it below</p>
                  </div>
                  <textarea
                    value={builtResume}
                    onChange={e => setBuiltResume(e.target.value)}
                    rows={20}
                    style={{ width:"100%", boxSizing:"border-box", padding:"16px", borderRadius:12, border:`1.5px solid ${C.border}`, fontSize:13, fontFamily:"'Space Mono',monospace", lineHeight:1.7, background:"#fff", color:C.text, resize:"vertical", outline:"none" }}
                  />
                  <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                    <button onClick={handleUseBuiltResume} style={{ padding:"13px 28px", borderRadius:12, border:"none", background:C.accent, color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                      💘 Use This Resume — Find My Matches
                    </button>
                    <button onClick={() => { setBuildDraft(false); setBuiltResume(""); }} style={{ padding:"13px 28px", borderRadius:12, border:`1.5px solid ${C.border}`, background:"transparent", color:C.sub, fontSize:15, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                      ← Start Over
                    </button>
                  </div>
                </>
              )}
            </div>
            )}

            {/* Location input */}
            <div style={{ marginTop:20, display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
                <div style={{ flex:1, minWidth:200, position:"relative" }}>
                  <span style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", fontSize:16, pointerEvents:"none" }}>📍</span>
                  <input value={location} onChange={e => setLocation(e.target.value)}
                    placeholder="City or ZIP code (e.g. Austin, TX or 78701)"
                    style={{ width:"100%", boxSizing:"border-box", background:"#ffffff", border:`1.5px solid ${location ? C.accent+"60" : C.border}`, borderRadius:14, padding:"15px 16px 15px 44px", color:C.text, fontSize:16, outline:"none", fontFamily:"'DM Sans',sans-serif", transition:"border-color .2s" }}
                  />
                </div>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <button onClick={() => { setRemoteOnly(false); setExcludeRemote(false); setFairChance(false); }}
                    style={{ background: !remoteOnly && !excludeRemote ? C.accent : "transparent", color: !remoteOnly && !excludeRemote ? "#fff" : C.muted, border:`1px solid ${!remoteOnly && !excludeRemote ? C.accent : C.border}`, borderRadius:8, padding:"8px 14px", fontSize:13, cursor:"pointer", fontFamily:"'Space Mono',monospace", transition:"all .15s", whiteSpace:"nowrap" }}>
                    All jobs
                  </button>
                  <button onClick={() => { setRemoteOnly(true); setExcludeRemote(false); setFairChance(false); }}
                    style={{ background: remoteOnly ? C.accent : "transparent", color: remoteOnly ? "#fff" : C.muted, border:`1px solid ${remoteOnly ? C.accent : C.border}`, borderRadius:8, padding:"8px 14px", fontSize:13, cursor:"pointer", fontFamily:"'Space Mono',monospace", transition:"all .15s", whiteSpace:"nowrap" }}>
                    Remote only
                  </button>
                  <button onClick={() => { setRemoteOnly(false); setExcludeRemote(true); setFairChance(false); }}
                    style={{ background: excludeRemote ? C.accent : "transparent", color: excludeRemote ? "#fff" : C.muted, border:`1px solid ${excludeRemote ? C.accent : C.border}`, borderRadius:8, padding:"8px 14px", fontSize:13, cursor:"pointer", fontFamily:"'Space Mono',monospace", transition:"all .15s", whiteSpace:"nowrap" }}>
                    On-site only
                  </button>
                  <button onClick={() => { setFairChance(f => !f); }}
                    style={{ background: fairChance ? "#185FA5" : "transparent", color: fairChance ? "#fff" : C.muted, border:`1px solid ${fairChance ? "#185FA5" : C.border}`, borderRadius:8, padding:"8px 14px", fontSize:13, cursor:"pointer", fontFamily:"'Space Mono',monospace", transition:"all .15s", whiteSpace:"nowrap" }}>
                    ✓ Fair chance
                  </button>
                </div>
              </div>
              <div style={{ marginTop:12 }}>
                <input
                  type="text"
                  value={pivotTarget}
                  onChange={e => setPivotTarget(e.target.value)}
                  placeholder="e.g. Software Engineer, UX Design, Healthcare IT"
                  style={{ width:"100%", background:"transparent", border:`1px solid ${C.border}`, borderRadius:8, padding:"8px 12px", fontSize:15, color:C.text, fontFamily:"'Space Mono',monospace", boxSizing:"border-box", outline:"none" }}
                />
                <p style={{ margin:"4px 0 0", fontSize:13, color:C.text, opacity:0.7, fontFamily:"'Space Mono',monospace" }}>
                  Pivoting to a new career? Enter your target role or industry (optional)
                </p>
              </div>
              {!remoteOnly && location && (
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <p style={{ margin:0, fontSize:13, color:C.muted, fontFamily:"'Space Mono',monospace" }}>
                    📍 Searching within {searchRadius === 0 ? "nationwide" : `${searchRadius} miles`} of {location}{remoteOnly ? " · remote only" : excludeRemote ? "" : " + remote roles"}
                  </p>
                  <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                    {[25, 50, 100, 0].map(r => (
                      <button key={r} onClick={() => setSearchRadius(r)}
                        style={{ background: searchRadius === r ? C.accent : "transparent", color: searchRadius === r ? "#fff" : C.muted, border:`1px solid ${searchRadius === r ? C.accent : C.border}`, borderRadius:20, padding:"2px 10px", fontSize:11, cursor:"pointer", fontFamily:"'Space Mono',monospace", transition:"all .15s" }}>
                        {r === 0 ? "Nationwide" : `${r}mi`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {remoteOnly && (
                <p style={{ margin:0, fontSize:11, color:C.accent, fontFamily:"'Space Mono',monospace" }}>
                  🌐 Showing remote-only opportunities
                </p>
              )}
              {fairChance && (
                <p style={{ margin:0, fontSize:11, color:"#185FA5", fontFamily:"'Space Mono',monospace" }}>
                  ✓ Showing fair chance employer opportunities
                </p>
              )}
            </div>

            {true && (
              <div style={{ textAlign:"center", marginTop:22 }}>
                {excludeRemote && !location && (
                  <div style={{ margin:"0 0 14px", background:"rgba(214,54,81,0.06)", border:"1px solid rgba(214,54,81,0.2)", borderRadius:12, padding:"12px 16px", textAlign:"center" }}>
                    <p style={{ margin:"0 0 4px", fontSize:14, fontWeight:700, color:C.accent, fontFamily:"'DM Sans',sans-serif" }}>📍 Location required for in-person jobs</p>
                    <p style={{ margin:0, fontSize:13, color:C.sub, fontFamily:"'DM Sans',sans-serif", lineHeight:1.5 }}>You selected <strong>On-site only</strong>, which filters to in-person roles only. We need a city or ZIP code to know where to search.</p>
                  </div>
                )}
                <button onClick={() => { if (excludeRemote && !location) return; setSearchError(""); startSearch(); }} style={{ background:`linear-gradient(135deg,${C.accent},#c0392b)`, color:"#fff", border:"none", borderRadius:16, padding:"18px 64px", fontSize:18, fontWeight:700, cursor: excludeRemote && !location ? "not-allowed" : "pointer", opacity: excludeRemote && !location ? 0.5 : 1, boxShadow:`0 0 50px ${C.accentGlow}`, letterSpacing:"-0.01em", fontFamily:"'DM Sans',sans-serif" }}>
                  {user ? "💘 Find My Matches" : "💘 Find My Matches — Sign up Free"}
                </button>
                <p style={{ color:"#1f2937", fontSize:13, marginTop:12, fontFamily:"'DM Sans',sans-serif" }}>
                  {user ? `${searchesLeft === 999 ? "Unlimited" : searchesLeft} match${searchesLeft !== 1 ? "es" : ""} remaining` : "Free to start · No credit card required"}
                </p>
                {user && profile && !profile.referral_code && profile.plan !== "pro" && profile.plan !== "pro_annual" && (
                  <div onClick={() => setScreen("account")} style={{ marginTop:14, display:"inline-flex", alignItems:"center", gap:8, background:C.tealSoft, border:`1px solid ${C.teal}30`, borderRadius:10, padding:"8px 16px", cursor:"pointer", transition:"all .2s" }}>
                    <span style={{ fontSize:12, fontFamily:"'Space Mono',monospace", color:C.teal, fontWeight:600 }}>Give 5 searches, get 5 free &rarr;</span>
                  </div>
                )}
                {searchError && (
                  <div style={{ marginTop:14, background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:10, padding:"12px 18px", fontSize:13, color:"#dc2626", display:"inline-flex", alignItems:"center", gap:8 }}>
                    ⚠️ {searchError} — <span style={{ textDecoration:"underline", cursor:"pointer" }} onClick={() => { setSearchError(""); startSearch(); }}>Try again</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Why Resumatch — dark feature strip ── */}
            <div style={{ background:"#1a1a2e", borderRadius:24, padding:"40px 44px", marginTop:52, position:"relative", overflow:"hidden" }}>
              <div style={{ position:"absolute", bottom:-80, left:-80, width:300, height:300, borderRadius:"50%", background:"radial-gradient(circle,rgba(214,54,81,0.14) 0%,transparent 65%)", pointerEvents:"none" }}/>
              <div style={{ position:"absolute", top:-40, right:-40, width:200, height:200, borderRadius:"50%", background:"radial-gradient(circle,rgba(13,148,136,0.11) 0%,transparent 65%)", pointerEvents:"none" }}/>
              <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(214,54,81,0.15)", border:"1px solid rgba(214,54,81,0.3)", color:"#f87191", borderRadius:20, padding:"4px 14px", fontSize:11, fontFamily:"'Space Mono',monospace", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:18 }}>
                💘 Why Resumatch works
              </div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontWeight:800, fontSize:"clamp(22px,3vw,28px)", letterSpacing:"-0.03em", lineHeight:1.15, color:"#ffffff", margin:"0 0 10px" }}>
                Stop getting ghosted.<br/>
                Start getting <span style={{ color:"#f87191" }}>interviews.</span>
              </h2>
              <p style={{ color:"rgba(255,255,255,0.55)", fontSize:16, lineHeight:1.7, maxWidth:560, margin:"0 0 32px" }}>
                Your resume is being filtered out before a human ever sees it. We fix that — with live job matching, ATS optimization, and one-click tailoring built for the way hiring actually works.
              </p>
              <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
                {[
                  { icon:"🎯", color:"rgba(214,54,81,0.22)", title:"Semantic matching — not just keywords", body:"We don't just check if a word appears. We match your actual experience to what the role requires — so you get scored on fit, not just vocabulary." },
                  { icon:"🔍", color:"rgba(13,148,136,0.22)", title:"Live job matching from across the web", body:"Not a static database. Real listings searched in real-time, ranked by how well they fit your actual career history and skills." },
                  { icon:"⚡", color:"rgba(13,148,136,0.22)", title:"You review every change — no AI hallucinations", body:"Your tailored resume is shown to you before anything is finalized. We suggest, you approve. No fabricated metrics, no invented credentials." },
                  { icon:"🏠", color:"rgba(214,54,81,0.22)", title:"The only tool that finds jobs AND tailors your resume", body:"Most tools do one or the other. Get Resumatch finds real matching jobs, then tailors your resume for each one — all in a single platform." },
                ].map(b => (
                  <div key={b.title} style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
                    <div style={{ width:38, height:38, borderRadius:11, background:b.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, flexShrink:0, marginTop:1 }}>{b.icon}</div>
                    <div>
                      <div style={{ color:"#ffffff", fontSize:14, fontWeight:700, marginBottom:3 }}>{b.title}</div>
                      <div style={{ color:"rgba(255,255,255,0.55)", fontSize:15, lineHeight:1.65 }}>{b.body}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:30, paddingTop:24, borderTop:"1px solid rgba(255,255,255,0.08)", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
                <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} style={{ background:"linear-gradient(135deg,#d63651,#c0392b)", color:"#fff", border:"none", borderRadius:10, padding:"11px 24px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", boxShadow:"0 0 24px rgba(214,54,81,0.35)" }}>
                  💘 Find My Matches
                </button>
                <span style={{ fontSize:13, color:"rgba(255,255,255,0.85)", fontFamily:"'Space Mono',monospace" }}>Free to start · No credit card</span>
              </div>
            </div>

            {/* ── Testimonials ── */}
            <div style={{ marginTop:52 }}>
              <div style={{ textAlign:"center", marginBottom:32 }}>
                <span style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:"#9b1c35", letterSpacing:"0.1em", textTransform:"uppercase" }}>What people are saying</span>
                <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:800, fontSize:"clamp(22px,3vw,28px)", letterSpacing:"-0.03em", lineHeight:1.15, color:C.text, marginTop:10 }}>
                  Real feedback from real job seekers
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:20 }}>
                {[
                  { quote:"I was surprised to find job listings I hadn't seen anywhere else. Get Resumatch surfaced roles that other job sites completely missed.", name:"Rachel L." },
                  { quote:"Everything you need for a job search is right here in one place. I didn't have to jump between different tools — it's all here start to finish.", name:"Tarah D." },
                  { quote:"I went through the whole process right in the app — matched to the role, tailored my resume, and landed an interview with Cox Communications. I was honestly shocked at how fast it worked.", name:"Rivkah L.", title:"Job Seeker" },
                  { quote:"Love the design and the whole vibe of the tool. It looks great and actually works — not something you see very often.", name:"Chris C." },
                ].map((t, i) => (
                  <div key={i} style={{ background:"#ffffff", border:`1px solid ${C.border}`, borderRadius:20, padding:"28px 32px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)", display:"flex", flexDirection:"column", gap:16 }}>
                    <div style={{ fontSize:32, color:C.accent, lineHeight:1, fontFamily:"Georgia,serif" }}>"</div>
                    <div style={{ fontSize:15, color:C.sub, lineHeight:1.75, fontStyle:"italic", flex:1 }}>{t.quote}</div>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:8 }}>
                      <div style={{ width:36, height:36, borderRadius:"50%", background:`linear-gradient(135deg,${C.accent},#c0392b)`, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:14, flexShrink:0 }}>{t.name[0]}</div>
                      <div>
                        <div style={{ fontWeight:700, fontSize:14, color:C.text }}>{t.name}</div>
                        {t.title && <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{t.title}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* How it works — 3 steps */}
            <div style={{ marginTop:64 }}>
              <div style={{ textAlign:"center", marginBottom:32 }}>
                <span style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:"#9b1c35", letterSpacing:"0.1em", textTransform:"uppercase" }}>How it works</span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap:20 }}>
                {[
                  { icon:"📄", num:"01", title:"Share Your Resume", desc:"We read your full career story — no forms, no manual input. Just drop your file." },
                  { icon:"💘", num:"02", title:"We Find Your Matches", desc:"Our AI scans thousands of live roles and ranks them by how well they fit you." },
                  { icon:"✍️", num:"03", title:"Tailor & Apply", desc:"We rewrite your resume for each match, then send you straight to the application." },
                ].map(item => (
                  <div key={item.num} style={{ background:"#ffffff", border:`1px solid ${C.border}`, borderRadius:20, padding:"32px 28px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                      <span style={{ fontSize:30 }}>{item.icon}</span>
                      <span style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:C.accent, letterSpacing:"0.08em" }}>{item.num}</span>
                    </div>
                    <div style={{ fontWeight:700, fontSize:17, marginBottom:10, letterSpacing:"-0.01em" }}>{item.title}</div>
                    <div style={{ color:C.sub, fontSize:15, lineHeight:1.75 }}>{item.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Demo GIF */}
            <div style={{ marginTop:48, textAlign:"center" }}>
              <div style={{ marginBottom:16 }}>
                <span style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:"#9b1c35", letterSpacing:"0.1em", textTransform:"uppercase" }}>See it in action</span>
              </div>
              <video
                src="/resumatch-demo.mp4"
                autoPlay
                loop
                muted
                playsInline
                width={800}
                height={450}
                aria-label="Get Resumatch — upload your resume, get matched to jobs, tailor with one click"
                style={{ width:"100%", maxWidth:800, borderRadius:16, border:"1px solid #e5e2dd", boxShadow:"0 4px 24px rgba(0,0,0,0.08)" }}
              />
            </div>

            {/* ── Stats bar ── */}
            <div style={{ background:"#fff", borderTop:"0.5px solid #e8e6e2", borderBottom:"0.5px solid #e8e6e2", padding:"20px 2rem", margin:"0 -16px 64px" }}>
              <div style={{ maxWidth:700, margin:"0 auto", display:"flex", justifyContent:"center", gap:0 }}>
                {[
                  { num:"500+", label:"active users" },
                  { num:"5★", label:"Every Trustpilot review" },
                  { num:"Real jobs", label:"live listings, not a static DB" },
                  { num:"Free", label:"to start, no card needed" },
                ].map((s, i, arr) => (
                  <div key={s.num} style={{ flex:1, textAlign:"center", padding:"0 12px", borderRight: i < arr.length - 1 ? "0.5px solid #e8e6e2" : "none" }}>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, color:"#1a1a2e" }}>{s.num}</div>
                    <div style={{ fontSize:11, color:"#999", marginTop:2 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing cards */}
            <div style={{ marginTop:0, background:C.surface, border:`1px solid ${C.border}`, borderRadius:24, padding:"36px 40px" }}>
              <div style={{ textAlign:"center", marginBottom:36 }}>
                <div style={{ fontSize:11, fontWeight:600, color:C.accent, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:10 }}>Pricing</div>
                <h3 style={{ margin:"0 0 6px", fontSize:28, fontWeight:800, letterSpacing:"-0.02em", fontFamily:"'Playfair Display',serif", color:"#1a1a2e" }}>Simple, honest pricing</h3>
                <p style={{ fontSize:14, color:"#777", fontFamily:"'DM Sans',sans-serif", margin:"0 0 10px" }}>No hidden fees. No surprises. Cancel anytime.</p>
                <p style={{ fontSize:13, margin:"0", fontFamily:"'DM Sans',sans-serif" }}>
                  <span style={{ background:"#fff", border:"0.5px solid #d0cec9", borderRadius:5, padding:"3px 9px", color:"#1a1a2e", fontWeight:700, fontFamily:"'Space Mono',monospace", fontSize:12 }}>LAUNCH25</span>
                  <span style={{ color:C.muted, marginLeft:8 }}>— 25% off Pro Matcher your first month</span>
                </p>
              </div>

              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.08fr 1fr", gap:14, maxWidth:920, margin:"0 auto", alignItems:"start" }}>

                {/* Free card */}
                <div style={{ background:"#fff", border:"0.5px solid #e8e6e2", borderRadius:14, padding:"26px 22px" }}>
                  <div style={{ fontSize:15, fontWeight:600, color:"#1a1a2e", marginBottom:4 }}>Free</div>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontSize:34, fontWeight:700, color:"#1a1a2e", lineHeight:1, marginBottom:2 }}>$0</div>
                  <div style={{ fontSize:12, color:"#999", marginBottom:18 }}>forever free</div>
                  {["5 job matches / month","1 resume tailor / month","1 ATS score check","1 AI resume build","Job tracker"].map(f => (
                    <div key={f} style={{ display:"flex", alignItems:"flex-start", gap:8, fontSize:13, color:"#444", marginBottom:8, lineHeight:1.4 }}>
                      <span style={{ width:15, height:15, borderRadius:"50%", background:"#e8faf5", display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
                        <svg width="9" height="7" viewBox="0 0 9 7"><path d="M1 3.5L3.5 6L8 1" stroke="#0d9488" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>
                      </span>
                      {f}
                    </div>
                  ))}
                  <button onClick={e => { e.stopPropagation(); setShowAuth(true); }}
                    style={{ marginTop:18, width:"100%", background:"transparent", color:"#555", border:"0.5px solid #d0cec9", borderRadius:8, padding:"11px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                    Get started free →
                  </button>
                </div>

                {/* Match Pack — dark featured card */}
                <div style={{ background:"#1a1a2e", border:"2px solid #0d9488", borderRadius:14, padding:"26px 22px", cursor:"pointer" }}
                  onClick={() => setShowUpgradeChoice(true)}>
                  <div style={{ display:"inline-block", fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:20, marginBottom:12, background:"#0d9488", color:"#fff" }}>Pay once, try everything</div>
                  <div style={{ fontSize:15, fontWeight:600, color:"#e0dcea", marginBottom:4 }}>Match Pack</div>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontSize:34, fontWeight:700, color:"#fff", lineHeight:1, marginBottom:2 }}>$9.99</div>
                  <div style={{ fontSize:12, color:"#5a5a80", marginBottom:18 }}>one-time purchase</div>
                  <hr style={{ border:"none", borderTop:"0.5px solid #2a2a4a", margin:"0 0 16px" }}/>
                  {["5 job matches","5 resume tailors","5 AI resume builds","ATS score + Fix My Gaps","Resume Strength Score","Cover letter generator","Interview prep questions","AI Mock Interview","LinkedIn rewriter","PDF & Word downloads","Job tracker"].map(f => (
                    <div key={f} style={{ display:"flex", alignItems:"flex-start", gap:8, fontSize:13, color:"#8a86aa", marginBottom:8, lineHeight:1.4 }}>
                      <span style={{ width:15, height:15, borderRadius:"50%", background:"#0d9488", display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1, color:"#fff", fontSize:9, fontWeight:700 }}>✓</span>
                      {f}
                    </div>
                  ))}
                  <button style={{ marginTop:18, width:"100%", background:"#0d9488", color:"#fff", border:"none", borderRadius:8, padding:"11px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                    Get Match Pack →
                  </button>
                  <p style={{ fontSize:11, color:"#4a4a6a", textAlign:"center", marginTop:8, marginBottom:0 }}>Pay once, use anytime. No subscription.</p>
                </div>

                {/* Pro Matcher card */}
                <div style={{ background:"#fff", border:"0.5px solid #e8e6e2", borderRadius:14, padding:"26px 22px", position:"relative", cursor:"pointer" }}
                  onClick={() => setShowUpgradeChoice(true)}>
                  <div style={{ position:"absolute", top:-1, right:18, background:C.accent, color:"#fff", fontSize:10, fontWeight:700, padding:"4px 10px", borderRadius:"0 0 8px 8px", letterSpacing:"0.04em", textTransform:"uppercase" }}>MOST POPULAR</div>
                  <div style={{ display:"inline-block", fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:20, marginBottom:12, background:"#fde8ec", color:C.accent }}>Best value</div>
                  <div style={{ fontSize:15, fontWeight:600, color:"#1a1a2e", marginBottom:4 }}>Pro Matcher</div>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontSize:34, fontWeight:700, color:C.accent, lineHeight:1, marginBottom:2 }}>
                    $24.99<span style={{ fontSize:14, fontFamily:"'DM Sans',sans-serif", fontWeight:400, color:"#888" }}>/mo</span>
                  </div>
                  <div style={{ fontSize:12, color:"#999", marginBottom:18 }}>cancel anytime</div>
                  <hr style={{ border:"none", borderTop:"0.5px solid #e8e6e2", margin:"0 0 16px" }}/>
                  {[
                    { label:"Unlimited job matches", highlight:true },
                    { label:"Unlimited resume tailors", highlight:true },
                    { label:"Unlimited AI resume builds", highlight:true },
                    { label:"ATS score + Fix My Gaps", highlight:false },
                    { label:"Resume Strength Score", highlight:false },
                    { label:"Cover letter generator", highlight:false },
                    { label:"Interview prep questions", highlight:false },
                    { label:"AI Mock Interview", highlight:false },
                    { label:"LinkedIn rewriter", highlight:false },
                    { label:"PDF & Word downloads", highlight:false },
                    { label:"Job tracker", highlight:false },
                    { label:"Priority support", highlight:false },
                  ].map(f => (
                    <div key={f.label} style={{ display:"flex", alignItems:"flex-start", gap:8, fontSize:13, color:"#444", marginBottom:8, lineHeight:1.4 }}>
                      <span style={{ width:15, height:15, borderRadius:"50%", background:"#fde8ec", display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
                        <svg width="9" height="7" viewBox="0 0 9 7"><path d="M1 3.5L3.5 6L8 1" stroke="#d63651" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>
                      </span>
                      {f.highlight ? <span><strong style={{ color:C.accent }}>Unlimited</strong> {f.label.replace("Unlimited ","")}</span> : f.label}
                    </div>
                  ))}
                  <button style={{ marginTop:18, width:"100%", background:C.accent, color:"#fff", border:"none", borderRadius:8, padding:"11px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                    Start Pro Matcher →
                  </button>
                  <p style={{ fontSize:11, color:"#aaa", textAlign:"center", marginTop:8, marginBottom:0 }}>Weekly job match emails included</p>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* ── SEARCHING ── */}
        {screen === "searching" && (
          <div style={{ textAlign:"center", paddingTop:96, animation:"fadeUp .4s ease forwards", willChange:"transform, opacity" }}>
            <div style={{ fontSize:52, marginBottom:28, display:"inline-block", animation:"heartbeat 1.2s ease-in-out infinite" }}>💘</div>
            <h2 style={{ fontSize:24, fontWeight:800, letterSpacing:"-0.025em", margin:"0 0 10px", fontFamily:"'Playfair Display',serif" }}>Finding your matches…</h2>
            <p style={{ color:C.muted, fontSize:13, fontFamily:"'Space Mono',monospace", marginBottom:40, animation:"pulse 1.6s ease-in-out infinite" }}>{progressMsg}</p>
            <div style={{ background:"#e5e7eb", borderRadius:100, height:5, width:340, margin:"0 auto 14px", overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${progress}%`, background:`linear-gradient(90deg,${C.accent},#e8735a)`, borderRadius:100, transition:"width .1s linear" }}/>
            </div>
            <p style={{ color:C.muted, fontSize:11, fontFamily:"'Space Mono',monospace" }}>{progress}% complete</p>
            <p style={{ color:C.sub, fontSize:16, marginTop:28, fontStyle:"italic", fontFamily:"'Playfair Display',serif", maxWidth:420, margin:"28px auto 0", lineHeight:1.6, opacity: quoteFade ? 1 : 0, transition:"opacity 0.4s ease" }}>"{loadingQuotes[quoteIndex]}"</p>
          </div>
        )}

        {/* ── RESULTS ── */}
        {screen === "results" && (
          <ResultsScreen
            activeJobs={activeJobs} displayedJobs={displayedJobs} activeTab={activeTab} savedIds={savedIds}
            location={location} remoteOnly={remoteOnly} excludeRemote={excludeRemote} searchRadius={searchRadius} setSearchRadius={setSearchRadius} startSearchWithRadius={startSearchWithRadius}
            perPage={perPage} setPerPage={setPerPage} setScreen={setScreen}
            showRefine={showRefine} refining={refining} refinedNote={refinedNote} refineHistory={refineHistory} refineInput={refineInput} setRefineInput={setRefineInput} refineRef={refineRef} handleRefine={handleRefine}
            expandedJobs={expandedJobs} setExpandedJobs={setExpandedJobs} jobDetails={jobDetails} loadJobDetails={loadJobDetails}
            toggleSave={toggleSave} openRewrite={openRewrite}
            pivotTarget={pivotTarget}
            user={user} setShowAuth={setShowAuth} setAuthMode={setAuthMode} showAuth={showAuth}
            addToTracker={addToTracker} trackerToast={trackerToast} dismissTrackerToast={dismissTrackerToast} onTailorNudge={handleTailorNudge}
            freeAtsUsed={freeAtsUsed} onJobAtsCheck={handleJobAtsCheck} jobAtsResults={jobAtsResults} jobAtsLoading={jobAtsLoading}
            isPaidUser={!!(isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack")}
            setShowAuth={setShowAuth} setAuthMode={setAuthMode} setUpgrade={setUpgrade}
          />
        )}

                {/* ── REWRITE ── */}
        {screen === "rewrite" && selectedJob && (
          <div style={{ animation:"fadeUp .35s ease forwards", willChange:"transform, opacity" }}>
            <button onClick={() => setScreen("results")}
              style={{ background:"#fff", border:"0.5px solid #e0deda", color:"#555", borderRadius:8, padding:"6px 14px", fontSize:12, cursor:"pointer", fontFamily:"'Space Mono',monospace", marginBottom:16, display:"inline-flex", alignItems:"center", gap:5 }}>
              ← Back to Matches
            </button>
            <div style={{ background:"#fff", border:"0.5px solid #e8e6e2", borderRadius:14, padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12, marginBottom:16 }}>
              <div>
                <div style={{ fontWeight:700, marginBottom:3, fontSize:15, color:"#1a1a2e", fontFamily:"Georgia,serif" }}>{selectedJob.title} · {selectedJob.company}</div>
                <div style={{ fontSize:13, color:"#666", display:"flex", alignItems:"center", gap:6 }}>
                  {selectedJob.location}
                  <span style={{ color:"#ccc" }}>·</span>
                  <span style={{ fontSize:11, color: selectedJob.salary && selectedJob.salary !== "Not listed" ? "#0d9488" : "#999", background: selectedJob.salary && selectedJob.salary !== "Not listed" ? "#e8faf5" : "#f4f2ef", borderRadius:5, padding:"2px 7px" }}>{selectedJob.salary || "Salary not listed"}</span>
                </div>
              </div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <CompatBadge score={selectedJob.match}/>
                <button onClick={() => window.open(selectedJob.url,"_blank")}
                  style={{ background:"#d63651", color:"#fff", border:"none", borderRadius:8, padding:"8px 18px", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                  Apply Now →
                </button>
              </div>
            </div>
            {!rewriteDone && (() => {
              const _tailorsUsed = profile?.tailors_used ?? 0;
              const _isFree = !isSuperUser && (!profile || (profile?.plan !== "pro" && profile?.plan !== "pro_annual" && profile?.plan !== "pack"));
              const _isPack = !isSuperUser && profile?.plan === "pack";
              const _isPro = isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual";
              if (_isPro) return (
                <div style={{ background:C.tealSoft, border:`1px solid ${C.teal}30`, borderRadius:10, padding:"10px 16px", marginBottom:16, fontSize:12, color:C.teal, fontFamily:"'Space Mono',monospace", display:"flex", alignItems:"center", gap:8 }}>
                  💘 Pro member — unlimited resume tailoring. You're all set!
                </div>
              );
              const _tailorsLeft = _isFree ? Math.max(0, 1 - _tailorsUsed) : _isPack ? Math.max(0, 3 - _tailorsUsed) : null;
              if (_tailorsLeft === null) return null;
              return (
                <div style={{ background:"rgba(245,166,35,0.08)", border:`1px solid rgba(245,166,35,0.3)`, borderRadius:10, padding:"10px 16px", marginBottom:16, fontSize:12, color:C.teal, fontFamily:"'Space Mono',monospace", display:"flex", alignItems:"center", gap:8 }}>
                  ⚠️ You have {_tailorsLeft} resume tailor{_tailorsLeft !== 1 ? "s" : ""} remaining. <span style={{ color:C.sub }}>{_isPack ? "Upgrade to Pro for unlimited tailoring — every job deserves a tailored resume." : "Upgrade for unlimited tailoring — every job deserves a tailored resume."}</span>
                </div>
              );
            })()}
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:16 }}>
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                  <span style={{ fontSize:11, fontFamily:"'Space Mono',monospace", color:C.muted, textTransform:"uppercase", letterSpacing:"0.07em" }}>{beforeFixResume ? "Your Tailored Resume" : "Your Current Resume"}</span>
                  {beforeFixResume && <span style={{ fontSize:10, fontFamily:"'Space Mono',monospace", color:C.muted, background:"rgba(255,255,255,0.05)", border:`1px solid ${C.border}`, borderRadius:4, padding:"1px 6px" }}>Before</span>}
                </div>
                <div style={{ background:"#fafafa", border:`1px solid ${C.border}`, borderRadius:14, padding:"28px 26px", height:420, overflowY:"auto" }}>
                  {beforeFixResume ? (
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, lineHeight:1.7, color:"#1a4a3a", whiteSpace:"pre-wrap", wordBreak:"break-word" }}>
                      <div style={{ fontSize:11, color:C.muted, fontFamily:"'Space Mono',monospace", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.06em" }}>Your Tailored Resume</div>
                      {beforeFixResume}
                    </div>
                  ) : resumeData ? (
                    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
                      <div>
                        <div style={{ fontWeight:800, fontSize:18, fontFamily:"'Playfair Display',serif", letterSpacing:"-0.02em", marginBottom:4 }}>{resumeData.name || "Your Resume"}</div>
                        <div style={{ fontSize:12, color:C.muted, fontFamily:"'Space Mono',monospace", lineHeight:1.8 }}>
                          {resumeData.email && <div>✉ {resumeData.email}</div>}
                          {resumeData.phone && <div>📞 {resumeData.phone}</div>}
                          {resumeData.location && <div>📍 {resumeData.location}</div>}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:C.accent, fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>Current Role</div>
                        <div style={{ fontWeight:700, fontSize:14 }}>{resumeData.current_title || resumeData.title}</div>
                        {resumeData.experience_years && <div style={{ fontSize:13, color:C.sub, marginTop:2 }}>{resumeData.experience_years} years experience</div>}
                      </div>
                      {resumeData.skills?.length > 0 && (
                        <div>
                          <div style={{ fontSize:11, color:C.accent, fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>Key Skills</div>
                          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                            {resumeData.skills.slice(0,12).map(s => (
                              <span key={s} style={{ background:C.accentSoft, border:`1px solid ${C.accent}30`, color:C.accent, borderRadius:6, padding:"3px 10px", fontSize:12, fontFamily:"'Space Mono',monospace" }}>{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {resumeData.industries?.length > 0 && (
                        <div>
                          <div style={{ fontSize:11, color:C.teal, fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>Industries</div>
                          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                            {resumeData.industries.map(ind => (
                              <span key={ind} style={{ background:C.tealSoft, border:`1px solid ${C.teal}30`, color:C.teal, borderRadius:6, padding:"3px 10px", fontSize:12, fontFamily:"'Space Mono',monospace" }}>{ind}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:C.muted, fontSize:13 }}>No resume data</div>
                  )}
                </div>
              </div>
              <div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                    <span style={{ fontSize:11, fontFamily:"'Space Mono',monospace", color:C.muted, textTransform:"uppercase", letterSpacing:"0.07em" }}>{beforeFixResume ? "Your Updated Resume" : `Tailored for ${selectedJob.company}`}</span>
                    {beforeFixResume && <span style={{ fontSize:10, fontFamily:"'Space Mono',monospace", color:C.teal, background:C.tealSoft, border:`1px solid ${C.teal}40`, borderRadius:4, padding:"1px 6px" }}>After</span>}
                    {rewriteDone && !beforeFixResume && <span style={{ fontSize:11, color:C.teal, fontFamily:"'Space Mono',monospace" }}>✓ Optimized</span>}
                  </div>
                  {beforeFixResume && lastFixTool && (
                    <span style={{ fontSize:12, fontWeight:700, color: C.teal, fontFamily:"'Space Mono',monospace", letterSpacing:"0.04em" }}>
                      ✓ Updated by {lastFixTool}
                    </span>
                  )}
                </div>
                <div style={{ background: rewriteDone ? "rgba(34,211,184,0.04)" : C.surface, border:`1px solid ${resumeFlash ? C.teal : (fixingGaps || strengtheningResume) ? C.teal+"80" : rewriteDone ? C.teal+"50" : C.border}`, borderRadius:14, padding:"18px 16px", fontFamily:"'DM Sans',sans-serif", fontSize:13, lineHeight:1.7, color:"#1a4a3a", height: isMobile ? "auto" : 420, minHeight: isMobile ? 200 : "auto", overflowY:"auto", whiteSpace:"pre-wrap", wordBreak:"break-word", display:"flex", alignItems: rewriteDone ? "flex-start" : "center", justifyContent: rewriteDone ? "flex-start" : "center", transition:"all .3s ease", boxShadow: resumeFlash ? `0 0 0 3px ${C.teal}30` : "none" }}>
                  {(fixingGaps || strengtheningResume) && (
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12, width:"100%", padding:"32px 0" }}>
                      <div style={{ width:36, height:36, border:`3px solid ${C.teal}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
                      <div style={{ fontSize:12, fontWeight:700, color:C.text, fontFamily:"'DM Sans',sans-serif" }}>
                        {fixingGaps ? "Updating your resume with missing keywords…" : "Rewriting your resume for human impact…"}
                      </div>
                      <div style={{ fontSize:11, color:C.muted, fontFamily:"'Space Mono',monospace", textAlign:"center", maxWidth:220, lineHeight:1.6 }}>
                        Your tailored resume is being updated
                      </div>
                    </div>
                  )}
                  {!rewriteDone && !rewriting && (
                    <div style={{ textAlign:"center", color:C.muted }}>
                      <div style={{ fontSize:30, marginBottom:12, opacity:.35 }}>✍️</div>
                      <div style={{ fontSize:12, lineHeight:1.6 }}>Click below to tailor<br/>this resume for {selectedJob.company}</div>
                    </div>
                  )}
                  {rewriting && (
                    <div style={{ textAlign:"center", color:C.accent }}>
                      <div style={{ fontSize:32, marginBottom:14, animation:"heartbeat 1s ease-in-out infinite", display:"inline-block" }}>💘</div>
                      <div style={{ fontSize:12, lineHeight:1.6 }}>Tailoring your resume<br/>for {selectedJob.company}…</div>
                    </div>
                  )}
                  {rewriteDone && !fixingGaps && !strengtheningResume && (() => {
                    const diffKey = (tailoredResume || "").slice(-20);
                    const originalLines = (beforeFixResume || resumeText || "").split("\n");
                    const tailoredLines = (tailoredResume || "").split("\n");
                    const originalSet = new Set(originalLines.map(l => l.trim().toLowerCase()));
                    return (
                      <div key={diffKey} style={{ width:"100%" }}>
                        {tailoredLines.map((line, i) => {
                          const trimmed = line.trim();
                          const isNew = trimmed.length > 0 && !originalSet.has(trimmed.toLowerCase());
                          const hasKeyword = isNew && [...addedKeywords].some(kw => trimmed.toLowerCase().includes(kw.toLowerCase()));
                          return (
                            <div key={i} style={{
                              background: hasKeyword ? "rgba(245,158,11,0.12)" : isNew ? "rgba(13,148,136,0.10)" : "transparent",
                              borderLeft: hasKeyword ? "3px solid #f59e0b" : isNew ? `3px solid ${C.teal}` : "3px solid transparent",
                              paddingLeft: 8,
                              marginBottom: 1,
                              borderRadius: (isNew || hasKeyword) ? "0 4px 4px 0" : 0,
                              transition:"background .2s"
                            }}>
                              {line || " "}
                            </div>
                          );
                        })}
                        <div style={{ marginTop:16, background:C.tealSoft, border:`1px solid ${C.teal}30`, borderRadius:10, padding:"10px 14px", display:"flex", alignItems:"flex-start", gap:10 }}>
                          <div style={{ width:10, height:10, borderRadius:2, background:"rgba(13,148,136,0.5)", border:`1px solid ${C.teal}`, flexShrink:0, marginTop:2 }}/>
                          <div>
                            <div style={{ fontSize:12, fontWeight:700, color:C.teal, marginBottom:2 }}>
                              {lastFixTool ? `Updated by ${lastFixTool}` : "AI Tailored Resume"}
                            </div>
                            <div style={{ fontSize:11, color:C.muted, fontFamily:"'Space Mono',monospace", lineHeight:1.6 }}>
                              Highlighted lines show exactly what was changed or added. Compare with your previous version on the left.
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                {rewriteDone && (
                  <div style={{ display:"flex", justifyContent:"flex-start", marginTop:10, gap:8, flexWrap:"wrap" }}>
                    <button onClick={() => copyResume(tailoredResume)}
                      style={{ background: resumeCopied ? C.tealSoft : "transparent", border:`1.5px solid ${C.border}`, color:C.muted, borderRadius:9, padding:"8px 16px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", gap:6 }}>
                      {resumeCopied ? "✓ Copied!" : "Copy Resume"}
                    </button>
                    <button onClick={handlePDFDownload}
                      style={{ background:C.teal, color:"#fff", border:"none", borderRadius:9, padding:"8px 16px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                      Download PDF
                    </button>
                    <button onClick={handleDocxDownload}
                      style={{ background:"#1e40af", color:"#fff", border:"none", borderRadius:9, padding:"8px 16px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                      Download Word
                    </button>
                    {profile && (
                      <button onClick={() => { setSaveVersionLabel(selectedJob ? `${selectedJob.title} — ${selectedJob.company}` : ""); setShowSaveModal(true); }}
                        style={{ background:"transparent", border:`1.5px solid ${C.border}`, color:C.muted, borderRadius:9, padding:"8px 16px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", gap:6 }}>
                        🔖 Save Version
                      </button>
                    )}
                    {profile && selectedJob && (
                      <button onClick={async () => {
                        try {
                          const res = await fetch(`${process.env.REACT_APP_API_URL}/add-job`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ user_id: profile.id, job_title: selectedJob.title, company: selectedJob.company, url: selectedJob.url || "", status: "Saved" }),
                          });
                          const data = await res.json();
                          if (data.success) showResumeToast("Job saved to tracker");
                        } catch (e) { console.error(e); }
                      }}
                        style={{ background:"transparent", border:`1.5px solid ${C.teal}`, color:C.teal, borderRadius:9, padding:"8px 16px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", gap:6 }}>
                        📌 Save to Job Tracker
                      </button>
                    )}
                    {profile && (
                      <button onClick={() => { setShowVersionsPanel(v => !v); handleLoadVersions(); }}
                        style={{ background:"transparent", border:`1.5px solid ${C.border}`, color:C.muted, borderRadius:9, padding:"8px 16px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", gap:6 }}>
                        📂 My Versions
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            {tailorError && (
              <div style={{ background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:10, padding:"12px 16px", marginTop:14, fontSize:13, color:"#dc2626", display:"flex", alignItems:"center", gap:8 }}>
                ⚠️ {tailorError}
                <button onClick={doRewrite} style={{ marginLeft:"auto", background:"transparent", border:"1px solid #fca5a5", color:"#dc2626", borderRadius:6, padding:"4px 10px", fontSize:12, cursor:"pointer" }}>Try again</button>
              </div>
            )}

            {/* ── ATS Score Panel ── */}
            {rewriteDone && (
              <div id="cover-letter-panel" style={{ marginTop:20, border:`1px solid ${C.border}`, borderRadius:18, overflow:"hidden", animation:"fadeUp .3s ease forwards" }}>
                <div style={{ background:"#f9fafb", borderBottom:`1px solid ${C.border}`, padding:"14px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:9, background:C.tealSoft, border:`1px solid ${C.teal}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontFamily:"'Space Mono',monospace", fontWeight:700, color:C.teal }}>AS</div>
                    <div>
                      <div style={{ fontWeight:800, fontSize:16, color:C.text }}>ATS Score Checker</div>
                      <div style={{ fontSize:12, color:C.sub, fontFamily:"'Space Mono',monospace" }}>Will a robot let your resume through? Checks keywords & formatting.</div>
                    </div>
                  </div>
                  {!atsScore && !atsLoading && (
                    <button onClick={runAtsScore}
                      style={{ background:"linear-gradient(135deg,#0d9488,#0d9488)", color:"#fff", border:"none", borderRadius:9, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", boxShadow:"0 0 18px rgba(99,102,241,0.25)", whiteSpace:"nowrap" }}>
                      Run ATS Check →
                    </button>
                  )}
                </div>
                {atsLoading && (
                  <div style={{ padding:"32px", textAlign:"center" }}>
                    <div style={{ fontSize:13, color:C.muted, fontFamily:"'Space Mono',monospace", animation:"pulse 1.2s ease-in-out infinite" }}>Analyzing resume against ATS filters…</div>
                  </div>
                )}
                {atsError && atsError !== "__no_improvement__" && !atsLoading && (
                  <div style={{ padding:"16px 22px", fontSize:13, color:"#dc2626" }}>{atsError}</div>
                )}
                {atsScore && !atsLoading && (
                  <div style={{ padding:"22px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:20, marginBottom:20, flexWrap:"wrap" }}>
                      <div style={{ position:"relative", width:90, height:90, flexShrink:0 }}>
                        <svg width="90" height="90" viewBox="0 0 90 90">
                          <circle cx="45" cy="45" r="36" fill="none" stroke="#e5e7eb" strokeWidth="8"/>
                          <circle cx="45" cy="45" r="36" fill="none"
                            stroke={atsScore.score >= 80 ? C.teal : atsScore.score >= 70 ? C.teal : C.teal}
                            strokeWidth="8" strokeLinecap="round"
                            strokeDasharray={`${(atsScore.score / 100) * 226} 226`}
                            strokeDashoffset="56.5"
                            style={{ transition:"stroke-dasharray 1s ease" }}/>
                        </svg>
                        <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                          <span style={{ fontFamily:"'Space Mono',monospace", fontWeight:700, fontSize:18, color: atsScore.score >= 80 ? C.teal : atsScore.score >= 70 ? C.teal : C.teal, lineHeight:1 }}>{atsScore.score}</span>
                          <span style={{ fontFamily:"'Space Mono',monospace", fontSize:9, color:C.muted, marginTop:1 }}>/100</span>
                        </div>
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
                          <span style={{ fontFamily:"'Space Mono',monospace", fontWeight:700, fontSize:22, color: atsScore.score >= 80 ? C.teal : atsScore.score >= 70 ? C.teal : C.teal }}>Grade {atsScore.grade}</span>
                          <span style={{ fontSize:11, fontFamily:"'Space Mono',monospace", background: atsScore.score >= 80 ? C.tealSoft : atsScore.score >= 70 ? C.tealSoft : C.tealSoft, color: atsScore.score >= 80 ? C.teal : atsScore.score >= 70 ? C.teal : C.teal, borderRadius:6, padding:"2px 8px" }}>
                            {atsScore.score >= 90 ? "ATS Ready" : atsScore.score >= 75 ? "Good Standing" : "Needs Improvement"}
                          </span>
                        </div>
                        <p style={{ color:C.sub, fontSize:13, margin:0, lineHeight:1.6 }}>{atsScore?.summary}</p>
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
                      <div style={{ background:C.tealSoft, border:`1px solid ${C.teal}30`, borderRadius:12, padding:"14px 16px" }}>
                        <div style={{ fontSize:11, color:C.teal, fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:700, marginBottom:10 }}>✓ Keywords Matched</div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                          {(atsScore.keywords_found || []).map((kw, i) => (
                            <span key={i} style={{ background:"rgba(255,255,255,0.7)", border:`1px solid ${C.teal}40`, color:C.teal, borderRadius:5, padding:"2px 8px", fontSize:11, fontFamily:"'Space Mono',monospace" }}>{kw}</span>
                          ))}
                        </div>
                      </div>
                      <div style={{ background:"rgba(220,38,38,0.05)", border:"1px solid rgba(220,38,38,0.15)", borderRadius:12, padding:"14px 16px" }}>
                        <div style={{ fontSize:11, color:"#dc2626", fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:700, marginBottom:10 }}>✗ Keywords Missing</div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                          {(atsScore.keywords_missing || []).map((kw, i) => {
                            const isAdded = addedKeywords.has(kw);
                            return (
                              <span key={i}
                                onClick={() => !isAdded && setKeywordPrompt(kw)}
                                style={{
                                  background: isAdded ? "rgba(13,148,136,0.08)" : "rgba(255,255,255,0.7)",
                                  border: isAdded ? "1px solid rgba(13,148,136,0.3)" : "1px solid rgba(220,38,38,0.25)",
                                  color: isAdded ? C.teal : "#dc2626",
                                  borderRadius:5, padding:"2px 8px", fontSize:11,
                                  fontFamily:"'Space Mono',monospace",
                                  cursor: isAdded ? "default" : "pointer",
                                  transition:"all .2s",
                                  opacity: isAdded ? 0.7 : 1
                                }}>
                                {isAdded ? `✓ ${kw}` : kw}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    {atsScore.formatting_notes?.length > 0 && (
                      <div style={{ background:C.tealSoft, border:`1px solid ${C.teal}30`, borderRadius:12, padding:"14px 16px", marginBottom:14 }}>
                        <div style={{ fontSize:11, color:C.teal, fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:700, marginBottom:8 }}>📋 Formatting Notes</div>
                        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                          {atsScore.formatting_notes?.map((note, i) => (
                            <div key={i} style={{ fontSize:13, color:C.sub, display:"flex", gap:8, lineHeight:1.5 }}>
                              <span style={{ color:C.teal, flexShrink:0 }}>→</span>{note}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(atsScore.improvements?.length > 0 || atsScore?.top_tip) && (
                      <div style={{ background:"linear-gradient(135deg,rgba(99,102,241,0.06),rgba(99,102,241,0.02))", border:"1px solid rgba(99,102,241,0.2)", borderRadius:12, padding:"14px 16px" }}>
                        <div style={{ fontSize:11, color:"#0d9488", fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:700, marginBottom:8 }}>TOP IMPROVEMENTS</div>
                        {atsScore.improvements?.length > 0 ? (
                          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                            {atsScore.improvements?.map((imp, i) => (
                              <div key={i} style={{ fontSize:13, color:C.sub, display:"flex", gap:8, lineHeight:1.5 }}>
                                <span style={{ color:"#0d9488", flexShrink:0, fontWeight:700 }}>{i+1}.</span>{imp}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize:13, color:C.sub, display:"flex", gap:8, lineHeight:1.5 }}>
                            <span style={{ color:"#0d9488", flexShrink:0, fontWeight:700 }}>1.</span>{atsScore?.top_tip}
                          </div>
                        )}
                      </div>
                    )}
                    {achievementFeedback?.length > 0 && (
                      <div style={{ background:"linear-gradient(135deg,rgba(214,54,81,0.05),rgba(214,54,81,0.02))", border:`1px solid ${C.accent}25`, borderRadius:12, padding:"14px 16px", marginTop:14 }}>
                        <div style={{ fontSize:11, color:C.accent, fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:700, marginBottom:10 }}>🎯 Achievement Framing</div>
                        <p style={{ fontSize:12, color:C.muted, margin:"0 0 12px", lineHeight:1.5 }}>These bullets describe duties instead of impact. Consider rewording to show results:</p>
                        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                          {achievementFeedback.map((item, i) => (
                            <div key={i} style={{ background:"#fff", borderRadius:10, padding:"12px 14px", border:`1px solid ${C.accent}15` }}>
                              <div style={{ fontSize:12, color:"#b91c1c", marginBottom:6, lineHeight:1.5 }}>
                                <span style={{ fontWeight:700, marginRight:6 }}>Before:</span>{item.original}
                              </div>
                              <div style={{ fontSize:12, color:C.teal, lineHeight:1.5 }}>
                                <span style={{ fontWeight:700, marginRight:6 }}>After:</span>{item.suggestion}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {atsScore && freeAtsUsed && !(isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack") && (
                      <div style={{ marginTop:16, background:"linear-gradient(135deg,rgba(99,102,241,0.07),rgba(99,102,241,0.02))", border:"1px solid rgba(99,102,241,0.2)", borderRadius:12, padding:"16px 18px", textAlign:"center" }}>
                        <div style={{ fontWeight:700, fontSize:13, color:"#0d9488", marginBottom:6 }}>You've used your free ATS check</div>
                        <p style={{ color:C.sub, fontSize:12, margin:"0 0 12px", lineHeight:1.6 }}>Most resumes get rejected before a human sees them. Upgrade to fix your ATS score and get your resume in front of real people.</p>
                        <button onClick={() => setShowUpgradeChoice(true)}
                          style={{ background:"linear-gradient(135deg,#0d9488,#0d9488)", color:"#fff", border:"none", borderRadius:9, padding:"9px 20px", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                          Unlock Unlimited ATS →
                        </button>
                      </div>
                    )}
                    {(atsPeaked || atsScore.grade === "A") && atsScore.score >= 75 ? (
                      <div style={{ marginTop:16, borderRadius:14, overflow:"hidden", border:`1px solid ${C.teal}30`, animation:"fadeUp .35s ease forwards", willChange:"transform, opacity" }}>
                        <div style={{ background:`linear-gradient(135deg,${C.tealSoft},rgba(13,148,136,0.08))`, padding:"14px 18px", display:"flex", alignItems:"center", gap:10, borderBottom:`1px solid ${C.teal}20` }}>
                          <div style={{ width:8, height:8, borderRadius:"50%", background:C.teal, flexShrink:0 }}/>
                          <div style={{ fontSize:15, fontWeight:800, color:C.text }}>You're ready to apply</div>
                        </div>
                        <div style={{ padding:"14px 18px", background:"#fff" }}>
                          <div style={{ fontSize:13, color:C.sub, marginBottom:14, lineHeight:1.5 }}>
                            {atsScore.grade === "A"
                              ? "Your resume is fully optimized for ATS filters. All key terms are covered."
                              : atsScore.score >= 75
                              ? `An ${atsScore.grade} (${atsScore.score}/100) is a strong ATS score — you're through the filters. Further AI tweaks won't move the needle from here.`
                              : `Your resume is as strong as we can make it for this role — consider applying and letting your cover letter bridge the gap.`}
                          </div>
                          <div style={{ display:"flex", gap:20, marginBottom:16 }}>
                            {[["ATS SCORE", `${atsScore.score}/100`, C.teal], ["GRADE", atsScore.grade, atsScore.grade === "A" || atsScore.grade === "B" ? C.teal : atsScore.grade === "C" ? C.teal : C.accent], ["KEYWORDS", `${atsScore.keywords_found?.length || 0} matched`, C.teal]].map(([label, val, color]) => (
                              <div key={label}>
                                <div style={{ fontSize:10, fontFamily:"'Space Mono',monospace", color:C.muted, letterSpacing:1, textTransform:"uppercase", marginBottom:3 }}>{label}</div>
                                <div style={{ fontSize:18, fontWeight:800, color }}>{val}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : atsScore.keywords_missing?.length > 0 ? (
                      <div style={{ marginTop:16, borderRadius:12, overflow:"hidden", border:`1px solid ${C.accent}20` }}>
                        {atsError === "__no_improvement__" && (
                          <div style={{ background:C.tealSoft, borderBottom:`1px solid ${C.teal}20`, padding:"10px 16px", display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ width:7, height:7, borderRadius:"50%", background:C.teal, flexShrink:0 }}/>
                            <div style={{ fontSize:12, color:C.text, fontFamily:"'Space Mono',monospace" }}>
                              This fix didn't improve the score — your best version is still showing.
                            </div>
                          </div>
                        )}
                        {(freeAtsUsed && !["levy1620@gmail.com","tchesnee@yahoo.com"].includes(profile?.email) && profile?.plan === "free") ? (
                          <div style={{ padding:"14px 16px", background:"linear-gradient(135deg,rgba(99,102,241,0.06),rgba(99,102,241,0.02))", border:"1px solid rgba(99,102,241,0.2)", borderRadius:12, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
                            <div>
                              <div style={{ fontSize:13, fontWeight:700, color:"#0d9488", marginBottom:3 }}>Fix My Gaps</div>
                              <div style={{ fontSize:12, color:C.muted, fontFamily:"'Space Mono',monospace" }}>Upgrade to fix missing keywords and pass ATS filters</div>
                            </div>
                            <button onClick={() => setShowUpgradeChoice(true)}
                              style={{ background:"linear-gradient(135deg,#0d9488,#0d9488)", color:"#fff", border:"none", borderRadius:9, padding:"10px 22px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap", flexShrink:0 }}>
                              Unlock Fix My Gaps →
                            </button>
                          </div>
                        ) : (
                          <div style={{ padding:"14px 16px", background:"linear-gradient(135deg,rgba(99,102,241,0.06),rgba(99,102,241,0.02))", border:"1px solid rgba(99,102,241,0.2)", borderRadius:12, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
                            <div>
                              <div style={{ fontSize:13, fontWeight:700, color:"#0d9488", marginBottom:3 }}>Fix My Gaps</div>
                              <div style={{ fontSize:12, color:C.muted, fontFamily:"'Space Mono',monospace" }}>
                                {atsAttempts <= 1
                                  ? `Target the ${atsScore.keywords_missing?.length} missing keywords`
                                  : `Attempt ${atsAttempts - 1} of 3 — targeting missing keywords`}
                              </div>
                            </div>
                            <button onClick={() => { setAtsError(""); fixMyGaps(); }} disabled={fixingGaps || strengtheningResume || atsAttempts >= 4}
                              style={{ background:"linear-gradient(135deg,#0d9488,#0d9488)", color:"#fff", border:"none", borderRadius:9, padding:"10px 22px", fontSize:13, fontWeight:700, cursor:(fixingGaps || strengtheningResume || atsAttempts >= 4)?"not-allowed":"pointer", opacity:(fixingGaps || strengtheningResume || atsAttempts >= 4)?0.4:1, fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap", flexShrink:0 }}>
                              {fixingGaps ? "Fixing…" : atsAttempts <= 1 ? "Fix My Gaps →" : atsAttempts === 2 ? "Try Again →" : atsAttempts === 3 ? "Final Attempt →" : "Completed"}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ marginTop:16, padding:"14px 16px", background:C.tealSoft, border:`1px solid ${C.teal}30`, borderRadius:12, display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ color:C.teal, fontWeight:700, fontSize:16 }}>✓</span>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:C.teal }}>No keyword gaps found</div>
                          <div style={{ fontSize:12, color:C.teal, opacity:0.8, fontFamily:"'Space Mono',monospace", marginTop:2 }}>Your resume already covers the key terms for this role.</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {!atsScore && !atsLoading && !atsError && !(isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack") && freeAtsUsed && (
                  <div style={{ padding:"28px 22px", textAlign:"center" }}>
                    <div style={{ fontSize:28, marginBottom:10 }}>🔒</div>
                    <div style={{ fontWeight:700, fontSize:14, marginBottom:6 }}>ATS Score locked on free plan</div>
                    <p style={{ color:C.sub, fontSize:13, margin:"0 0 16px", lineHeight:1.6 }}>Your ATS score determines if a human ever sees your resume. Upgrade to see exactly what's holding you back.</p>
                    <button onClick={() => setUpgrade("Upgrade to see your full ATS Score breakdown — keyword matches, missing terms, formatting notes, and your #1 improvement tip.")}
                      style={{ background:"linear-gradient(135deg,#0d9488,#0d9488)", color:"#fff", border:"none", borderRadius:9, padding:"10px 22px", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      Unlock ATS Checker →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Combined Insight Row ── */}
            {rewriteDone && atsScore && strengthScore && (() => {
              const avgScore = Math.round((atsScore.score + strengthScore.score) / 2);
              const bothA = atsScore.grade === "A" && strengthScore.grade === "A";
              const atsOnly = atsScore.grade === "A" && strengthScore.grade !== "A";
              const strengthOnly = strengthScore.grade === "A" && atsScore.grade !== "A";
              const bothStrong = avgScore >= 70; // passing territory — C/B and above
              const bg = bothA ? C.tealSoft : bothStrong ? C.tealSoft : atsOnly ? C.tealSoft : "rgba(214,54,81,0.06)";
              const borderColor = bothA ? C.teal : bothStrong ? C.teal : atsOnly ? C.teal : C.accent;
              const icon = null;
              const headline = bothA
                ? "You're ready to apply with confidence"
                : atsOnly
                ? "Strong results — you're well positioned to apply"
                : strengthOnly
                ? "Strong to humans — but needs more keywords to pass ATS filters"
                : bothStrong
                ? "Solid scores — you're in good shape to apply"
                : atsPeaked && usedRewrite >= 3
                ? "Your resume is as strong as we can make it — consider applying and letting your cover letter do the rest"
                : "Good start — use the tools below to strengthen your application";
              return (
                <div style={{ marginTop:16, padding:"16px 22px", borderRadius:14, background:bg, border:`2px solid ${borderColor}40`, display:"flex", alignItems:"center", gap:14 }}>
                  <div>
                    <div style={{ fontWeight:800, fontSize:14, color:C.text, marginBottom:4 }}>{headline}</div>
                    <div style={{ fontSize:12, color:C.sub, fontFamily:"'Space Mono',monospace", marginTop:2 }}>
                      ATS {atsScore.grade} ({atsScore.score}/100) · Strength {strengthScore.grade} ({strengthScore.score}/100)
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── Resume Strength Score Panel ── */}
            {rewriteDone && (
              <div style={{ marginTop:20, border:`1px solid ${C.border}`, borderRadius:18, overflow:"hidden", animation:"fadeUp .3s ease forwards" }}>
                <div style={{ background:"#f9fafb", borderBottom:`1px solid ${C.border}`, padding:"14px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:9, background:C.tealSoft, border:`1px solid ${C.teal}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontFamily:"'Space Mono',monospace", fontWeight:700, color:C.teal }}>RS</div>
                    <div>
                      <div style={{ fontWeight:800, fontSize:16, color:C.text }}>Resume Strength Score</div>
                      <div style={{ fontSize:12, color:C.sub, fontFamily:"'Space Mono',monospace" }}>Will a hiring manager be impressed? Checks impact, achievements & language.</div>
                    </div>
                  </div>
                  {!strengthScore && !strengthLoading && (
                    <button onClick={runStrengthScore}
                      style={{ background:C.teal, color:"#fff", border:"none", borderRadius:9, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", boxShadow:`0 0 18px ${C.tealSoft}`, whiteSpace:"nowrap" }}>
                      Run Strength Check →
                    </button>
                  )}
                </div>
                {strengthLoading && (
                  <div style={{ padding:"32px", textAlign:"center" }}>
                    <div style={{ fontSize:13, color:C.muted, fontFamily:"'Space Mono',monospace", animation:"pulse 1.2s ease-in-out infinite" }}>Analyzing resume strength…</div>
                  </div>
                )}
                {strengthError && strengthError !== "__no_improvement__" && !strengthLoading && (
                  <div style={{ padding:"16px 22px", fontSize:13, color:"#dc2626" }}>{strengthError}</div>
                )}
                {strengthScore && !strengthLoading && (
                  <div style={{ padding:"22px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:20, marginBottom:20, flexWrap:"wrap" }}>
                      <div style={{ position:"relative", width:90, height:90, flexShrink:0 }}>
                        <svg width="90" height="90" viewBox="0 0 90 90">
                          <circle cx="45" cy="45" r="36" fill="none" stroke="#e5e7eb" strokeWidth="8"/>
                          <circle cx="45" cy="45" r="36" fill="none"
                            stroke={strengthScore.score >= 80 ? C.teal : strengthScore.score >= 70 ? C.teal : C.teal}
                            strokeWidth="8" strokeLinecap="round"
                            strokeDasharray={`${(strengthScore.score / 100) * 226} 226`}
                            strokeDashoffset="56.5"/>
                        </svg>
                        <div style={{ position:"absolute", top:0, left:0, right:0, bottom:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                          <span style={{ fontFamily:"'Space Mono',monospace", fontWeight:700, fontSize:18, color: strengthScore.score >= 80 ? C.teal : strengthScore.score >= 70 ? C.teal : C.teal, lineHeight:1 }}>{strengthScore.score}</span>
                          <span style={{ fontFamily:"'Space Mono',monospace", fontSize:9, color:C.muted, marginTop:1 }}>/100</span>
                        </div>
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
                          <span style={{ fontFamily:"'Space Mono',monospace", fontWeight:700, fontSize:22, color: strengthScore.score >= 80 ? C.teal : strengthScore.score >= 70 ? C.teal : C.teal }}>Grade {strengthScore.grade}</span>
                          <span style={{ fontSize:11, fontFamily:"'Space Mono',monospace", background: strengthScore.score >= 80 ? C.tealSoft : strengthScore.score >= 70 ? C.tealSoft : C.tealSoft, color: strengthScore.score >= 80 ? C.teal : strengthScore.score >= 70 ? C.teal : C.teal, borderRadius:6, padding:"2px 8px" }}>
                            {strengthScore.score >= 90 ? "Interview Ready" : strengthScore.score >= 75 ? "Good Standing" : "Needs Improvement"}
                          </span>
                        </div>
                        <p style={{ color:C.sub, fontSize:13, margin:0, lineHeight:1.6 }}>{strengthScore?.summary}</p>
                      </div>
                    </div>
                    {strengthScore.categories?.length > 0 && (
                      <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:16 }}>
                        {strengthScore.categories?.map((cat, i) => (
                          <div key={i}>
                            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                              <span style={{ fontSize:14, fontWeight:600, color:C.text }}>{cat.name}</span>
                              <span style={{ fontSize:13, fontFamily:"'Space Mono',monospace", color: cat.score >= 80 ? C.teal : cat.score >= 60 ? C.teal : C.accent }}>{cat.score}/100</span>
                            </div>
                            <div style={{ height:6, background:"#e5e7eb", borderRadius:4, overflow:"hidden" }}>
                              <div style={{ height:"100%", width:`${cat.score}%`, background: cat.score >= 80 ? C.teal : cat.score >= 60 ? C.teal : C.accent, borderRadius:4, transition:"width 1s ease" }}/>
                            </div>
                            <div style={{ fontSize:13, color:C.muted, marginTop:4, lineHeight:1.6 }}>{cat.feedback}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {strengthScore?.improvements?.length > 0 && (
                      <div style={{ background:C.tealSoft, border:`1px solid ${C.teal}30`, borderRadius:12, padding:"14px 16px" }}>
                        <div style={{ fontSize:11, color:C.teal, fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:700, marginBottom:8 }}>Top Improvements</div>
                        <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                          {strengthScore.improvements?.map((tip, i) => (
                            <div key={i} style={{ fontSize:13, color:C.sub, display:"flex", gap:8, lineHeight:1.5 }}>
                              <span style={{ color:C.teal, flexShrink:0, fontWeight:700 }}>{i+1}.</span>{tip}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {strengthScore?.grade === "A" ? (
                      <div style={{ marginTop:14, padding:"14px 16px", background:C.tealSoft, border:`1px solid ${C.teal}30`, borderRadius:12, display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ color:C.teal, fontWeight:700, fontSize:16 }}>✓</span>
                        <div style={{ fontSize:13, fontWeight:700, color:C.teal }}>Strong resume — hiring managers will take notice</div>
                      </div>
                    ) : strengtheningResume ? (
                      <div style={{ marginTop:14, padding:"16px 20px", background:C.tealSoft, border:`1px solid ${C.teal}40`, borderRadius:12, display:"flex", alignItems:"center", gap:14 }}>
                        <div style={{ width:28, height:28, border:`2px solid ${C.teal}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite", flexShrink:0 }}/>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{strengthAttempts === 0 ? "Analyzing your resume…" : "Strengthening your resume…"}</div>
                          <div style={{ fontSize:12, color:C.muted, fontFamily:"'Space Mono',monospace", marginTop:2 }}>{strengthAttempts === 0 ? "Checking how hiring managers will read your resume" : "Rewriting for impact, clarity & achievement language"}</div>
                        </div>
                      </div>
                    ) : strengthPeaked ? (
                      <div style={{ marginTop:14, borderRadius:16, overflow:"hidden", border:`1.5px solid ${C.teal}40`, animation:"fadeUp .35s ease forwards", willChange:"transform, opacity" }}>
                        <div style={{ background:`linear-gradient(135deg,${C.teal}15,${C.tealSoft})`, padding:"20px 24px", borderBottom:`1px solid ${C.teal}20` }}>
                          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:8 }}>
                            <div style={{ width:10, height:10, borderRadius:"50%", background:C.teal, flexShrink:0 }}/>
                            <div style={{ fontSize:16, fontWeight:800, color:C.text }}>You're ready to apply</div>
                          </div>
                          <div style={{ fontSize:13, color:C.sub, lineHeight:1.7, paddingLeft:22 }}>
                            A {strengthScore?.score || "high"}/100 is an excellent score — you're in the top tier of candidates for this role. Further AI rewrites won't meaningfully improve it from here.
                          </div>
                        </div>
                        <div style={{ background:"#fff", padding:"14px 24px", display:"flex", gap:24, flexWrap:"wrap", borderBottom:`1px solid ${C.border}` }}>
                          {[
                            { label:"Strength Score", value:`${strengthScore?.score || "—"}/100`, color: (strengthScore?.score || 0) >= 80 ? C.teal : (strengthScore?.score || 0) >= 70 ? C.teal : C.teal },
                            { label:"ATS Status", value: atsScore ? `${atsScore.score}/100` : "Not run", color:C.teal },
                            { label:"Grade", value: strengthScore?.grade || "B", color: (strengthScore?.score || 0) >= 80 ? C.teal : (strengthScore?.score || 0) >= 70 ? C.teal : C.teal },
                          ].map(stat => (
                            <div key={stat.label}>
                              <div style={{ fontSize:10, color:C.muted, fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:3 }}>{stat.label}</div>
                              <div style={{ fontSize:16, fontWeight:800, color:stat.color }}>{stat.value}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ background:"#fafafa", padding:"16px 24px", display:"flex", gap:10, flexWrap:"wrap" }}/>
                      </div>
                    ) : (
                      <div style={{ marginTop:14, borderRadius:12, overflow:"hidden", border:`1px solid ${C.teal}30` }}>
                        {strengthError === "__no_improvement__" && (
                          <div style={{ background:C.tealSoft, borderBottom:`1px solid ${C.teal}20`, padding:"12px 16px", display:"flex", alignItems:"center", gap:10 }}>
                            <div style={{ width:7, height:7, borderRadius:"50%", background:C.teal, flexShrink:0 }}/>
                            <div style={{ fontSize:12, color:C.text, fontFamily:"'Space Mono',monospace" }}>
                              Resume was strengthened, but your previous version scored higher — your best version is still showing.
                            </div>
                          </div>
                        )}
                        <div style={{ padding:"14px 16px", background:C.tealSoft, border:`1px solid ${C.teal}30`, borderRadius:12, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
                          <div>
                            <div style={{ fontSize:13, fontWeight:700, color:C.teal, marginBottom:3 }}>
                              {strengthAttempts === 0 ? "Check Resume Strength" : "Strengthen My Resume"}
                            </div>
                            <div style={{ fontSize:12, color:C.muted, fontFamily:"'Space Mono',monospace" }}>
                              {strengthAttempts === 0
                                ? "Analyze how your resume reads to a human hiring manager"
                                : "Rewrite bullets for impact, clarity & measurable achievements"}
                            </div>
                          </div>
                          <button onClick={() => { setStrengthError(""); strengthenResume(); }} disabled={strengtheningResume || fixingGaps || strengthAttempts >= 4}
                            style={{ background:`linear-gradient(135deg,${C.teal},#0d9488)`, color:"#fff", border:"none", borderRadius:9, padding:"10px 22px", fontSize:13, fontWeight:700, cursor:(strengtheningResume || fixingGaps || strengthAttempts >= 4) ? "not-allowed" : "pointer", opacity: (strengthAttempts >= 4 || fixingGaps) ? 0.4 : 1, fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap", flexShrink:0 }}>
                            {strengthAttempts === 0 ? "Analyze →" : strengthAttempts === 1 ? "Strengthen Resume →" : strengthAttempts === 2 ? "Try Again →" : strengthAttempts === 3 ? "Final Attempt →" : "Completed"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {!strengthScore && !strengthLoading && !strengthError && !(isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack") && (
                  <div style={{ padding:"28px 22px", textAlign:"center" }}>
                    <div style={{ fontWeight:700, fontSize:14, marginBottom:6 }}>Resume Strength locked on free plan</div>
                    <p style={{ color:C.sub, fontSize:13, margin:"0 0 16px", lineHeight:1.6 }}>Find out if your resume will impress a human hiring manager — not just pass a filter.</p>
                    <button onClick={() => setUpgrade("Resume Strength Score checks the human side of your resume — impact language, quantified achievements, and career narrative. Available on Match Pack and Pro.")}
                      style={{ background:`linear-gradient(135deg,${C.teal},#0d9488)`, color:"#fff", border:"none", borderRadius:9, padding:"10px 22px", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      Unlock Strength Score →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Cover Letter Panel ── */}
            {rewriteDone && (
              <div style={{ marginTop:20, border:`1px solid ${C.border}`, borderRadius:18, overflow:"hidden", animation:"fadeUp .3s ease forwards" }}>
                <div style={{ background:"#f9fafb", borderBottom:`1px solid ${C.border}`, padding:"14px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:9, background:C.tealSoft, border:`1px solid ${C.teal}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontFamily:"'Space Mono',monospace", fontWeight:700, color:C.teal }}>CL</div>
                    <div>
                      <div style={{ fontWeight:800, fontSize:16, color:C.text }}>Cover Letter Generator</div>
                      <div style={{ fontSize:11, color:C.muted, fontFamily:"'Space Mono',monospace" }}>AI-written cover letter tailored to {selectedJob?.company}</div>
                    </div>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                    {(isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack") && (
                      <div style={{ display:"flex", gap:4 }}>
                        {["professional","conversational","bold"].map((tone) => (
                          <button key={tone} onClick={() => { setCoverLetterTone(tone); setCoverLetter(""); }}
                            style={{ background: coverLetterTone === tone ? C.tealSoft : "transparent", border:`1px solid ${coverLetterTone === tone ? C.teal : C.border}`, color: coverLetterTone === tone ? C.teal : C.muted, borderRadius:8, padding:"5px 10px", fontSize:11, cursor:"pointer", fontFamily:"'Space Mono',monospace", transition:"all .15s", textTransform:"capitalize" }}>
                            {tone}
                          </button>
                        ))}
                      </div>
                    )}
                    {coverLetter && (
                      <button onClick={runCoverLetter}
                        style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.muted, borderRadius:9, padding:"7px 14px", fontSize:12, cursor:"pointer", fontFamily:"'Space Mono',monospace" }}>
                        ↺ Re-generate
                      </button>
                    )}
                    {!coverLetter && !coverLetterLoading && (
                      <button onClick={runCoverLetter}
                        style={{ background:C.teal, color:"#fff", border:"none", borderRadius:9, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", boxShadow:`0 0 18px ${C.tealSoft}`, whiteSpace:"nowrap" }}>
                        Generate Cover Letter →
                      </button>
                    )}
                  </div>
                </div>
                {coverLetterLoading && (
                  <div style={{ padding:"32px", textAlign:"center" }}>
                    <div style={{ fontSize:13, color:C.muted, fontFamily:"'Space Mono',monospace", animation:"pulse 1.2s ease-in-out infinite" }}>Writing your cover letter…</div>
                  </div>
                )}
                {coverLetterError && !coverLetterLoading && (
                  <div style={{ padding:"16px 22px", fontSize:13, color:"#dc2626" }}>{coverLetterError}</div>
                )}
                {coverLetter && !coverLetterLoading && (
                  <div style={{ padding:"22px" }}>
                    <div style={{ background:"#fafaf9", border:`1px solid ${C.border}`, borderRadius:12, padding:"20px 22px", fontFamily:"'DM Sans',sans-serif", fontSize:13, lineHeight:1.9, color:C.text, whiteSpace:"pre-wrap", maxHeight:420, overflowY:"auto" }}>
                      {coverLetter}
                    </div>
                    <div style={{ display:"flex", justifyContent:"flex-start", marginTop:10, gap:8, flexWrap:"wrap" }}>
                      <button onClick={copyCoverLetter}
                        style={{ background: coverLetterCopied ? C.tealSoft : "transparent", border:`1.5px solid ${C.border}`, color:C.muted, borderRadius:9, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", gap:6, transition:"all .2s" }}>
                        {coverLetterCopied ? "Copied!" : "Copy cover letter"}
                      </button>
                      <button onClick={handleCoverLetterPDFDownload}
                        style={{ background:C.teal, color:"#fff", border:"none", borderRadius:10, padding:"9px 18px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                        Download PDF
                      </button>
                      <button onClick={handleCoverLetterDocxDownload}
                        style={{ background:"#1e40af", color:"#fff", border:"none", borderRadius:10, padding:"9px 18px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                        Download Word
                      </button>
                    </div>
                  </div>
                )}
                {!coverLetter && !coverLetterLoading && !coverLetterError && !(isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack") && (
                  <div style={{ padding:"28px 22px", textAlign:"center" }}>
                    <div style={{ fontSize:28, marginBottom:10 }}>🔒</div>
                    <div style={{ fontWeight:700, fontSize:14, marginBottom:6 }}>Cover Letter locked on free plan</div>
                    <p style={{ color:C.sub, fontSize:13, margin:"0 0 16px", lineHeight:1.6 }}>Upgrade to generate a tailored cover letter for any job in seconds — Professional, Conversational, or Bold tone.</p>
                    <button onClick={() => setUpgrade("Generate tailored cover letters for any job. Choose your tone — Professional, Conversational, or Bold. Available on Match Pack and Pro.")}
                      style={{ background:`linear-gradient(135deg,${C.accent},#c0392b)`, color:"#fff", border:"none", borderRadius:9, padding:"10px 22px", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      Unlock Cover Letter Generator →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Interview Prep Panel ── */}
            {rewriteDone && (
              <div style={{ marginTop:20, border:`1px solid ${C.border}`, borderRadius:18, overflow:"hidden", animation:"fadeUp .3s ease forwards" }}>
                <div style={{ background:"#f9fafb", borderBottom:`1px solid ${C.border}`, padding:"14px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:9, background:C.tealSoft, border:`1px solid ${C.teal}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontFamily:"'Space Mono',monospace", fontWeight:700, color:C.teal }}>IP</div>
                    <div>
                      <div style={{ fontWeight:800, fontSize:16, color:C.text }}>Interview Prep</div>
                      <div style={{ fontSize:12, color:C.sub, fontFamily:"'Space Mono',monospace" }}>Targeted questions and coaching tips for {selectedJob?.company}</div>
                    </div>
                  </div>
                  {!interviewQuestions && !interviewLoading && (
                    <button onClick={runInterviewPrep}
                      style={{ background:`linear-gradient(135deg,${C.teal},#0f766e)`, color:"#fff", border:"none", borderRadius:9, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", boxShadow:`0 0 18px ${C.tealSoft}`, whiteSpace:"nowrap" }}>
                      Generate Questions →
                    </button>
                  )}
                </div>

                {interviewLoading && (
                  <div style={{ padding:"32px", textAlign:"center" }}>
                    <div style={{ fontSize:13, color:C.muted, fontFamily:"'Space Mono',monospace", animation:"pulse 1.2s ease-in-out infinite" }}>Generating interview questions…</div>
                  </div>
                )}

                {interviewError && !interviewLoading && (
                  <div style={{ padding:"16px 22px", fontSize:13, color:"#dc2626" }}>{interviewError}</div>
                )}

                {interviewQuestions && !interviewLoading && (
                  <div style={{ padding:"22px" }}>
                    {/* Mode Toggle */}
                    <div style={{ display:"flex", gap:8, marginBottom:20, background:"#f4f4f5", borderRadius:10, padding:4, width:"fit-content" }}>
                      {["study","mock"].map(mode => (
                        <button key={mode} onClick={() => { setInterviewMode(mode); resetMockInterview(); }}
                          style={{ background: interviewMode === mode ? "#fff" : "transparent", border:"none", borderRadius:8, padding:"7px 18px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", color: interviewMode === mode ? C.text : C.muted, boxShadow: interviewMode === mode ? "0 1px 4px rgba(0,0,0,0.10)" : "none", transition:"all .2s" }}>
                          {mode === "study" ? "📖 Study Mode" : "🎤 Mock Interview"}
                        </button>
                      ))}
                    </div>

                    {/* Study Mode */}
                    {interviewMode === "study" && (
                      <>
                        {interviewQuestions.map((cat, ci) => (
                          <div key={ci} style={{ marginBottom: ci < interviewQuestions.length - 1 ? 24 : 0 }}>
                            <div style={{ fontSize:11, color:C.teal, fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:700, marginBottom:12 }}>{cat.category}</div>
                            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                              {cat.questions.map((q, qi) => (
                                <div key={qi} style={{ background:"#fafafa", border:`1px solid ${C.border}`, borderRadius:12, padding:"14px 16px" }}>
                                  <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:6, lineHeight:1.5 }}>{qi + 1}. {q.q}</div>
                                  <div style={{ fontSize:12, color:C.sub, fontFamily:"'Space Mono',monospace", lineHeight:1.6, borderTop:`1px solid ${C.border}`, paddingTop:8, marginTop:2 }}>Tip: {q.tip}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        <div style={{ display:"flex", justifyContent:"flex-start", marginTop:16, gap:8 }}>
                          <button onClick={copyInterviewQuestions}
                            style={{ background: interviewCopied ? C.tealSoft : "transparent", border:`1.5px solid ${C.border}`, color:C.muted, borderRadius:9, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", transition:"all .2s" }}>
                            {interviewCopied ? "Copied!" : "Copy all questions"}
                          </button>
                          <button onClick={runInterviewPrep}
                            style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.muted, borderRadius:9, padding:"7px 14px", fontSize:12, cursor:"pointer", fontFamily:"'Space Mono',monospace" }}>
                            Re-generate
                          </button>
                        </div>
                      </>
                    )}

                    {/* Mock Interview Mode */}
                    {interviewMode === "mock" && (() => {
                      const allQuestions = interviewQuestions.flatMap(cat => cat.questions.map(q => ({ ...q, category: cat.category })));
                      const total = allQuestions.length;
                      const current = allQuestions[mockCurrentIdx];
                      return mockComplete ? (
                        <div style={{ textAlign:"center", padding:"32px 0" }}>
                          <div style={{ fontSize:32, marginBottom:12 }}>🎉</div>
                          <div style={{ fontSize:18, fontWeight:700, color:C.text, marginBottom:8 }}>Mock Interview Complete!</div>
                          <div style={{ fontSize:14, color:C.muted, marginBottom:24 }}>You answered all {total} questions. Great practice!</div>
                          <button onClick={resetMockInterview}
                            style={{ background:`linear-gradient(135deg,${C.teal},#0f766e)`, color:"#fff", border:"none", borderRadius:9, padding:"10px 24px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                            Start Over
                          </button>
                        </div>
                      ) : (
                        <div>
                          {/* Progress */}
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                            <div style={{ fontSize:11, color:C.teal, fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:700 }}>{current.category}</div>
                            <div style={{ fontSize:12, color:C.muted, fontFamily:"'Space Mono',monospace" }}>Question {mockCurrentIdx + 1} of {total}</div>
                          </div>
                          <div style={{ background:"#f4f4f5", borderRadius:8, height:6, marginBottom:20 }}>
                            <div style={{ background:C.teal, borderRadius:8, height:6, width:`${((mockCurrentIdx + 1) / total) * 100}%`, transition:"width .3s" }} />
                          </div>

                          {/* Question */}
                          <div style={{ background:"#fafafa", border:`1px solid ${C.border}`, borderRadius:12, padding:"16px 18px", marginBottom:16 }}>
                            <div style={{ fontSize:15, fontWeight:700, color:C.text, lineHeight:1.5, marginBottom:8 }}>{current.q}</div>
                            <div style={{ fontSize:12, color:C.sub, fontFamily:"'Space Mono',monospace", lineHeight:1.6, borderTop:`1px solid ${C.border}`, paddingTop:8 }}>Tip: {current.tip}</div>
                          </div>

                          {/* Answer Input */}
                          {!mockFeedback && (
                            <>
                              <textarea
                                value={mockAnswer}
                                onChange={e => setMockAnswer(e.target.value)}
                                placeholder="Type your answer here..."
                                rows={5}
                                style={{ width:"100%", borderRadius:10, border:`1.5px solid ${C.border}`, padding:"12px 14px", fontSize:14, fontFamily:"'DM Sans',sans-serif", color:C.text, resize:"vertical", outline:"none", boxSizing:"border-box", marginBottom:12 }}
                              />
                              {mockFeedbackError && <div style={{ fontSize:13, color:"#dc2626", marginBottom:8 }}>{mockFeedbackError}</div>}
                              <button onClick={submitMockAnswer} disabled={mockFeedbackLoading || !mockAnswer.trim()}
                                style={{ background: mockAnswer.trim() ? `linear-gradient(135deg,${C.teal},#0f766e)` : "#e5e7eb", color: mockAnswer.trim() ? "#fff" : C.muted, border:"none", borderRadius:9, padding:"10px 24px", fontSize:14, fontWeight:700, cursor: mockAnswer.trim() ? "pointer" : "not-allowed", fontFamily:"'DM Sans',sans-serif", transition:"all .2s" }}>
                                {mockFeedbackLoading ? "Getting feedback…" : "Submit Answer →"}
                              </button>
                            </>
                          )}

                          {/* Feedback */}
                          {mockFeedback && (
                            <div style={{ background:"#f0fdf4", border:"1.5px solid #bbf7d0", borderRadius:12, padding:"16px 18px", marginBottom:16 }}>
                              <div style={{ fontSize:11, color:"#16a34a", fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:700, marginBottom:8 }}>Coach Feedback</div>
                              <div style={{ fontSize:14, color:C.text, lineHeight:1.7 }}>{mockFeedback}</div>
                            </div>
                          )}

                          {mockFeedback && (
                            <button onClick={nextMockQuestion}
                              style={{ background:`linear-gradient(135deg,${C.teal},#0f766e)`, color:"#fff", border:"none", borderRadius:9, padding:"10px 24px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                              {mockCurrentIdx + 1 >= total ? "Finish Interview" : "Next Question →"}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {!interviewQuestions && !interviewLoading && !interviewError && !(isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack") && (
                  <div style={{ padding:"28px 22px", textAlign:"center" }}>
                    <div style={{ fontWeight:700, fontSize:14, marginBottom:6 }}>Interview Prep locked on free plan</div>
                    <p style={{ color:C.sub, fontSize:13, margin:"0 0 16px", lineHeight:1.6 }}>Get targeted interview questions and coaching tips tailored to the role and your resume. Available on Match Pack and Pro.</p>
                    <button onClick={() => setUpgrade("Walking in prepared wins interviews. Unlock Interview Prep on Match Pack ($9.99) or Pro — get targeted questions for every role you apply to.")}
                      style={{ background:`linear-gradient(135deg,${C.teal},#0f766e)`, color:"#fff", border:"none", borderRadius:9, padding:"10px 22px", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      Unlock Interview Prep →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── LinkedIn Rewriter Panel ── */}
            {rewriteDone && (
              <div style={{ marginTop:20, border:`1px solid ${C.border}`, borderRadius:18, overflow:"hidden", animation:"fadeUp .3s ease forwards" }}>
                <div style={{ background:"#f9fafb", borderBottom:`1px solid ${C.border}`, padding:"14px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:9, background:"rgba(13,148,136,0.1)", border:"1px solid rgba(13,148,136,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontFamily:"'Space Mono',monospace", fontWeight:700, color:"#0d9488" }}>LI</div>
                    <div>
                      <div style={{ fontWeight:800, fontSize:16, color:C.text }}>LinkedIn Rewriter</div>
                      <div style={{ fontSize:12, color:C.sub, fontFamily:"'Space Mono',monospace" }}>Headline and summary optimized for {selectedJob?.title} roles</div>
                    </div>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    {linkedInResult && (
                      <button onClick={runLinkedIn}
                        style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.muted, borderRadius:9, padding:"7px 14px", fontSize:12, cursor:"pointer", fontFamily:"'Space Mono',monospace" }}>
                        Re-generate
                      </button>
                    )}
                    {!linkedInResult && !linkedInLoading && (
                      <button onClick={runLinkedIn}
                        style={{ background:C.teal, color:"#fff", border:"none", borderRadius:9, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", boxShadow:`0 0 18px ${C.tealSoft}`, whiteSpace:"nowrap" }}>
                        Generate LinkedIn Copy →
                      </button>
                    )}
                  </div>
                </div>

                {linkedInLoading && (
                  <div style={{ padding:"32px", textAlign:"center" }}>
                    <div style={{ fontSize:13, color:C.muted, fontFamily:"'Space Mono',monospace", animation:"pulse 1.2s ease-in-out infinite" }}>Writing your LinkedIn headline and summary…</div>
                  </div>
                )}

                {linkedInError && !linkedInLoading && (
                  <div style={{ padding:"16px 22px", fontSize:13, color:"#dc2626" }}>{linkedInError}</div>
                )}

                {linkedInResult && !linkedInLoading && (
                  <div style={{ padding:"22px", display:"flex", flexDirection:"column", gap:16 }}>
                    {/* Headline */}
                    <div>
                      <div style={{ fontSize:11, color:"#0d9488", fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:700, marginBottom:8 }}>Headline</div>
                      <div style={{ background:"#fafafa", border:`1px solid ${C.border}`, borderRadius:12, padding:"14px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
                        <div style={{ fontSize:15, fontWeight:600, color:C.text, lineHeight:1.5 }}>{linkedInResult.headline}</div>
                        <button onClick={() => copyLinkedIn("headline", linkedInResult.headline)}
                          style={{ background: linkedInCopied.headline ? "rgba(13,148,136,0.1)" : "transparent", border:`1.5px solid ${C.border}`, color:C.muted, borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap", flexShrink:0, transition:"all .2s" }}>
                          {linkedInCopied.headline ? "Copied!" : "Copy"}
                        </button>
                      </div>
                      <div style={{ fontSize:11, color:C.muted, fontFamily:"'Space Mono',monospace", marginTop:5 }}>
                        {linkedInResult.headline.length}/220 characters
                      </div>
                    </div>

                    {/* Summary */}
                    <div>
                      <div style={{ fontSize:11, color:"#0d9488", fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:700, marginBottom:8 }}>About / Summary</div>
                      <div style={{ background:"#fafafa", border:`1px solid ${C.border}`, borderRadius:12, padding:"16px" }}>
                        <div style={{ fontSize:14, color:C.text, lineHeight:1.8, whiteSpace:"pre-wrap", marginBottom:12 }}>{linkedInResult.summary}</div>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                          <div style={{ fontSize:11, color:C.muted, fontFamily:"'Space Mono',monospace" }}>
                            {linkedInResult.summary.length}/2600 characters
                          </div>
                          <button onClick={() => copyLinkedIn("summary", linkedInResult.summary)}
                            style={{ background: linkedInCopied.summary ? "rgba(13,148,136,0.1)" : "transparent", border:`1.5px solid ${C.border}`, color:C.muted, borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", transition:"all .2s" }}>
                            {linkedInCopied.summary ? "Copied!" : "Copy"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Keywords */}
                    {linkedInResult.keywords?.length > 0 && (
                      <div>
                        <div style={{ fontSize:11, color:"#0d9488", fontFamily:"'Space Mono',monospace", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:700, marginBottom:8 }}>Keywords Used</div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                          {linkedInResult.keywords.map((kw, i) => (
                            <span key={i} style={{ background:"rgba(13,148,136,0.08)", border:"1px solid rgba(13,148,136,0.2)", color:"#0d9488", borderRadius:5, padding:"2px 10px", fontSize:11, fontFamily:"'Space Mono',monospace" }}>{kw}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!linkedInResult && !linkedInLoading && !linkedInError && !(isSuperUser || profile?.plan === "pro" || profile?.plan === "pro_annual" || profile?.plan === "pack") && (
                  <div style={{ padding:"28px 22px", textAlign:"center" }}>
                    <div style={{ fontWeight:700, fontSize:14, marginBottom:6 }}>LinkedIn Rewriter locked on free plan</div>
                    <p style={{ color:C.sub, fontSize:13, margin:"0 0 16px", lineHeight:1.6 }}>Get a LinkedIn headline and summary optimized for the roles you're targeting. Available on Match Pack and Pro.</p>
                    <button onClick={() => setUpgrade("Recruiters check LinkedIn before they call you. Unlock the LinkedIn Rewriter on Match Pack ($9.99) or Pro — make your profile work as hard as your resume.")}
                      style={{ background:"linear-gradient(135deg,#0d9488,#0d9488)", color:"#fff", border:"none", borderRadius:9, padding:"10px 22px", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      Unlock LinkedIn Rewriter →
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={{ textAlign:"center", marginTop:24 }}>
              {!rewriteDone ? (
                <button onClick={doRewrite} disabled={rewriting}
                  style={{ background:`linear-gradient(135deg,${C.accent},#c0392b)`, color:"#fff", border:"none", borderRadius:12, padding:"14px 40px", fontSize:15, fontWeight:700, cursor:rewriting?"not-allowed":"pointer", opacity:rewriting?.7:1, boxShadow:`0 0 36px ${C.accentGlow}` }}>
                  {rewriting ? "Tailoring your resume…" : `Tailor Resume for ${selectedJob.company}`}
                </button>
              ) : (
                <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
                  <button onClick={() => window.open(selectedJob.url,"_blank")} style={{ background:`linear-gradient(135deg,${C.accent},#c0392b)`, color:"#fff", border:"none", borderRadius:12, padding:"12px 26px", fontSize:14, fontWeight:700, cursor:"pointer" }}>Apply at {selectedJob.company} →</button>
                  <button onClick={() => setScreen("results")} style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.muted, borderRadius:12, padding:"12px 22px", fontSize:14, cursor:"pointer" }}>← More Matches</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── PRIVACY POLICY ── */}
        {screen === "privacy" && (
          <LegalPage
            title="Privacy Policy"
            subtitle="We built getresumatch.com on a simple principle: your resume is yours. Here's exactly how we handle your data — transparently, and with your interests first."
            lastUpdated="March 11, 2026"
            sections={PRIVACY_SECTIONS}
            onBack={() => setScreen("upload")}
          />
        )}

        {/* ── TERMS OF SERVICE ── */}
        {screen === "terms" && (
          <LegalPage
            title="Terms of Service"
            subtitle="These are the terms that govern your use of getresumatch.com. We've written them to be clear and readable — not buried in legalese."
            lastUpdated="March 11, 2026"
            sections={TERMS_SECTIONS}
            onBack={() => setScreen("upload")}
          />
        )}

        {screen === "account" && user && (
          <div style={{ maxWidth:860, margin:"0 auto", padding: isMobile ? "24px 16px" : "40px 32px", overflowX:"hidden" }}>
            <a href="/" style={{ display:"inline-flex", alignItems:"center", gap:6, background:"transparent", border:"1px solid rgba(0,0,0,0.12)", color:C.muted, borderRadius:8, padding:"6px 14px", fontSize:12, fontFamily:"'Space Mono',monospace", textDecoration:"none", marginBottom:32 }}>← Back to Home</a>
            <h1 style={{ fontFamily:"'Playfair Display',serif", fontWeight:800, fontSize:36, margin:"0 0 6px", color:C.text }}>Account Settings</h1>
            <p style={{ color:C.muted, fontSize:14, fontFamily:"'Space Mono',monospace", margin:"0 0 40px" }}>{user.email}</p>

            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:20, alignItems:"start" }}>
              <div>
            {/* Current Plan */}
            {profile?.plan && profile.plan !== "free" && (
              <div style={{ border:`1px solid ${C.border}`, borderRadius:16, overflow:"hidden", marginBottom:20 }}>
                <div style={{ background:"#f9fafb", borderBottom:`1px solid ${C.border}`, padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:15, color:C.text }}>Current Plan</div>
                    <div style={{ fontSize:12, color:C.sub, fontFamily:"'Space Mono',monospace", marginTop:2 }}>
                      {profile.plan === "pro" ? "Pro Matcher — $24.99/mo" : profile.plan === "pro_annual" ? "Pro Annual — $199/yr" : "Match Pack — One-Time"}
                    </div>
                  </div>
                  <div style={{ background: profile.plan === "pro" || profile.plan === "pro_annual" ? C.accentSoft : C.tealSoft, border:`1px solid ${profile.plan === "pro" || profile.plan === "pro_annual" ? C.accent : C.teal}30`, borderRadius:6, padding:"3px 10px", fontSize:11, fontFamily:"'Space Mono',monospace", fontWeight:700, color: profile.plan === "pro" || profile.plan === "pro_annual" ? C.accent : C.teal }}>
                    {profile.plan === "pro" ? "PRO" : profile.plan === "pro_annual" ? "PRO ANNUAL" : "PACK"}
                  </div>
                </div>
                <div style={{ padding:"16px 20px", display:"flex", gap:10, flexWrap:"wrap" }}>
                  <button onClick={handleBillingPortal}
                    style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.sub, borderRadius:9, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                    Billing history
                  </button>
                  {(profile.plan === "pro" || profile.plan === "pro_annual") && (
                    <button onClick={() => { setCancelDone(false); setCancelError(""); setShowCancelModal(true); }}
                      style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.muted, borderRadius:9, padding:"9px 18px", fontSize:13, cursor:"pointer", fontFamily:"'Space Mono',monospace" }}>
                      Cancel subscription
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Refer a Friend */}
            <div style={{ border:`1px solid ${C.border}`, borderRadius:16, overflow:"hidden", marginBottom:20 }}>
              <div style={{ background:"#f9fafb", borderBottom:`1px solid ${C.border}`, padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:15, color:C.text }}>Refer a Friend</div>
                  <div style={{ fontSize:12, color:C.sub, fontFamily:"'Space Mono',monospace", marginTop:2 }}>You both get +5 free searches</div>
                </div>
                {profile?.referral_count > 0 && (
                  <div style={{ background:C.tealSoft, border:`1px solid ${C.teal}30`, borderRadius:6, padding:"3px 10px", fontSize:11, fontFamily:"'Space Mono',monospace", fontWeight:700, color:C.teal }}>
                    {profile.referral_count} referral{profile.referral_count !== 1 ? "s" : ""}
                  </div>
                )}
              </div>
              <div style={{ padding:"16px 20px" }}>
                <p style={{ color:C.sub, fontSize:13, margin:"0 0 12px", lineHeight:1.6 }}>
                  Share your link. When a friend signs up and runs their first search, you both get 5 extra matches free.
                </p>
                {profile?.referral_code ? (
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <div style={{ flex:1, background:"#f3f4f6", border:`1px solid ${C.border}`, borderRadius:9, padding:"10px 14px", fontSize:12, fontFamily:"'Space Mono',monospace", color:C.sub, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      https://getresumatch.com?ref={profile.referral_code}
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(`https://getresumatch.com?ref=${profile.referral_code}`); setReferralCopied(true); setTimeout(() => setReferralCopied(false), 2000); }}
                      style={{ background: referralCopied ? C.tealSoft : C.teal, color: referralCopied ? C.teal : "#fff", border: referralCopied ? `1px solid ${C.teal}40` : "none", borderRadius:9, padding:"10px 18px", fontSize:13, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", transition:"all .2s" }}>
                      {referralCopied ? "Copied!" : "Copy link"}
                    </button>
                  </div>
                ) : (
                  <button onClick={async () => {
                    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
                    await supabase.from("profiles").update({ referral_code: code }).eq("id", user.id);
                    loadProfile(user.id);
                  }} style={{ background:C.teal, color:"#fff", border:"none", borderRadius:9, padding:"10px 20px", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                    Generate my referral link
                  </button>
                )}
              </div>
            </div>

              </div>{/* end left col */}
              <div>{/* right col */}
            {/* Change Password */}
            <div style={{ border:`1px solid ${C.border}`, borderRadius:16, overflow:"hidden", marginBottom:20 }}>
              <div style={{ background:"#f9fafb", borderBottom:`1px solid ${C.border}`, padding:"14px 20px" }}>
                <div style={{ fontWeight:700, fontSize:15, color:C.text }}>Change Password</div>
                <div style={{ fontSize:12, color:C.sub, fontFamily:"'Space Mono',monospace", marginTop:2 }}>Must be at least 6 characters</div>
              </div>
              <div style={{ padding:"20px", display:"flex", flexDirection:"column", gap:10 }}>
                {changePwError && <div style={{ background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#dc2626" }}>{changePwError}</div>}
                {changePwSuccess && <div style={{ background:C.tealSoft, border:`1px solid ${C.teal}40`, borderRadius:8, padding:"10px 14px", fontSize:13, color:C.teal, fontWeight:600 }}>{changePwSuccess}</div>}
                <input value={changePwNew} onChange={e => setChangePwNew(e.target.value)} placeholder="New password" type="password"
                  style={{ border:`1px solid ${C.border}`, borderRadius:10, padding:"11px 14px", fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", color:C.text }}
                  onFocus={e => e.target.style.borderColor = C.accent} onBlur={e => e.target.style.borderColor = "rgba(0,0,0,0.08)"}/>
                <input value={changePwConfirm} onChange={e => setChangePwConfirm(e.target.value)} placeholder="Confirm new password" type="password"
                  style={{ border:`1px solid ${C.border}`, borderRadius:10, padding:"11px 14px", fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", color:C.text }}
                  onFocus={e => e.target.style.borderColor = C.accent} onBlur={e => e.target.style.borderColor = "rgba(0,0,0,0.08)"}/>
                <button onClick={handleChangePassword} disabled={changePwLoading}
                  style={{ background:`linear-gradient(135deg,${C.accent},#c0392b)`, color:"#fff", border:"none", borderRadius:10, padding:"12px", fontSize:14, fontWeight:700, cursor:"pointer", opacity: changePwLoading ? 0.7 : 1 }}>
                  {changePwLoading ? "Updating…" : "Update Password"}
                </button>
              </div>
            </div>

            {/* Delete Account */}
            <div style={{ border:"1px solid #fca5a5", borderRadius:16, overflow:"hidden" }}>
              <div style={{ background:"#fef2f2", borderBottom:"1px solid #fca5a5", padding:"14px 20px" }}>
                <div style={{ fontWeight:700, fontSize:15, color:"#dc2626" }}>Delete Account</div>
                <div style={{ fontSize:12, color:"#b91c1c", fontFamily:"'Space Mono',monospace", marginTop:2 }}>Permanent — all data will be erased</div>
              </div>
              <div style={{ padding:"20px", display:"flex", flexDirection:"column", gap:10 }}>
                <p style={{ color:C.sub, fontSize:13, margin:0, lineHeight:1.6 }}>This will permanently delete your account, resume, saved jobs, and all usage data. This action cannot be undone.</p>
                {deleteError && <div style={{ background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#dc2626" }}>{deleteError}</div>}
                <input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} placeholder='Type DELETE to confirm'
                  style={{ border:"1px solid #fca5a5", borderRadius:10, padding:"11px 14px", fontSize:14, outline:"none", fontFamily:"'Space Mono',monospace", color:C.text, letterSpacing:"0.05em" }}/>
                <button onClick={handleDeleteAccount} disabled={deleteLoading}
                  style={{ background: deleteConfirmText === "DELETE" ? "#dc2626" : "#f3f4f6", color: deleteConfirmText === "DELETE" ? "#fff" : C.muted, border:"none", borderRadius:10, padding:"12px", fontSize:14, fontWeight:700, cursor: deleteConfirmText === "DELETE" ? "pointer" : "not-allowed", opacity: deleteConfirmText === "DELETE" ? 1 : 0.5, transition:"all .2s" }}>
                  {deleteLoading ? "Deleting…" : "Delete My Account"}
                </button>
              </div>
            </div>
              </div>{/* end right col */}
            </div>{/* end grid */}
          </div>
        )}

        {screen === "tracker" && (
          <TrackerPage user={user} isMobile={isMobile} onBack={() => setScreen(prevScreen)} />
        )}

        {screen === "404" && (
          <div style={{ maxWidth:600, margin:"0 auto", padding:"80px 32px", textAlign:"center" }}>
            <div style={{ fontFamily:"'Space Mono',monospace", fontSize:120, fontWeight:700, color:C.border, lineHeight:1, marginBottom:8 }}>404</div>
            <h1 style={{ fontFamily:"'Playfair Display',serif", fontWeight:800, fontSize:32, color:C.text, margin:"0 0 16px" }}>Page not found</h1>
            <p style={{ color:C.sub, fontSize:15, lineHeight:1.7, margin:"0 0 40px" }}>The page you're looking for doesn't exist or may have moved. Let's get you back on track.</p>
            <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
              <button onClick={() => { setScreen("upload"); window.history.pushState({}, "", "/"); }}
                style={{ background:`linear-gradient(135deg,${C.accent},#c0392b)`, color:"#fff", border:"none", borderRadius:10, padding:"12px 28px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                Go Home
              </button>
              <button onClick={() => setScreen("faq")}
                style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.muted, borderRadius:10, padding:"12px 28px", fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                View FAQ
              </button>
              <button onClick={() => setScreen("contact")}
                style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.muted, borderRadius:10, padding:"12px 28px", fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                Contact Support
              </button>
            </div>
          </div>
        )}

        {screen === "faq" && (
          <div style={{ maxWidth:860, margin:"0 auto", padding: isMobile ? "24px 16px" : "40px 32px", overflowX:"hidden" }}>
            <a href="/" style={{ display:"inline-flex", alignItems:"center", gap:6, background:"transparent", border:"1px solid rgba(0,0,0,0.12)", color:C.muted, borderRadius:8, padding:"6px 14px", fontSize:12, fontFamily:"'Space Mono',monospace", textDecoration:"none", marginBottom:32 }}>← Back to Home</a>
            <h1 style={{ fontFamily:"'Playfair Display',serif", fontWeight:800, fontSize:36, margin:"0 0 8px", color:C.text }}>Frequently Asked Questions</h1>
            <p style={{ color:C.sub, fontSize:15, lineHeight:1.7, margin:"0 0 48px" }}>Everything you need to know about getresumatch.com.</p>

            {[
              { section: "Getting Started", items: [
                { q: "What is Get Resumatch?", a: "Get Resumatch (getresumatch.com) is an AI-powered job matching platform. You upload your resume once, and we search for jobs that actually fit your experience — then help you tailor your resume, write cover letters, prep for interviews, and more." },
                { q: "How do I get started?", a: "Upload your resume on the home screen. We'll parse it automatically and start finding matching jobs right away. No lengthy profile setup required." },
                { q: "What file types does Resumatch accept?", a: "We accept PDF resumes. For best results, make sure your PDF has selectable text (not a scanned image)." },
              ]},
              { section: "Searches & Matches", items: [
                { q: "How many job searches do I get?", a: "Free accounts get 5 searches per month. The Match Pack ($9.99 One-Time) includes 5 searches and unlocks all AI tools. Pro Matcher ($24.99/mo) gives you unlimited searches every month." },
                { q: "How does the job matching work?", a: "We use your resume to generate a search query and run it through real-time job listings. Results are filtered by relevance and quality so you see matches that actually make sense for your background." },
                { q: "Can I save jobs I like?", a: "Yes — free users can save up to 5 jobs. Pro users can save unlimited jobs and access them anytime from the saved jobs panel." },
              ]},
              { section: "AI Tools", items: [
                { q: "What does 'Tailor Resume' do?", a: "It rewrites your resume to better match a specific job listing — highlighting the most relevant experience and using language that aligns with the job description. You see a before/after comparison." },
                { q: "What is the ATS Score Checker?", a: "ATS stands for Applicant Tracking System — the software most companies use to filter resumes before a human sees them. Our checker scores your resume against a job listing and tells you how to improve it." },
                { q: "How does Interview Prep work?", a: "We generate 9 tailored interview questions across three categories — Role-Specific, Behavioral, and Culture & Company — with coaching tips pulled from your actual resume." },
                { q: "What does the LinkedIn Rewriter do?", a: "It rewrites your LinkedIn headline and About section to match the type of roles you're targeting, using keywords from the job listing to improve your visibility." },
              ]},
              { section: "Billing & Plans", items: [
                { q: "What's the difference between Match Pack and Pro?", a: "Match Pack is a One-Time $9.99 purchase that includes 5 searches and unlocks all AI tools. Pro Matcher is $24.99/month and gives you unlimited searches, all AI tools, and unlimited saved jobs — best if you're actively job hunting." },
                { q: "Can I cancel Pro anytime?", a: "Yes. You can cancel from Account Settings at any time. You'll keep access until the end of your billing period." },
                { q: "Do unused searches roll over?", a: "Match Pack searches don't expire. Pro monthly searches reset each billing cycle and don't roll over." },
                { q: "Is there a refund policy?", a: "If you have an issue with your purchase, reach out to us via the Contact page and we'll make it right." },
              ]},
              { section: "Privacy & Data", items: [
                { q: "Is my resume data secure?", a: "Yes. Your resume is stored securely in our database and is only used to power your job searches and AI tools. We never sell your data." },
                { q: "Can I delete my account?", a: "Yes — go to Account Settings and scroll to the Delete Account section. This permanently removes your account, resume, saved jobs, and all usage data." },
              ]},
            ].map((section, si) => (
              <div key={si} style={{ marginBottom:40 }}>
                <div style={{ fontFamily:"'Space Mono',monospace", fontSize:11, fontWeight:700, color:C.accent, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:16 }}>{section.section}</div>
                <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                  {section.items.map((item, ii) => (
                    <FaqItem key={ii} q={item.q} a={item.a} />
                  ))}
                </div>
              </div>
            ))}

            <div style={{ marginTop:48, padding:"24px 28px", background:C.accentSoft, border:`1px solid ${C.accent}20`, borderRadius:16, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:16 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:16, color:C.text, marginBottom:4 }}>Still have questions?</div>
                <div style={{ color:C.sub, fontSize:14 }}>We're happy to help — reach out anytime.</div>
              </div>
              <button onClick={() => { setScreen("contact"); window.scrollTo({ top:0, behavior:"smooth" }); }}
                style={{ background:`linear-gradient(135deg,${C.accent},#c0392b)`, color:"#fff", border:"none", borderRadius:10, padding:"11px 24px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap" }}>
                Contact Support
              </button>
            </div>
          </div>
        )}

        {screen === "contact" && (
          <div style={{ maxWidth:860, margin:"0 auto", padding: isMobile ? "24px 16px" : "40px 32px", overflowX:"hidden" }}>
            <a href="/" style={{ display:"inline-flex", alignItems:"center", gap:6, background:"transparent", border:"1px solid rgba(0,0,0,0.12)", color:C.muted, borderRadius:8, padding:"6px 14px", fontSize:12, fontFamily:"'Space Mono',monospace", textDecoration:"none", marginBottom:32 }}>← Back to Home</a>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:40, alignItems:"start" }}>
              {/* Left — info */}
              <div>
                <h1 style={{ fontFamily:"'Playfair Display',serif", fontWeight:800, fontSize:36, margin:"0 0 12px", color:C.text }}>Get in touch</h1>
                <p style={{ color:C.sub, fontSize:15, lineHeight:1.7, margin:"0 0 36px" }}>Have a question, a bug to report, or just want to say hi? We read every message and typically respond within one business day.</p>

                <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
                  {[
                    { label:"General Support", desc:"Questions about your account, searches, or how the app works.", subject:"general" },
                    { label:"Billing & Payments", desc:"Issues with charges, upgrades, or cancellations.", subject:"billing" },
                    { label:"Bug Report", desc:"Something broken or not working as expected.", subject:"bug" },
                    { label:"Feature Request", desc:"Something you'd love to see in Resumatch.", subject:"feature" },
                  ].map((item) => (
                    <div key={item.subject} onClick={() => setContactSubject(item.subject)}
                      style={{ background: contactSubject === item.subject ? C.accentSoft : "#f9fafb", border:`1px solid ${contactSubject === item.subject ? C.accent+"40" : C.border}`, borderRadius:12, padding:"14px 16px", cursor:"pointer", transition:"all .15s" }}>
                      <div style={{ fontWeight:700, fontSize:14, color: contactSubject === item.subject ? C.accent : C.text, marginBottom:3 }}>{item.label}</div>
                      <div style={{ fontSize:12, color:C.muted, fontFamily:"'Space Mono',monospace", lineHeight:1.5 }}>{item.desc}</div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop:32, padding:"16px 20px", background:"#f9fafb", border:`1px solid ${C.border}`, borderRadius:12 }}>
                  <div style={{ fontSize:12, color:C.muted, fontFamily:"'Space Mono',monospace", marginBottom:4 }}>Or email us directly</div>
                  <a href="mailto:support@getresumatch.com" style={{ fontSize:14, fontWeight:600, color:C.accent, textDecoration:"none" }}>support@getresumatch.com</a>
                </div>
              </div>

              {/* Right — form */}
              <div style={{ border:`1px solid ${C.border}`, borderRadius:20, overflow:"hidden" }}>
                <div style={{ background:"#f9fafb", borderBottom:`1px solid ${C.border}`, padding:"18px 24px" }}>
                  <div style={{ fontWeight:700, fontSize:16, color:C.text }}>Send us a message</div>
                  <div style={{ fontSize:12, color:C.sub, fontFamily:"'Space Mono',monospace", marginTop:2 }}>
                    {contactSubject === "general" ? "General Support" : contactSubject === "billing" ? "Billing & Payments" : contactSubject === "bug" ? "Bug Report" : "Feature Request"}
                  </div>
                </div>

                <div style={{ padding:"24px", display:"flex", flexDirection:"column", gap:14 }}>
                  {contactSent ? (
                    <div style={{ textAlign:"center", padding:"32px 16px" }}>
                      <div style={{ width:56, height:56, borderRadius:"50%", background:C.tealSoft, border:`1px solid ${C.teal}30`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", fontSize:22, color:C.teal, fontWeight:700 }}>&#10003;</div>
                      <div style={{ fontWeight:700, fontSize:17, color:C.text, marginBottom:8 }}>Message sent!</div>
                      <p style={{ color:C.sub, fontSize:13, lineHeight:1.6, margin:"0 0 20px" }}>We&apos;ll get back to you at {contactEmail || "your email"} within one business day.</p>
                      <button onClick={() => setContactSent(false)} style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.muted, borderRadius:9, padding:"9px 20px", fontSize:13, cursor:"pointer", fontFamily:"'Space Mono',monospace" }}>Send another</button>
                    </div>
                  ) : (
                    <>
                      {contactError && <div style={{ background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#dc2626" }}>{contactError}</div>}
                      <input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Your name"
                        style={{ border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", color:C.text }}
                        onFocus={e => e.target.style.borderColor = C.accent} onBlur={e => e.target.style.borderColor = "rgba(0,0,0,0.08)"}/>
                      <input value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="Email address" type="email"
                        style={{ border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", color:C.text }}
                        onFocus={e => e.target.style.borderColor = C.accent} onBlur={e => e.target.style.borderColor = "rgba(0,0,0,0.08)"}/>
                      <textarea value={contactMessage} onChange={e => setContactMessage(e.target.value)} placeholder="Describe your issue or question in as much detail as possible…" rows={6}
                        style={{ border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", fontSize:14, outline:"none", fontFamily:"'DM Sans',sans-serif", color:C.text, resize:"vertical", lineHeight:1.6 }}
                        onFocus={e => e.target.style.borderColor = C.accent} onBlur={e => e.target.style.borderColor = "rgba(0,0,0,0.08)"}/>
                      <button onClick={handleContactSubmit} disabled={contactLoading}
                        style={{ background:`linear-gradient(135deg,${C.accent},#c0392b)`, color:"#fff", border:"none", borderRadius:10, padding:"13px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity: contactLoading ? 0.7 : 1 }}>
                        {contactLoading ? "Sending…" : "Send Message"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </main>

      {/* ── Footer ── */}
      <footer style={{ borderTop:`1px solid ${C.border}`, padding:"20px 28px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <Logo size={20}/>
          <span style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:13, color:C.muted }}>
            get<span style={{ color:"#b02d45" }}>resu</span>match.com
          </span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          <p style={{ color:C.muted, fontSize:11, fontFamily:"'Space Mono',monospace", margin:0 }}>
            © 2026 getresumatch.com · Your career deserves a perfect match.
          </p>
          <a href="https://www.anthropic.com" target="_blank" rel="noopener noreferrer"
            style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#1a1a2e", border:"1px solid rgba(26,26,46,0.3)", borderRadius:20, padding:"3px 10px", textDecoration:"none", transition:"all .15s" }}
            onMouseEnter={e => { e.currentTarget.style.background="#1a1a2e"; e.currentTarget.style.borderColor="rgba(26,26,46,0.6)"; }}
            onMouseLeave={e => { e.currentTarget.style.background="#1a1a2e"; e.currentTarget.style.borderColor="rgba(26,26,46,0.3)"; }}>
            <span style={{ fontSize:10, fontFamily:"'Space Mono',monospace", color:"#ffffff", fontWeight:700, whiteSpace:"nowrap" }}>Powered by Claude AI</span>
          </a>
        </div>
        <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
          <a href="/blog"
            style={{ color:C.muted, fontSize:11, fontFamily:"'Space Mono',monospace", cursor:"pointer", transition:"color .15s", textDecoration:"none", padding:"8px 10px", minHeight:44, display:"inline-flex", alignItems:"center" }}
            onMouseEnter={e => e.target.style.color = C.accent}
            onMouseLeave={e => e.target.style.color = C.muted}>
            Blog
          </a>
          <span
            onClick={() => { setScreen("faq"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            style={{ color:C.muted, fontSize:11, fontFamily:"'Space Mono',monospace", cursor:"pointer", transition:"color .15s", padding:"8px 10px", minHeight:44, display:"inline-flex", alignItems:"center" }}
            onMouseEnter={e => e.target.style.color = C.accent}
            onMouseLeave={e => e.target.style.color = C.muted}>
            FAQ
          </span>
          <span
            onClick={() => { setScreen("contact"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            style={{ color:C.muted, fontSize:11, fontFamily:"'Space Mono',monospace", cursor:"pointer", transition:"color .15s", padding:"8px 10px", minHeight:44, display:"inline-flex", alignItems:"center" }}
            onMouseEnter={e => e.target.style.color = C.accent}
            onMouseLeave={e => e.target.style.color = C.muted}>
            Contact
          </span>
          <span
            onClick={() => { setScreen("privacy"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            style={{ color:C.muted, fontSize:11, fontFamily:"'Space Mono',monospace", transition:"color .15s", padding:"8px 10px", minHeight:44, display:"inline-flex", alignItems:"center" }}
            onMouseEnter={e => e.target.style.color = C.accent}
            onMouseLeave={e => e.target.style.color = C.muted}>
            <a href="/privacy" style={{ color:"inherit", textDecoration:"none" }}>Privacy</a>
          </span>
          <span
            style={{ color:C.muted, fontSize:11, fontFamily:"'Space Mono',monospace", transition:"color .15s", padding:"8px 10px", minHeight:44, display:"inline-flex", alignItems:"center" }}
            onMouseEnter={e => e.target.style.color = C.accent}
            onMouseLeave={e => e.target.style.color = C.muted}>
            <a href="/terms" style={{ color:"inherit", textDecoration:"none" }}>Terms</a>
          </span>
        </div>
        {/* SEO internal links */}
        <div style={{ width:"100%", borderTop:`1px solid ${C.border}`, paddingTop:12, display:"flex", alignItems:"center", justifyContent:"center", gap:8, flexWrap:"wrap" }}>
          {[
            { href:"/best-job-search-sites-for-personalized-matching", label:"Personalized Job Matching" },
            { href:"/resume-builder", label:"AI Resume Builder" },
            { href:"/job-tracker", label:"Free Job Tracker" },
            { href:"/ats-resume-checker", label:"ATS Resume Checker" },
            { href:"/ai-job-matching", label:"AI Job Matching" },
            { href:"/free-ats-checker", label:"Free ATS Score" },
          ].map(({ href, label }) => (
            <a key={href} href={href} style={{ fontSize:11, color:C.muted, textDecoration:"none", fontFamily:"'Space Mono',monospace", padding:"2px 0", transition:"color .15s" }}
              onMouseEnter={e => e.currentTarget.style.color = C.accent}
              onMouseLeave={e => e.currentTarget.style.color = C.muted}>
              {label}
            </a>
          ))}
        </div>
      </footer>

      {/* ── Onboarding Modal ── */}
      {showOnboarding && (() => {
        const steps = [
          {
            emoji: "👋",
            title: `Welcome to Resumatch${user?.user_metadata?.full_name ? `, ${user.user_metadata.full_name.split(" ")[0]}` : ""}!`,
            body: "You're about to stop applying blindly and start matching smarter. Here's how to get the most out of Resumatch in the next 2 minutes.",
            cta: "Let's go →",
          },
          {
            emoji: "📄",
            title: "Step 1 — Upload your resume",
            body: "Drop your resume on the home screen. We'll read it and use it to find jobs that actually match your background — no manual profile setup needed.",
            cta: "Got it →",
          },
          {
            emoji: "🔍",
            title: "Step 2 — Run your first search",
            body: "Hit Search and we'll scan real job listings for roles that fit you. Each result shows match quality, salary range, and company info.",
            cta: "Next →",
          },
          {
            emoji: "🤖",
            title: "Step 3 — Use the AI tools",
            body: "Click any job to open the tools panel. Tailor your resume, check your ATS score, generate a cover letter, prep for interviews, and rewrite your LinkedIn — all in one click.",
            cta: "Let's find my matches →",
          },
        ];
        const step = steps[onboardingStep];
        const isLast = onboardingStep === steps.length - 1;
        return (
          <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.55)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
            <div style={{ background:"#fff", borderRadius:24, maxWidth:480, width:"100%", padding:"40px 36px", boxShadow:"0 24px 80px rgba(0,0,0,0.2)", position:"relative" }}>
              {/* Skip */}
              <button onClick={handleOnboardingDone}
                style={{ position:"absolute", top:16, right:16, background:"none", border:"none", color:C.muted, fontSize:12, cursor:"pointer", fontFamily:"'Space Mono',monospace" }}>
                Skip
              </button>

              {/* Step dots */}
              <div style={{ display:"flex", gap:6, marginBottom:28 }}>
                {steps.map((_, i) => (
                  <div key={i} style={{ height:4, borderRadius:2, flex:1, background: i <= onboardingStep ? C.accent : C.border, transition:"background .3s" }}/>
                ))}
              </div>

              {/* Content */}
              <div style={{ fontSize:44, marginBottom:16 }}>{step.emoji}</div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontWeight:800, fontSize:24, color:C.text, margin:"0 0 12px", lineHeight:1.3 }}>{step.title}</h2>
              <p style={{ color:C.sub, fontSize:15, lineHeight:1.7, margin:"0 0 32px" }}>{step.body}</p>

              <button onClick={() => isLast ? handleOnboardingDone() : setOnboardingStep(s => s + 1)}
                style={{ width:"100%", background:`linear-gradient(135deg,${C.accent},#c0392b)`, color:"#fff", border:"none", borderRadius:12, padding:"14px", fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                {step.cta}
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── Cookie Consent Banner ── */}
      {showCookieBanner && (
        <div style={{ position:"fixed", bottom:0, left:0, right:0, zIndex:1000, background:"#1a1a2e", borderTop:"1px solid rgba(255,255,255,0.08)", padding:"16px 28px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:16, boxShadow:"0 -4px 24px rgba(0,0,0,0.2)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:14, flex:1, minWidth:260 }}>
            <span style={{ fontSize:22 }}>🍪</span>
            <div>
              <div style={{ color:"#fff", fontSize:14, fontWeight:600, marginBottom:3 }}>We use essential cookies only</div>
              <div style={{ color:"rgba(255,255,255,0.55)", fontSize:12, lineHeight:1.5 }}>
                No tracking, no ads. Just the cookies needed to keep you logged in and the app running.{" "}
                <span onClick={() => { setScreen("privacy"); setShowCookieBanner(false); window.scrollTo({ top:0, behavior:"smooth" }); }}
                  style={{ color:C.teal, cursor:"pointer", textDecoration:"underline" }}>Learn more</span>
              </div>
            </div>
          </div>
          <div style={{ display:"flex", gap:10, flexShrink:0 }}>
            <button onClick={handleCookieAccept}
              style={{ background:`linear-gradient(135deg,${C.teal},#0f766e)`, color:"#fff", border:"none", borderRadius:9, padding:"10px 22px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap" }}>
              Got it
            </button>
            <button onClick={() => setShowCookieBanner(false)}
              style={{ background:"rgba(255,255,255,0.08)", color:"rgba(255,255,255,0.6)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:9, padding:"10px 16px", fontSize:13, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
              ✕
            </button>
          </div>
        </div>
      )}

      <SupportChat />
      <ResumeToast toast={resumeToast} onDismiss={dismissResumeToast} />
      {showBannerToast && bannerJob && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999, pointerEvents:"none" }}>
          <div style={{ background:"#fff8f0", border:"2px solid #d63651", borderRadius:18, padding:"22px 32px", minWidth:320, maxWidth:460, display:"flex", alignItems:"center", gap:16, boxShadow:"0 20px 60px rgba(0,0,0,0.18)", pointerEvents:"all" }}>
            <div style={{ width:12, height:12, borderRadius:"50%", background:"#d63651", flexShrink:0 }} />
            <div style={{ flex:1 }}>
              <div style={{ fontSize:17, fontWeight:800, color:"#d63651", marginBottom:8 }}>{"You’re putting in the work applying — make sure your resume is working just as hard."}</div>
              <div style={{ fontSize:14, color:"#1a1a2e", marginBottom:12, lineHeight:1.6, fontWeight:600 }}>{"Tailored resumes are significantly more likely to get you past ATS screening and into the interview you deserve."}</div>
              <button onClick={() => { setSelectedJob(bannerJob); setScreen("rewrite"); setShowBannerToast(false); }} style={{ background:"#d63651", color:"#fff", border:"none", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:700, cursor:"pointer" }}>{"Tailor My Resume →"}</button>
            </div>
            <button onClick={() => setShowBannerToast(false)} style={{ background:"none", border:"none", cursor:"pointer", color:"#6b7280", fontSize:20, padding:"0 2px", flexShrink:0, lineHeight:1, pointerEvents:"all" }}>{"×"}</button>
          </div>
        </div>
      )}
      <ResumeToast toast={trackerToast} onDismiss={dismissTrackerToast} />
      {showNudgeToast && nudgeJob && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999, pointerEvents:"none" }}>
          <div style={{ background:"#fff8f0", border:"2px solid #d63651", borderRadius:18, padding:"22px 32px", minWidth:320, maxWidth:460, display:"flex", alignItems:"center", gap:16, boxShadow:"0 20px 60px rgba(0,0,0,0.18)", pointerEvents:"all" }}>
            <div style={{ width:12, height:12, borderRadius:"50%", background:"#d63651", flexShrink:0 }} />
            <div style={{ flex:1 }}>
              <div style={{ fontSize:17, fontWeight:800, color:"#d63651", marginBottom:8 }}>{"Added to your Job Tracker ✓"}</div>
              <div style={{ fontSize:14, color:"#1a1a2e", marginBottom:12, lineHeight:1.6, fontWeight:600 }}>{"Most resumes never reach a human. Tailoring yours to this job is the single biggest thing you can do to get an interview. Don’t skip it."}</div>
              <button onClick={() => { setSelectedJob(nudgeJob); setScreen("rewrite"); setShowNudgeToast(false); }} style={{ background:"#d63651", color:"#fff", border:"none", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:700, cursor:"pointer" }}>{"Tailor My Resume →"}</button>
            </div>
            <button onClick={() => setShowNudgeToast(false)} style={{ background:"none", border:"none", cursor:"pointer", color:"#6b7280", fontSize:20, padding:"0 2px", flexShrink:0, lineHeight:1, pointerEvents:"all" }}>{"×"}</button>
          </div>
        </div>
      )}
      <LeadCaptureBar
        show={showLeadBar && !user}
        submitted={leadSubmitted}
        email={leadEmail}
        onEmailChange={setLeadEmail}
        onSubmit={handleLeadCapture}
        loading={leadLoading}
        onDismiss={() => setShowLeadBar(false)}
      />
      {showCancelModal && (
        <CancelModal
          profile={profile}
          onClose={() => { setShowCancelModal(false); setCancelDone(false); setCancelError(""); }}
          onCancel={handleCancelSubscription}
          onUpgradeAnnual={() => { setShowCancelModal(false); handleUpgrade("pro_annual"); }}
          cancelLoading={cancelLoading}
          cancelDone={cancelDone}
          cancelError={cancelError}
        />
      )}
    </div>
  );
}
